// Import necessary React hooks and icons
import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  CreditCard,
  Download,
  FileText,
  Gift,
  DollarSign,
  UserCheck,
  Activity,
  TrendingUp,
  Eye,
  BarChart3,
  Search,
  Filter,
  Calendar,
  Clock,
  Users,
  AlertCircle,
  CheckCircle,
  MoreVertical,
  Edit,
  Trash2,
  Send,
  Plus,
  Calculator,
  TrendingDown,
  Award,
  Target,
  Building2,
  Car,
  Coffee,
  Smartphone,
  Settings,
  PieChart,
  Receipt,
  Banknote,
  Percent,
  Minus,
  Heart,
  Shield,
  GraduationCap,
  Info,
  Building,
  X,
  Save,
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  Flag,
  ChevronDown,
  Upload,
} from "lucide-react";
import * as XLSX from "xlsx";

// Import theme context
import { ThemeContext } from "./theme";
import ReusableDropdown, {
  unifiedControlStyle,
} from "../components/ReusableDropdown";
import AlertModal from "../components/AlertModal";

//import ShareButton from "./sharebtn.js";
import { LanguageContext } from "./LanguageContext"; // أو "../LanguageContext" حسب مكان الملف
import templateFile from "../Documents/payroll_template.xlsx";
// ✅ Use centralized HTTP helper
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import WelcomeCard from "../components/WelcomeCard";

// Translations object
const translations = {
  en: {
    success: "Success",
    error: "Error",
    important: "Important",
    fillTypeAmount: "Please fill in the type and amount",
    deductionAddedSuccess: "Deduction added successfully!",
    errorAddingDeduction: "An error occurred while adding the deduction",
    noDescription: "No description",
    employeeDataUpdatedSuccess: "Employee data updated successfully",
    errorSavingEmployeeData: "Error saving employee data",
    payrollProcessedSuccess:
      "Payroll processed successfully for {count} employees!",
    payrollProcessingError: "Error processing payroll: {error}",

    // Employee Details Modal    // Employee Details Modal
    employeeDetailsTitle: "Employee Details & Payroll Information",
    edit: "Edit",
    cancel: "Cancel",
    saveChanges: "Save Changes",
    saving: "Saving...",

    // Personal Information
    personalInformation: "Personal Information",
    fullName: "Full Name",
    employeeId: "Employee ID",
    emailAddress: "Email Address",
    phoneNumber: "Phone Number",
    emergencyPhone: "Emergency Phone",
    nationalId: "National ID",
    dateOfBirth: "Date of Birth",
    gender: "Gender",
    selectGender: "Select Gender",
    male: "Male",
    female: "Female",
    maritalStatus: "Marital Status",
    selectStatus: "Select Status",
    single: "Single",
    married: "Married",
    divorced: "Divorced",
    widowed: "Widowed",
    nationality: "Nationality",
    city: "City",
    country: "Country",
    address: "Address",
    showing: "Showing",
    of: "of",
    previous: "Previous",
    next: "Next",

    // Work Information
    workInformation: "Work Information",
    department: "Department",
    position: "Position",
    hiringDate: "Hiring Date",
    contractType: "Contract Type",
    selectType: "Select Type",
    fullTime: "Full-time",
    partTime: "Part-time",
    contract: "Contract",
    temporary: "Temporary",
    workingHours: "Working Hours",
    contractExpiration: "Contract Expiration",
    directManager: "Direct Manager",
    workLocation: "Work Location",
    employmentStatus: "Employment Status",

    // Salary & Earnings
    salaryAndEarnings: "Salary & Earnings",
    basicSalary: "Basic Salary",
    bonus: "Bonus",
    overtimeHours: "Overtime Hours",
    overtimeTotal: "Overtime Total",
    transportationAllowance: "Transportation Allowance",
    mealAllowance: "Meal Allowance",
    mobileAllowance: "Mobile Allowance",
    bonusAdjustments: "Bonus Adjustments",
    grossSalary: "Gross Salary",
    payrollSettings: "Payroll Settings",
    paymentMethod: "Payment Method",
    paymentDate: "Payment Date",
    processingOptions: "Processing Options",
    includeOvertime: "Include Overtime",
    includeBonuses: "Include Bonuses",
    processTaxes: "Process Income Tax",
    processInsurance: "Process Social Insurance",
    notes: "Notes (Optional)",
    addNotesPayroll: "Add any notes about this payroll run...",
    reviewProcess: "Review & Process",
    payrollSummary: "Payroll Summary",
    employees: "Employees",
    grossTotal: "Gross Total",
    deductions: "Deductions",
    taxInsurance: "Tax & Insurance",
    netTotal: "NET TOTAL",
    employeeBreakdown: "Employee Breakdown",
    important: "Important",
    processPayrollWarning:
      "This action will process payroll for {count} employees and cannot be undone.",

    // Performance & KPI
    performanceKPI: "Performance & KPI",
    kpiScore: "KPI Score",
    performanceRating: "Performance Rating",
    excellent: "Excellent",
    good: "Good",
    satisfactory: "Satisfactory",
    needsImprovement: "Needs Improvement",

    // Banking Information
    bankingInformation: "Banking Information",
    bankName: "Bank Name",
    accountNumber: "Account Number",
    iban: "IBAN",
    branchCode: "Branch Code",

    // Action Buttons
    downloadPayslip: "Download Payslip",
    sendViaEmail: "Send via Email",

    // Status Options
    active: "Active",
    paid: "Paid",
    pending: "Pending",
    processing: "Processing",
    onHold: "On Hold",
    // Header
    pageTitle: "Payroll Management System",
    currency: "EGP",
    nextStep: "Next Step",
    // Action Buttons
    processPayroll: "Process New Payroll",
    importExcel: "Import from Excel",
    importAllowancesDeductions: "Import Allowances & Deductions",
    exportTemplate: "Export Template",
    exportAllowanceDeductionTemplate: "Export Allowances/Deductions Template",
    exportPayrollExcel: "Export to Excel",
    exportPayrollPDF: "Download as PDF",
    exportDate: "Export Date",
    payrollExportSuccess: "Payroll exported to Excel successfully!",
    payrollExportSuccessPDF: "Payroll exported to PDF successfully!",
    payrollExportError: "Error exporting payroll to Excel",
    payrollExportErrorPDF: "Error exporting payroll to PDF",
    allowanceDeductionImportSuccess:
      "Allowances and deductions imported successfully",
    allowanceDeductionImportError: "Error importing allowances and deductions",
    // manageVisibility: "Manage Visibility",

    // Visibility Rules (UI removed translations retained for messages)
    visibilityAdded: "Visibility rule added successfully",
    visibilityRemoved: "Visibility rule removed successfully",
    loadingVisibilityRules: "Loading visibility rules...",
    noVisibilityRules: "No visibility rules configured",

    // Statistics
    totalGross: "Total Gross Salary",
    employeesPaid: "Employees Paid",
    pendingPayments: "Pending Payments",
    averageKPI: "Average KPI Score",
    monthlyGross: "Monthly gross payroll",
    currentMonth: "Current month",
    awaitingApproval: "Awaiting approval",
    performanceIndicator: "Performance indicator",

    // Filters
    filterSearch: "Filter & Search",
    searchEmployees: "Search Employees",
    searchPlaceholder: "Search by name or employee ID...",
    paymentStatus: "Payment Status",
    allStatuses: "All Statuses",
    paid: "Paid",
    pending: "Pending",
    approved: "Approved/Processing",
    rejected: "Rejected/On Hold",
    department: "Department/Location",
    allDepartments: "All Departments",
    payPeriod: "Pay Period",
    payPeriodMonth: "Pay Period (Month)",
    payPeriodYear: "Pay Period (Year)",
    refreshData: "Refresh Data",
    activeFilters: "Active Filters",
    clearAll: "Clear All",
    workingCompany: "Working Company",
    allCompanies: "All Companies",

    // Benefits
    benefitsPackage: "Employee Benefits Package",
    medicalInsurance: "Medical Insurance",
    socialInsurance: "Social Insurance",

    // Medical Insurance Modal
    miManagement: "Medical Insurance Management",
    miFamilyMembers: "Family Members",
    miBack: "← Back",
    miManageTiers: "Manage Tiers",
    miInsuranceTiers: "Insurance Tiers",
    miTierName: "Tier Name",
    miMonthlyPriceEgp: "Monthly Price (EGP)",
    miDescriptionOptional: "Description (optional)",
    miRemove: "Remove",
    miNoTiers: "No tiers configured",
    miAddTier: "Add Tier",
    miTierNamePlaceholder: "e.g. Gold",
    miDescriptionPlaceholder: "Coverage details...",
    miTierNamePriceRequired: "Tier name and price are required",
    miNetworkError: "Network error",
    miSearchPlaceholder: "Search by name or ID...",
    miLoadingEmployees: "Loading employees...",
    miMonthlyCost: "Monthly Cost",
    miManage: "Manage",
    miNoEmployees: "No employees found",
    miMembers: "Members",
    miTotalMonthly: "Total Monthly",
    miRelationship: "Relationship",
    miInsuranceTier: "Insurance Tier",
    miNoFamilyMembers: "No family members added yet",
    miAddFamilyMember: "Add Family Member",
    miNewFamilyMember: "New Family Member",
    miFullNamePlaceholder: "Full name",
    miRelationshipPlaceholder: "e.g. Spouse, Son, Daughter",
    miSelectTier: "— Select tier —",
    miFillAllFields: "Please fill all fields",
    miFailedToAdd: "Failed to add member",
    miSaving: "Saving...",
    miActivate: "Activate",
    miDeactivate: "Deactivate",
    miDelete: "Delete",
    miInactive: "Inactive",
    annualLeave: "Annual Leave",
    training: "Training & Development",
    fullFamily: "Full Family",
    govMandated: "Government Mandated",
    paidTimeOff: "Paid Time Off",
    professionalGrowth: "Professional Growth",
    comprehensiveCoverage:
      "Full medical coverage for employee and family members",
    pensionCoverage: "Pension, disability, and unemployment insurance",
    annualVacation: "Annual paid vacation as per Egyptian labor law",
    profDevelopment: "Professional development and skill enhancement programs",
    active: "Active",
    available: "Available",
    daysRemaining: "days remaining",
    companySponsored: "Company sponsored",

    // Payroll Table
    payrollDetails: "Comprehensive Payroll Details",
    employee: "Employee",
    basicSalary: "Basic Salary",
    allowances: "Allowances",
    grossSalary: "Gross Salary",
    deductions: "Deductions",
    netSalary: "Net Salary",
    kpi: "KPI",
    status: "Status",
    actions: "Actions",
    includingOvertime: "Including overtime",
    taxInsuranceOther: "Tax + Insurance + Other",

    // Employee Details Modal
    employeeDetails: "Employee Details & Payroll Information",
    edit: "Edit",
    cancel: "Cancel",
    save: "Save Changes",
    saving: "Saving...",
    personalInfo: "Personal Information",
    fullName: "Full Name",
    employeeId: "Employee ID",
    email: "Email Address",
    phone: "Phone Number",
    emergencyPhone: "Emergency Phone",
    nationalId: "National ID",
    dateOfBirth: "Date of Birth",
    gender: "Gender",
    male: "Male",
    female: "Female",
    maritalStatus: "Marital Status",
    single: "Single",
    married: "Married",
    divorced: "Divorced",
    widowed: "Widowed",
    nationality: "Nationality",
    city: "City",
    country: "Country",
    address: "Address",

    // Work Info
    workInfo: "Work Information",
    position: "Position",
    hiringDate: "Hiring Date",
    contractType: "Contract Type",
    fullTime: "Full-time",
    partTime: "Part-time",
    contract: "Contract",
    temporary: "Temporary",
    workingHours: "Working Hours",
    contractExpiration: "Contract Expiration",
    directManager: "Direct Manager",
    workLocation: "Work Location",
    employmentStatus: "Employment Status",

    // Salary & Earnings
    salaryEarnings: "Salary & Earnings",
    bonus: "Bonus",
    overtimeHours: "Overtime Hours",
    overtimeTotal: "Overtime Total",
    transportation: "Transportation Allowance",
    meal: "Meal Allowance",
    mobile: "Mobile Allowance",
    bonusAdjustments: "Bonus Adjustments",
    taxMonth: "Monthly Tax",
    socialInsurance: "Social Insurance",
    penalty: "Penalty",
    advancePayment: "Advance Payment",

    // Performance
    performance: "Performance & KPI",
    kpiScore: "KPI Score",
    performanceRating: "Performance Rating",
    excellent: "Excellent",
    good: "Good",
    satisfactory: "Satisfactory",
    needsImprovement: "Needs Improvement",

    // Banking
    bankingInfo: "Banking Information",
    bankName: "Bank Name",
    accountNumber: "Account Number",
    iban: "IBAN",
    branchCode: "Branch Code",

    // Actions
    downloadPayslip: "Download Payslip",
    sendEmail: "Send via Email",

    // Process Payroll Modal
    processNewPayroll: "Process New Payroll",
    selectPeriod: "Select Period",
    chooseEmployees: "Choose Employees",
    configureSettings: "Configure Settings",
    reviewProcess: "Review & Process",
    month: "Month",
    year: "Year",
    startDate: "Start Date",
    endDate: "End Date",
    selectByDepartment: "Select Employees by Department",
    selectByCompany: "Select Employees by Company",
    groupByDepartment: "By Department",
    groupByCompany: "By Company",
    selectAll: "Select All",
    valid: "Valid",
    payrollSettings: "Payroll Settings",
    paymentMethod: "Payment Method",
    bankTransfer: "Bank Transfer",
    cash: "Cash",
    check: "Check",
    digitalWallet: "Digital Wallet",
    paymentDate: "Payment Date",
    processingOptions: "Processing Options",
    includeOvertime: "Include Overtime",
    includeBonuses: "Include Bonuses",
    processTaxes: "Process Income Tax",
    processInsurance: "Process Social Insurance",
    notes: "Notes (Optional)",
    notesPlaceholder: "Add any notes about this payroll run...",
    payrollSummary: "Payroll Summary",
    employees: "Employees",
    grossTotal: "Gross Total",
    taxInsurance: "Tax & Insurance",
    netTotal: "NET TOTAL",
    employeeBreakdown: "Employee Breakdown",
    important: "Important",
    cannotUndo:
      "This action will process payroll for {count} employees and cannot be undone.",
    back: "Back",
    nextStep: "Next Step",
    processing: "Processing...",
    processPayrollBtn: "Process Payroll",

    // Import Modal
    importPayroll: "Import Payroll from Excel",
    upload: "Upload",
    preview: "Preview",
    results: "Results",
    uploadFile: "Click to upload Excel file",
    supportedFormats: "Supports .xlsx, .xls, and .csv files",
    processingFile: "Processing file...",
    expectedFormat: "Expected Excel Format",
    formatDescription:
      "Your Excel file should contain columns: Infinity Code (Required), Employee Name, Payroll Start/End dates, Working days, Absence Days, OverTime Days, Bonus Value, expenses, Achievement, and more.",
    importSettings: "Import Settings",
    skipErrors: "Skip rows with errors",
    autoCalculate: "Auto-calculate missing values from employee data",
    totalRows: "Total Rows",
    validRows: "Valid",
    errorRows: "Errors",
    row: "Row",
    code: "Code",
    name: "Name",
    period: "Period",
    errorsWillBeSkipped: "{count} row(s) have errors and will be skipped.",
    total: "Total",
    successful: "Successful",
    failed: "Failed",
    skipped: "Skipped",
    successMessage: "Successfully imported {count} payroll record(s)!",
    errorsFound: "Errors",
    recordsCreated:
      "The payroll records have been created. You can now review them in the payroll table.",
    done: "Done",
    importing: "Importing...",
    importRecords: "Import {count} Records",
    importingRecords: "Importing payroll records...",
    showMore: "Show More",
    remaining: "remaining",
    // Deductions Modal
    employeeDeductions: "Employee Deductions",
    addDeduction: "Add Deduction",
    deductionType: "Deduction Type",
    selectType: "Select Type",
    absence: "Absence",
    penalty: "Penalty",
    advance: "Advance",
    insurance: "Insurance",
    sickLeave: "Sick Leave 25%",
    lateArrival: "Late Arrival",
    lateArrivalCalcDesc: "Late deduction based on configuration rates",
    calculatingLate: "Loading rates...",
    lateHours: "Late Hours",
    other: "Other",
    amount: "Amount (EGP)",
    date: "Date",
    days: "Days (Optional)",
    description: "Description",
    descriptionPlaceholder: "Deduction description...",
    saveDeduction: "Save Deduction",
    currentDeductions: "Current Deductions",
    loading: "Loading...",
    noDeductions: "No deductions for this employee",
    totalDeductions: "Total Deductions",
    unknown: "Unknown", // Modal titles & actions
    close: "Close",
    view: "View",
    viewDetails: "View Details",

    // Deductions Modal - اللي ناقصة
    employeeDeductions: "Employee Deductions",
    addDeduction: "Add Deduction",
    deductionType: "Deduction Type",
    selectType: "Select Type",
    absence: "Absence",
    penalty: "Penalty",
    advance: "Advance",
    insurance: "Insurance",
    sickLeave: "Sick Leave 25%",
    lateArrival: "Late Arrival",
    lateArrivalCalcDesc: "Late deduction based on configuration rates",
    calculatingLate: "Loading rates...",
    lateHours: "Late Hours",
    other: "Other",
    amount: "Amount (EGP)",
    date: "Date",
    days: "Days (Optional)",
    description: "Description",
    descriptionPlaceholder: "Deduction description...",
    saveDeduction: "Save Deduction",
    currentDeductions: "Current Deductions",
    loading: "Loading...",
    noDeductions: "No deductions for this employee",
    totalDeductions: "Total Deductions",

    // Months
    january: "January",
    february: "February",
    march: "March",
    april: "April",
    may: "May",
    june: "June",
    july: "July",
    august: "August",
    september: "September",
    october: "October",
    november: "November",
    december: "December",
    2025: "2025",
    2024: "2024",
    2023: "2023",
    2022: "2022",
    2026: "2026",
  },
  ar: {
    success: "نجاح",
    error: "خطأ",
    important: "مهم",
    fillTypeAmount: "يرجى ملء النوع والمبلغ",
    deductionAddedSuccess: "تم إضافة الخصم بنجاح!",
    errorAddingDeduction: "حدث خطأ أثناء إضافة الخصم",
    noDescription: "لا يوجد وصف",
    employeeDataUpdatedSuccess: "تم تحديث بيانات الموظف بنجاح",
    errorSavingEmployeeData: "حدث خطأ أثناء حفظ البيانات",
    payrollProcessedSuccess: "تم معالجة الرواتب بنجاح لـ {count} موظف!",
    payrollProcessingError: "خطأ في معالجة الرواتب: {error}",

    // Employee Details Modal

    // Employee Details Modal
    employeeDetailsTitle: "تفاصيل الموظف ومعلومات الراتب",
    edit: "تعديل",
    cancel: "إلغاء",
    saveChanges: "حفظ التغييرات",
    saving: "جاري الحفظ...",

    // Personal Information
    personalInformation: "المعلومات الشخصية",
    fullName: "الاسم الكامل",
    employeeId: "رقم الموظف",
    emailAddress: "البريد الإلكتروني",
    phoneNumber: "رقم الهاتف",
    emergencyPhone: "هاتف الطوارئ",
    nationalId: "الرقم القومي",
    dateOfBirth: "تاريخ الميلاد",
    gender: "النوع",
    selectGender: "اختر النوع",
    male: "ذكر",
    female: "أنثى",
    maritalStatus: "الحالة الاجتماعية",
    selectStatus: "اختر الحالة",
    single: "أعزب",
    married: "متزوج",
    divorced: "مطلق",
    widowed: "أرمل",
    nationality: "الجنسية",
    city: "المدينة",
    country: "الدولة",
    address: "العنوان",

    // Work Information
    workInformation: "معلومات العمل",
    department: "القسم",
    position: "المنصب",
    hiringDate: "تاريخ التعيين",
    contractType: "نوع العقد",
    selectType: "اختر النوع",
    fullTime: "دوام كامل",
    partTime: "دوام جزئي",
    contract: "عقد",
    temporary: "مؤقت",
    workingHours: "ساعات العمل",
    contractExpiration: "انتهاء العقد",
    directManager: "المدير المباشر",
    workLocation: "موقع العمل",
    employmentStatus: "حالة التوظيف",

    // Salary & Earnings
    salaryAndEarnings: "الراتب والمكاسب",
    basicSalary: "الراتب الأساسي",
    bonus: "المكافأة",
    overtimeHours: "ساعات العمل الإضافي",
    overtimeTotal: "إجمالي الوقت الإضافي",
    transportationAllowance: "بدل المواصلات",
    mealAllowance: "بدل الوجبات",
    mobileAllowance: "بدل الهاتف",
    bonusAdjustments: "تعديلات المكافأة",
    grossSalary: "الراتب الإجمالي",
    payrollSettings: "إعدادات الراتب",
    paymentMethod: "طريقة الدفع",
    paymentDate: "تاريخ الدفع",
    processingOptions: "خيارات المعالجة",
    includeOvertime: "تضمين الوقت الإضافي",
    includeBonuses: "تضمين المكافآت",
    processTaxes: "معالجة ضريبة الدخل",
    processInsurance: "معالجة التأمين الاجتماعي",
    notes: "ملاحظات (اختياري)",
    addNotesPayroll: "أضف أي ملاحظات حول معالجة الراتب هذه...",
    reviewProcess: "المراجعة والمعالجة",
    payrollSummary: "ملخص الرواتب",
    employees: "الموظفون",
    grossTotal: "الإجمالي الإجمالي",
    deductions: "الخصومات",
    taxInsurance: "الضرائب والتأمين",
    netTotal: "الإجمالي الصافي",
    employeeBreakdown: "تفصيل الموظفين",
    important: "مهم",
    processPayrollWarning:
      "سيؤدي هذا الإجراء إلى معالجة الرواتب لـ {count} موظفين ولا يمكن التراجع عنه.",

    // Performance & KPI
    performanceKPI: "الأداء ومؤشرات الأداء",
    kpiScore: "درجة الأداء",
    performanceRating: "تقييم الأداء",
    excellent: "ممتاز",
    good: "جيد",
    satisfactory: "مرضي",
    needsImprovement: "يحتاج تحسين",

    // Banking Information
    bankingInformation: "المعلومات البنكية",
    bankName: "اسم البنك",
    accountNumber: "رقم الحساب",
    iban: "الآيبان",
    branchCode: "كود الفرع",

    // Action Buttons
    downloadPayslip: "تحميل كشف الراتب",
    sendViaEmail: "إرسال عبر البريد",

    // Status Options
    active: "نشط",
    paid: "مدفوع",
    pending: "معلق",
    processing: "قيد المعالجة",
    onHold: "معلق",
    // Header
    pageTitle: "نظام إدارة الرواتب",
    currency: "جنيه",
    nextStep: "الخطوة التالية",
    // Action Buttons
    processPayroll: "معالجة رواتب جديدة",
    importExcel: "استيراد من Excel",
    importAllowancesDeductions: "استيراد البدلات والخصومات",
    exportTemplate: "تصدير النموذج",
    exportAllowanceDeductionTemplate: "تصدير نموذج البدلات/الخصومات",
    exportPayrollExcel: "تصدير إلى Excel",
    exportPayrollPDF: "تحميل كـ PDF",
    exportDate: "تاريخ التصدير",
    payrollExportSuccess: "تم تصدير الرواتب إلى Excel بنجاح!",
    payrollExportSuccessPDF: "تم تصدير الرواتب إلى PDF بنجاح!",
    payrollExportError: "خطأ في تصدير الرواتب إلى Excel",
    payrollExportErrorPDF: "خطأ في تصدير الرواتب إلى PDF",
    allowanceDeductionImportSuccess: "تم استيراد البدلات والخصومات بنجاح",
    allowanceDeductionImportError: "خطأ أثناء استيراد البدلات والخصومات",
    // manageVisibility: "إدارة الظهور",

    // قواعد الظهور
    visibilityRulesTitle: "قواعد ظهور المسؤولين",
    visibleEmployees: "الموظفون المرئيون",
    addVisibilityRule: "إضافة قاعدة ظهور",
    removeVisibilityRule: "إزالة الوصول",
    selectAdmin: "اختر المسؤول",
    selectEmployee: "اختر الموظف",
    adminName: "اسم المسؤول",
    employeeName: "اسم الموظف",
    visibilityAdded: "تمت إضافة قاعدة الظهور بنجاح",
    visibilityRemoved: "تمت إزالة قاعدة الظهور بنجاح",
    loadingVisibilityRules: "جاري تحميل قواعد الظهور...",
    noVisibilityRules: "لم يتم تكوين قواعد ظهور",
    bulkAddRules: "إضافة قواعد متعددة",
    selectMultipleEmployees: "اختر عدة موظفين",
    employeesSelected: "موظف محدد",
    addSelectedEmployees: "إضافة الموظفين المحددين",
    searchEmployees: "ابحث عن الموظفين...",
    allEmployees: "جميع الموظفين",
    visibleToAdmin: "مرئي لهذا المسؤول",
    notVisibleToAdmin: "غير مرئي لهذا المسؤول",

    // Statistics
    totalGross: "إجمالي الراتب ",
    employeesPaid: "الموظفون المدفوعون",
    pendingPayments: "المدفوعات المعلقة",
    averageKPI: "متوسط درجة الأداء",
    monthlyGross: "الرواتب الإجمالية الشهرية",
    currentMonth: "الشهر الحالي",
    awaitingApproval: "في انتظار الموافقة",
    performanceIndicator: "مؤشر الأداء",

    // Filters
    filterSearch: "تصفية وبحث",
    searchEmployees: "البحث عن الموظفين",
    searchPlaceholder: "ابحث بالاسم أو رقم الموظف...",
    paymentStatus: "حالة الدفع",
    allStatuses: "جميع الحالات",
    paid: "مدفوع",
    pending: "معلق",
    approved: "معتمد/قيد المعالجة",
    rejected: "مرفوض/معلق",
    department: "القسم/الموقع",
    allDepartments: "جميع الأقسام",
    payPeriod: "فترة الدفع",
    payPeriodMonth: "فترة الدفع (الشهر)",
    payPeriodYear: "فترة الدفع (السنة)",
    refreshData: "تحديث البيانات",
    activeFilters: "الفلاتر النشطة",
    clearAll: "مسح الكل",
    workingCompany: "الشركة",
    allCompanies: "جميع الشركات",

    // Benefits
    benefitsPackage: "حزمة مزايا الموظفين",
    medicalInsurance: "التأمين الطبي",
    socialInsurance: "التأمينات الاجتماعية",

    // Medical Insurance Modal
    miManagement: "إدارة التأمين الطبي",
    miFamilyMembers: "أفراد العائلة",
    miBack: "→ رجوع",
    miManageTiers: "إدارة الفئات",
    miInsuranceTiers: "فئات التأمين",
    miTierName: "اسم الفئة",
    miMonthlyPriceEgp: "السعر الشهري (جنيه)",
    miDescriptionOptional: "الوصف (اختياري)",
    miRemove: "حذف",
    miNoTiers: "لا توجد فئات مضافة",
    miAddTier: "إضافة فئة",
    miTierNamePlaceholder: "مثال: ذهبي",
    miDescriptionPlaceholder: "تفاصيل التغطية...",
    miTierNamePriceRequired: "اسم الفئة والسعر مطلوبان",
    miNetworkError: "خطأ في الشبكة",
    miSearchPlaceholder: "ابحث بالاسم أو الرقم الوظيفي...",
    miLoadingEmployees: "جاري تحميل الموظفين...",
    miMonthlyCost: "التكلفة الشهرية",
    miManage: "إدارة",
    miNoEmployees: "لا يوجد موظفون",
    miMembers: "الأعضاء",
    miTotalMonthly: "الإجمالي الشهري",
    miRelationship: "صلة القرابة",
    miInsuranceTier: "فئة التأمين",
    miNoFamilyMembers: "لم يتم إضافة أفراد عائلة بعد",
    miAddFamilyMember: "إضافة فرد عائلة",
    miNewFamilyMember: "فرد عائلة جديد",
    miFullNamePlaceholder: "الاسم الكامل",
    miRelationshipPlaceholder: "مثال: زوج، ابن، ابنة",
    miSelectTier: "— اختر الفئة —",
    miFillAllFields: "يرجى ملء جميع الحقول",
    miFailedToAdd: "فشل إضافة الفرد",
    miSaving: "جاري الحفظ...",
    miActivate: "تفعيل",
    miDeactivate: "تعطيل",
    miDelete: "حذف نهائي",
    miInactive: "غير نشط",
    annualLeave: "الإجازة السنوية",
    training: "التدريب والتطوير",
    fullFamily: "العائلة_الكاملة",
    govMandated: "إلزامي_  حكومياً",
    paidTimeOff: "إجازة مدفوعة الأجر",
    professionalGrowth: "النمو_المهني",
    comprehensiveCoverage: "تغطية طبية شاملة للموظف وأفراد عائلته",
    pensionCoverage: "التأمين التقاعدي والعجز والبطالة",
    annualVacation: "إجازة سنوية مدفوعة حسب قانون العمل المصري",
    profDevelopment: "برامج التطوير المهني وتحسين المهارات",
    active: "نشط",
    available: "متاح",
    daysRemaining: "يوم متبقي",
    companySponsored: "برعاية الشركة",

    // Payroll Table
    payrollDetails: "تفاصيل الرواتب الشاملة",
    employee: "الموظف",
    basicSalary: "الراتب الأساسي",
    allowances: "البدلات",
    grossSalary: "الراتب الإجمالي",
    deductions: "الخصومات",
    netSalary: "صافي الراتب",
    kpi: "الأداء",
    status: "الحالة",
    actions: "الإجراءات",
    includingOvertime: "+ الوقت الإضافي",
    taxInsuranceOther: "الضرائب + التأمين + أخرى",

    // Employee Details Modal
    employeeDetails: "تفاصيل الموظف ومعلومات الراتب",
    edit: "تعديل",
    cancel: "إلغاء",
    save: "حفظ التغييرات",
    saving: "جاري الحفظ...",
    personalInfo: "المعلومات الشخصية",
    fullName: "الاسم الكامل",
    employeeId: "رقم الموظف",
    email: "البريد الإلكتروني",
    phone: "رقم الهاتف",
    emergencyPhone: "هاتف الطوارئ",
    nationalId: "الرقم القومي",
    dateOfBirth: "تاريخ الميلاد",
    gender: "الجنس",
    male: "ذكر",
    female: "أنثى",
    maritalStatus: "الحالة الاجتماعية",
    single: "أعزب",
    married: "متزوج",
    divorced: "مطلق",
    widowed: "أرمل",
    nationality: "الجنسية",
    city: "المدينة",
    country: "الدولة",
    address: "العنوان",

    // Work Info
    workInfo: "معلومات العمل",
    position: "المنصب",
    hiringDate: "تاريخ التعيين",
    contractType: "نوع العقد",
    fullTime: "دوام كامل",
    partTime: "دوام جزئي",
    contract: "عقد",
    temporary: "مؤقت",
    workingHours: "ساعات العمل",
    contractExpiration: "انتهاء العقد",
    directManager: "المدير المباشر",
    workLocation: "موقع العمل",
    employmentStatus: "حالة التوظيف",

    // Salary & Earnings
    salaryEarnings: "الراتب والمكاسب",
    bonus: "المكافأة",
    overtimeHours: "ساعات العمل الإضافي",
    overtimeTotal: "إجمالي الوقت الإضافي",
    transportation: "بدل المواصلات",
    meal: "بدل الوجبات",
    mobile: "بدل الهاتف",
    bonusAdjustments: "تعديلات المكافأة",
    taxMonth: "ضريبة شهرية",
    socialInsurance: "التأمين الاجتماعي",
    penalty: "جزاء",
    advancePayment: "دفعة مقدمة",

    // Performance
    performance: "الأداء والكفاءة",
    kpiScore: "درجة الأداء",
    performanceRating: "تقييم الأداء",
    excellent: "ممتاز",
    good: "جيد",
    satisfactory: "مرضي",
    needsImprovement: "يحتاج تحسين",

    // Banking
    bankingInfo: "المعلومات البنكية",
    bankName: "اسم البنك",
    accountNumber: "رقم الحساب",
    iban: "الآيبان",
    branchCode: "كود الفرع",

    // Actions
    downloadPayslip: "تحميل كشف الراتب",
    sendEmail: "إرسال عبر البريد",

    // Process Payroll Modal
    processNewPayroll: "معالجة رواتب جديدة",
    selectPeriod: "اختر الفترة",
    chooseEmployees: "اختر الموظفين",
    configureSettings: "إعداد الإعدادات",
    reviewProcess: "مراجعة ومعالجة",
    month: "الشهر",
    year: "السنة",
    startDate: "تاريخ البداية",
    endDate: "تاريخ النهاية",
    selectByDepartment: "اختر الموظفين حسب القسم",
    selectByCompany: "اختر الموظفين حسب الشركة",
    groupByDepartment: "حسب القسم",
    groupByCompany: "حسب الشركة",
    selectAll: "تحديد الكل",
    valid: "صالح",
    payrollSettings: "إعدادات الرواتب",
    paymentMethod: "طريقة الدفع",
    bankTransfer: "تحويل بنكي",
    cash: "نقداً",
    check: "شيك",
    digitalWallet: "محفظة رقمية",
    paymentDate: "تاريخ الدفع",
    processingOptions: "خيارات المعالجة",
    includeOvertime: "تضمين الوقت الإضافي",
    includeBonuses: "تضمين المكافآت",
    processTaxes: "معالجة ضريبة الدخل",
    processInsurance: "معالجة التأمينات الاجتماعية",
    notes: "ملاحظات (اختياري)",
    notesPlaceholder: "أضف أي ملاحظات عن هذه الدفعة...",
    payrollSummary: "ملخص الرواتب",
    employees: "الموظفون",
    grossTotal: "الإجمالي الكلي",
    taxInsurance: "الضرائب والتأمين",
    netTotal: "الصافي الكلي",
    employeeBreakdown: "تفصيل الموظفين",
    important: "هام",
    cannotUndo:
      "سيتم معالجة رواتب {count} موظف ولا يمكن التراجع عن هذا الإجراء.",
    back: "رجوع",
    nextStep: "الخطوة التالية",
    processing: "جاري المعالجة...",
    processPayrollBtn: "معالجة الرواتب",

    // Import Modal
    importPayroll: "استيراد الرواتب من Excel",
    upload: "رفع",
    preview: "معاينة",
    results: "النتائج",
    uploadFile: "اضغط لرفع ملف Excel",
    supportedFormats: "يدعم ملفات .xlsx و .xls و .csv",
    processingFile: "جاري معالجة الملف...",
    expectedFormat: "تنسيق Excel المتوقع",
    formatDescription:
      "يجب أن يحتوي ملف Excel على أعمدة: كود الموظف (مطلوب)، اسم الموظف، تواريخ بداية/نهاية الرواتب، أيام العمل، أيام الغياب، أيام العمل الإضافي، قيمة المكافأة، المصروفات، الإنجاز، والمزيد.",
    importSettings: "إعدادات الاستيراد",
    skipErrors: "تخطي الصفوف التي بها أخطاء",
    autoCalculate: "احسب القيم المفقودة تلقائياً من بيانات الموظف",
    totalRows: "إجمالي الصفوف",
    validRows: "صالح",
    errorRows: "أخطاء",
    row: "صف",
    code: "الكود",
    name: "الاسم",
    period: "الفترة",
    errorsWillBeSkipped: "{count} صف به أخطاء وسيتم تخطيه.",
    total: "الإجمالي",
    successful: "ناجح",
    failed: "فشل",
    skipped: "متخطى",
    successMessage: "تم استيراد {count} سجل رواتب بنجاح!",
    errorsFound: "أخطاء",
    recordsCreated:
      "تم إنشاء سجلات الرواتب. يمكنك الآن مراجعتها في جدول الرواتب.",
    done: "تم",
    importing: "جاري الاستيراد...",
    importRecords: "استيراد {count} سجل",
    importingRecords: "جاري استيراد سجلات الرواتب...",

    showing: "عرض",
    of: "من",
    previous: "السابق",
    next: "التالي",
    // Deductions Modal
    employeeDeductions: "خصومات الموظف",
    addDeduction: "إضافة خصم",
    deductionType: "نوع الخصم",
    selectType: "اختر النوع",
    absence: "غياب",
    penalty: "جزاء",
    advance: "سلفة",
    insurance: "تأمين",
    sickLeave: "إجازة مرضية 25%",
    lateArrival: "تأخير",
    lateArrivalCalcDesc: "خصم التأخير بناءً على الإعدادات",
    calculatingLate: "جاري تحميل المعدلات...",
    lateHours: "ساعات التأخير",
    other: "أخرى",
    amount: "المبلغ (جنيه)",
    date: "التاريخ",
    days: "الأيام (اختياري)",
    description: "الوصف",
    descriptionPlaceholder: "وصف الخصم...",
    saveDeduction: "حفظ الخصم",
    currentDeductions: "الخصومات الحالية",
    loading: "جاري التحميل...",
    noDeductions: "لا توجد خصومات لهذا الموظف",
    totalDeductions: "إجمالي الخصومات",
    unknown: "غير معروف",
    close: "إغلاق",
    view: "عرض",
    showMore: "عرض المزيد",
    remaining: "متبقي",
    viewDetails: "عرض التفاصيل",

    // Deductions Modal
    employeeDeductions: "خصومات الموظف",
    addDeduction: "إضافة خصم",
    deductionType: "نوع الخصم",
    selectType: "اختر النوع",
    absence: "غياب",
    penalty: "جزاء",
    advance: "سلفة",
    insurance: "تأمين",
    sickLeave: "إجازة مرضية 25%",
    lateArrival: "تأخير",
    lateArrivalCalcDesc: "خصم التأخير بناءً على الإعدادات",
    calculatingLate: "جاري تحميل المعدلات...",
    lateHours: "ساعات التأخير",
    other: "أخرى",
    amount: "المبلغ (جنيه)",
    date: "التاريخ",
    days: "الأيام (اختياري)",
    description: "الوصف",
    descriptionPlaceholder: "وصف الخصم...",
    saveDeduction: "حفظ الخصم",
    currentDeductions: "الخصومات الحالية",
    loading: "جاري التحميل...",
    noDeductions: "لا توجد خصومات لهذا الموظف",
    totalDeductions: "إجمالي الخصومات",

    // Months
    january: "يناير",
    february: "فبراير",
    march: "مارس",
    april: "أبريل",
    may: "مايو",
    june: "يونيو",
    july: "يوليو",
    august: "أغسطس",
    september: "سبتمبر",
    october: "أكتوبر",
    november: "نوفمبر",
    december: "ديسمبر",

    2025: "٢٠٢٥",
    2024: "٢٠٢٤",
    2023: "٢٠٢٣",
    2022: "٢٠٢٢",
    2026: "٢٠٢٦",
  },
};
// const Mo3tiCard = ({ title, icon: Icon, children, action, className = "" }) => {
//   const { currentTheme, isDarkMode } = useContext(ThemeContext);

//   return (
//     <div
//       className={`mo3ti-card ${className}`}
//       style={{
//         background: currentTheme.colors.cardBackground,
//         padding: "24.00px",
//         borderRadius: "16.00px",
//         boxShadow: currentTheme.shadows.card,
//         backdropFilter: "blur(40.00px) saturate(200.00%)",
//         border: `2.00px solid ${currentTheme.colors.border}`,
//         transition: "all 0.40s cubic-bezier(0.40, 0.00, 0.00, 1.00)",
//         marginBottom: "24.00px",
//         position: "relative",
//         overflow: "hidden",

//         /* 👇 دي اللي تحركه لليمين */
//         marginLeft: 20, // يخليه يلزق ناحية اليمين
//         marginRight: "0",
//       }}
//     >
//       {/* Decorative gradient overlay */}
//       <div
//         style={{
//           position: "absolute",
//           top: "0",
//           left: "0",
//           right: "0",
//           height: "4px",
//          // background: " (90deg, #2e7cba, #8b5cf6, #ec4899)",
//           opacity: isDarkMode ? 0.8 : 0.6,
//         }}
//       />

//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//           marginBottom: "20px",
//         }}
//       >
//         <h3
//           style={{
//             display: "flex",
//             alignItems: "center",
//             gap: "12px",
//             fontSize: "20px",
//             fontWeight: "800",
//             color: currentTheme.colors.textPrimary,
//             textTransform: "uppercase",
//             letterSpacing: "1px",
//             margin: 0,
//           }}
//         >
//           {Icon && <Icon size={24} color={currentTheme.colors.primary} />}
//           {title}
//         </h3>
//         {action}
//       </div>
//       {children}
//     </div>
//   );
// };

// استخدم الـ Context زي باقي الصفحات

// const Mo3tiStatCard = ({
//   icon: Icon,
//   value,
//   label,
//   color,
//   trend,
//   subtitle,
// }) => {
//   const { currentTheme, isDarkMode } = useContext(ThemeContext);

//   return (
//     <div
//       style={{
//         background: currentTheme.colors.cardBackground,
//         padding: "20px",
//         borderRadius: "16px",
//         border: `2px solid ${currentTheme.colors.border}`,
//         transition: "all 0.3s ease",
//         cursor: "pointer",
//         backdropFilter: "blur(20px)",
//         position: "relative",
//         overflow: "hidden",
//         boxShadow: currentTheme.shadows.card,
//       }}
//       onMouseEnter={(e) => {
//         e.currentTarget.style.transform = "translateY(-4px) scale(1.01)";
//         e.currentTarget.style.boxShadow = isDarkMode
//           ? "0 20px 60px rgba(0,0,0,0.4)"
//           : "0 20px 60px rgba(0,0,0,0.12)";
//       }}
//       onMouseLeave={(e) => {
//         e.currentTarget.style.transform = "translateY(0) scale(1)";
//         e.currentTarget.style.boxShadow = currentTheme.shadows.card;
//       }}
//     >
//       {/* Background decoration */}
//       <div
//         style={{
//           position: "absolute",
//           top: "-50%",
//           right: "-50%",
//           width: "100%",
//           height: "100%",
//           background: ` (135deg, ${color}15, transparent)`,
//           borderRadius: "50%",
//           transform: "rotate(45deg)",
//         }}
//       />

//       <div
//         style={{
//           width: "48px",
//           height: "48px",
//           borderRadius: "16px",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           marginBottom: "16px",
//           background: ` (135deg, ${color}, ${color}dd)`,
//           boxShadow: `0 8px 24px ${color}40`,
//           position: "relative",
//           zIndex: 1,
//         }}
//       >
//         <Icon size={24} color="#fff" />
//       </div>

//       <div
//         style={{
//           fontSize: "28px",
//           fontWeight: "900",
//           margin: "8px 0",
//           letterSpacing: "-1px",
//           color: color,
//           position: "relative",
//           zIndex: 1,
//         }}
//       >
//         {value}
//       </div>

//       <div
//         style={{
//           fontSize: "14px",
//           fontWeight: "700",
//           textTransform: "uppercase",
//           letterSpacing: "1px",
//           color: currentTheme.colors.textPrimary,
//           marginBottom: "6px",
//           position: "relative",
//           zIndex: 1,
//         }}
//       >
//         {label}
//       </div>

//       <div
//         style={{
//           fontSize: "13px",
//           color: currentTheme.colors.textSecondary,
//           marginBottom: "10px",
//           position: "relative",
//           zIndex: 1,
//         }}
//       >
//         {subtitle}
//       </div>

//       {trend && (
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             gap: "6px",
//             fontSize: "13px",
//             fontWeight: "600",
//             color: trend.startsWith("+")
//               ? "#10b981"
//               : trend.startsWith("-")
//                 ? "#ef4444"
//                 : color,
//             position: "relative",
//             zIndex: 1,
//           }}
//         >
//           {trend.startsWith("+") ? (
//             <TrendingUp size={14} />
//           ) : trend.startsWith("-") ? (
//             <TrendingDown size={14} />
//           ) : null}
//           {trend}
//         </div>
//       )}
//     </div>
//   );
// };

// const Mo3tiButton = ({
//   children,
//   variant = "primary",
//   icon: Icon,
//   size = "md",
//   onClick,
//   disabled = false,
//   className = "",
// }) => {
//   const { currentTheme, isDarkMode } = useContext(ThemeContext);

//   const baseStyles = {
//     display: "flex",
//     alignItems: "center",
//     gap: size === "sm" ? "6px" : "6px",
//     border: "none",
//     borderRadius: size === "sm" ? "12px" : "16px",
//     cursor: disabled ? "not-allowed" : "pointer",
//     transition: "all 0.3s ease",
//     fontSize: size === "sm" ? "14px" : "14px",
//     fontWeight: "700",
//     textTransform: "uppercase",
//     letterSpacing: "0.5px",
//     padding: size === "sm" ? "10px 16px" : "16px 28px",
//     opacity: disabled ? 0.6 : 1,
//     position: "relative",
//     overflow: "hidden",
//   };

//   const variantStyles = {
//     primary: {
//       background: ` (135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.primaryDark})`,
//       color: "#fff",
//       boxShadow: currentTheme.shadows.button,
//     },
//     secondary: {
//       background: currentTheme.colors.inputBackground,
//       color: currentTheme.colors.textPrimary,
//       border: `2px solid ${currentTheme.colors.border}`,
//       backdropFilter: "blur(20px)",
//     },
//     ghost: {
//       background: "transparent",
//       color: currentTheme.colors.textSecondary,
//       border: `1px solid ${currentTheme.colors.border}`,
//     },
//     secondary1: {
//       background: currentTheme.colors.inputBackground,
//       //color: currentTheme.colors.textPrimary,
//       border: `2px solid ${currentTheme.colors.border}`,
//       backdropFilter: "blur(20px)",
//     },
//   };

//   return (
//     <button
//       style={{ ...baseStyles, ...variantStyles[variant] }}
//       className={className}
//       onClick={onClick}
//       disabled={disabled}
//       onMouseEnter={(e) => {
//         if (!disabled) {
//           if (variant === "primary") {
//             e.currentTarget.style.transform = "translateY(-3px) scale(1.05)";
//             e.currentTarget.style.boxShadow = `0 15px 50px ${currentTheme.colors.primary}60`;
//           } else {
//             e.currentTarget.style.transform = "translateY(-2px)";
//             e.currentTarget.style.background =
//               variant === "secondary"
//                 ? ` (135deg, ${currentTheme.colors.success}, #059669)`
//                 : `${currentTheme.colors.primary}20`;
//             e.currentTarget.style.color = "#fff";
//           }
//         }
//       }}
//       onMouseLeave={(e) => {
//         if (!disabled) {
//           e.currentTarget.style.transform = "translateY(0) scale(1)";
//           e.currentTarget.style.background = variantStyles[variant].background;
//           e.currentTarget.style.color = variantStyles[variant].color;
//           if (variant === "primary") {
//             e.currentTarget.style.boxShadow = variantStyles[variant].boxShadow;
//           }
//         }
//       }}
//     >
//       {Icon && <Icon size={size === "sm" ? 16 : 20} />}
//       {children}
//     </button>
//   );
// };

// Helper function to generate status badges
// const getStatusBadge = (status, currentTheme) => {
//   const statusConfig = {
//     paid: {
//       symbol: '✔️',
//       color: currentTheme?.colors?.success || '#10b981',
//       bg: 'rgba(16,185,129,0.15)',
//       icon: CheckCircle,
//       glow: '0 0 20px rgba(16,185,129,0.3)',
//     },
//     pending: {
//       //symbol: '⏳',
//       color: currentTheme?.colors?.warning || '#f59e0b',
//       bg: 'rgba(245,158,11,0.15)',
//       icon: Clock,
//       glow: '0 0 20px rgba(245,158,11,0.3)',
//     },
//     'On Hold': {
//       symbol: '⏸',
//       color: currentTheme?.colors?.danger || '#ef4444',
//       bg: 'rgba(239,68,68,0.15)',
//       icon: AlertCircle,
//       glow: '0 0 20px rgba(239,68,68,0.3)',
//     },
//     Processing: {
//       symbol: '🔄',
//       color: currentTheme?.colors?.primary || '#2e7cba',
//       bg: 'rgba(59,130,246,0.15)',
//       icon: Activity,
//       glow: '0 0 20px rgba(59,130,246,0.3)',
//     },
//     approved: {
//       symbol: '✔✔',
//       color: currentTheme?.colors?.primary || '#2e7cba',
//       bg: 'rgba(59,130,246,0.15)',
//       icon: CheckCircle,
//       glow: '0 0 20px rgba(59,130,246,0.3)',
//     },
//     rejected: {
//       symbol: '⛔',
//       color: currentTheme?.colors?.danger || '#ef4444',
//       bg: 'rgba(239,68,68,0.15)',
//       icon: AlertCircle,
//       glow: '0 0 20px rgba(239,68,68,0.3)',
//     },
//   };

//   const config = statusConfig[status] || statusConfig.pending;
//   const IconComponent = config.icon;

//   return (
//     <span
//       style={{
//         display: 'inline-flex',
//         alignItems: 'center',
//         gap: '6px',
//         background: config.bg,
//         color: config.color,
//         padding: '8px 14px',
//         borderRadius: '12px',
//         fontSize: '16px',
//         fontWeight: '700',
//         border: `2px solid ${config.color}40`,
//         boxShadow: config.glow,
//         transition: 'all 0.3s ease',
//         minWidth: '60px',
//         justifyContent: 'center',
//       }}
//     >
//       <IconComponent size={16} />
//       {config.symbol}
//     </span>
//   );
// };

// ✅ ضع هذا بدلاً من getStatusBadge القديمة
// ✅ النسخة الصحيحة - بدون اعتماد على متغيرات خارجية
// ✅ حط ده مكان getStatusBadge القديمة
// const getStatusBadge = (status) => {
//   const { language } = useContext(LanguageContext);
//   const t = (key) => translations[language][key] || key;

//   const statusStyles = {
//     paid: { bg: "#d1fae5", color: "#065f46", text: t('paid') },
//     pending: { bg: "#fef3c7", color: "#92400e", text: t('pending') },
//     approved: { bg: "#dbeafe", color: "#1e40af", text: t('approved') },
//     rejected: { bg: "#fee2e2", color: "#991b1b", text: t('rejected') },
//   };

//   const style = statusStyles[status] || statusStyles.pending;

//   return (
//     <span style={{
//       padding: "0.25rem 0.625rem",
//       borderRadius: "0.5rem",
//       fontSize: "0.75rem",
//       fontWeight: "600",
//       backgroundColor: style.bg,
//       color: style.color,
//       display: "inline-block",
//     }}>
//       {style.text}
//     </span>
//   );
// };

// ✅ أضف هنا - قبل const ProfessionalPayrollSystem
// ✅ أضف هنا - قبل getScreenSize
// ✅ StatusBadge Component
const StatusBadge = ({ status, language, translations }) => {
  const t = (key) => translations[language][key] || key;

  const statusStyles = {
    paid: { bg: "#d1fae5", color: "#065f46", text: t("paid") },
    pending: { bg: "#fef3c7", color: "#92400e", text: t("pending") },
    approved: { bg: "#dbeafe", color: "#1e40af", text: t("approved") },
    rejected: { bg: "#fee2e2", color: "#991b1b", text: t("rejected") },
  };

  const style = statusStyles[status] || statusStyles.pending;

  return (
    <span
      style={{
        padding: "0.25rem 0.625rem",
        borderRadius: "0.5rem",
        fontSize: "0.75rem",
        fontWeight: "600",
        backgroundColor: style.bg,
        color: style.color,
        display: "inline-block",
      }}
    >
      {style.text}
    </span>
  );
};

const Mo3tiButton = ({
  children,
  variant = "primary",
  icon: Icon,
  size = "md",
  onClick,
  disabled = false,
}) => {
  const { theme } = useContext(ThemeContext);

  const buttonStyles = {
    primary: {
      background: "#2e7cba",
      color: "white",
      border: "none",
    },
    secondary: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: theme === "dark" ? "white" : "#1a1a1a",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.2)"
          : "1px solid rgba(0,0,0,0.1)",
    },
    ghost: {
      background: "transparent",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
      border: `1px solid ${
        theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
      }`,
    },
  };

  const sizeStyles = {
    sm: { padding: "0.5rem 0.75rem", fontSize: "0.875rem" },
    md: { padding: "0.75rem 1.5rem", fontSize: "1rem" },
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...buttonStyles[variant],
        ...sizeStyles[size],
        borderRadius: "0.75rem",
        fontWeight: "600",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        transition: "all 0.3s ease",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 16 : 20} />}
      {children}
    </button>
  );
};

const Mo3tiCard = ({ title, icon: Icon, children }) => {
  const { theme } = useContext(ThemeContext);

  return (
    <div
      style={{
        background:
          theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
        border:
          theme === "dark"
            ? "1px solid rgba(255,255,255,0.15)"
            : "1px solid rgba(0,0,0,0.05)",
        borderRadius: "1.5rem",
        padding: "1.5rem",
        marginBottom: "1.5rem",

        boxShadow:
          theme === "dark"
            ? "0 8px 32px rgba(0,0,0,0.3)"
            : "0 8px 32px rgba(0,0,0,0.1)",
      }}
    >
      {title && (
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            fontSize: "1.25rem",
            fontWeight: "700",
            color: theme === "dark" ? "white" : "#1a1a1a",
            marginBottom: "1.5rem",
          }}
        >
          {Icon && <Icon size={24} />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
};

const Mo3tiStatCard = ({ icon: Icon, value, label, color, subtitle }) => {
  const { theme } = useContext(ThemeContext);

  return (
    <div
      style={{
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 0.6)"
            : "rgba(255, 255, 255, 0.9)",
        border:
          theme === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.05)",
        borderRadius: "1.5rem",
        padding: "2rem",
        boxShadow:
          theme === "dark"
            ? "0 8px 32px rgba(0,0,0,0.3)"
            : "0 8px 32px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "0.5rem",
        }}
      >
        <Icon size={20} style={{ color }} />
        <h4
          style={{
            fontSize: "0.875rem",
            fontWeight: "600",
            color: theme === "dark" ? "white" : "#1a1a1a",
            margin: 0,
          }}
        >
          {label}
        </h4>
      </div>
      <p
        style={{
          fontSize: "1.875rem",
          fontWeight: "700",
          color,
          margin: "0.5rem 0",
        }}
      >
        {value}
      </p>
      <p
        style={{
          fontSize: "0.75rem",
          color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
          margin: 0,
        }}
      >
        {subtitle}
      </p>
    </div>
  );
};
const getScreenSize = () => {
  const width = window.innerWidth;
  if (width <= 480) return "xs";
  if (width <= 768) return "sm";
  if (width <= 1024) return "md";
  if (width <= 1366) return "lg";
  if (width <= 1920) return "xl";
  return "xxl";
};

const getResponsiveSpacing = (screenSize) => {
  const spacingMap = {
    xs: { padding: "0.75rem", margin: "0.75rem", gap: "0.5rem" },
    sm: { padding: "1rem", margin: "1rem", gap: "0.75rem" },
    md: { padding: "1.25rem", margin: "1.25rem", gap: "1rem" },
    lg: { padding: "1.5rem", margin: "1.5rem", gap: "1.25rem" },
    xl: { padding: "1.75rem", margin: "1.75rem", gap: "1.5rem" },
    xxl: { padding: "2rem", margin: "2rem", gap: "1.75rem" },
  };
  return spacingMap[screenSize] || spacingMap.md;
};

const getResponsiveFontSizes = (screenSize) => {
  const fontMap = {
    xs: {
      tiny: "0.625rem",
      small: "0.75rem",
      body: "0.875rem",
      large: "1rem",
      xlarge: "1.25rem",
    },
    sm: {
      tiny: "0.6875rem",
      small: "0.8125rem",
      body: "0.9375rem",
      large: "1.125rem",
      xlarge: "1.375rem",
    },
    md: {
      tiny: "0.75rem",
      small: "0.875rem",
      body: "1rem",
      large: "1.25rem",
      xlarge: "1.5rem",
    },
    lg: {
      tiny: "0.8125rem",
      small: "0.9375rem",
      body: "1.0625rem",
      large: "1.375rem",
      xlarge: "1.625rem",
    },
    xl: {
      tiny: "0.875rem",
      small: "1rem",
      body: "1.125rem",
      large: "1.5rem",
      xlarge: "1.75rem",
    },
    xxl: {
      tiny: "0.9375rem",
      small: "1.0625rem",
      body: "1.25rem",
      large: "1.625rem",
      xlarge: "2rem",
    },
  };
  return fontMap[screenSize] || fontMap.md;
};

const getResponsiveContainer = (screenSize) => {
  const containerMap = {
    xs: { maxWidth: "100%", padding: "0.75rem" },
    sm: { maxWidth: "100%", padding: "1rem" },
    md: { maxWidth: "100%", padding: "1.25rem" },
    lg: { maxWidth: "75rem", padding: "1.5rem" },
    xl: { maxWidth: "87.5rem", padding: "1.75rem" },
    xxl: { maxWidth: "100rem", padding: "2rem" },
  };
  return containerMap[screenSize] || containerMap.md;
};

const getResponsiveGrid = (screenSize) => {
  const gridMap = {
    xs: 1, // موبايل صغير: card واحد في الصف
    sm: 2, // موبايل كبير/تابلت: card
    md: 4, // تابلت أفقي: 3 cards
    lg: 4, // لابتوب: 4 cards ✅
    xl: 4, // شاشة كبيرة: 4 cards ✅
    xxl: 4, // شاشة عريضة جداً: 4 cards ✅
  };
  return gridMap[screenSize] || 4;
};

// ProcessPayrollModal Component
const ProcessPayrollModal = ({
  onClose,
  onProcessComplete,
  payrollData,
  selectedEmployees,
  setSelectedEmployees,
  selectedMonth,
  setSelectedMonth,
  theme,
  language,
  API_BASE_URL,
  clientName,
  showAlert,
  processStep,
  setProcessStep,
}) => {
  const { currentTheme } = useContext(ThemeContext);
  // const { language } = useContext(LanguageContext);
  const t = (key) => translations[language][key] || key;

  // Access setSelectedMonth from parent scope
  // (Assume setSelectedMonth is available in the closure)

  // ✅ حط الـ styles هنا جوه الـ Modal
  const modalStyles = {
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 3000,
      padding: "20px",
      overflowY: "auto",
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },
    modalContent: {
      background:
        theme === "dark"
          ? "rgba(30, 41, 59, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(25px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
      borderRadius: "24px",
      padding: "40px",
      width: "100%",
      maxWidth: "1200px",
      margin: "20px auto",
      maxHeight: "90vh",
      overflowY: "auto",
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      boxShadow:
        theme === "dark"
          ? "0 20px 60px rgba(0,0,0,0.5)"
          : "0 20px 60px rgba(0,0,0,0.2)",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "40px",
      paddingBottom: "20px",
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.1)"
          : "2px solid rgba(0,0,0,0.1)",
    },
    title: {
      color: theme === "dark" ? "#ffffff" : "#1a1a1a",
      fontSize: "32px",
      fontWeight: "800",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    formSection: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      borderRadius: "20px",
      padding: "32px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.1)"
          : "2px solid rgba(0,0,0,0.05)",
      marginBottom: "24px",
    },
    sectionTitle: {
      color: theme === "dark" ? "#ffffff" : "#1a1a1a",
      fontSize: "24px",
      fontWeight: "700",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "24px",
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    formGroupWithMargin: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginTop: "20px",
    },
    label: {
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
      fontSize: "14px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    input: {
      background:
        theme === "dark"
          ? "rgba(30, 41, 59, 0.8)"
          : "rgba(255, 255, 255, 0.95)",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.1)"
          : "2px solid rgba(0,0,0,0.1)",
      borderRadius: "12px",
      padding: "16px",
      color: theme === "dark" ? "#ffffff" : "#1a1a1a",
      fontSize: "16px",
      fontWeight: "500",
      outline: "none",
    },
    checkboxGroup: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    stepIndicator: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "20px",
      marginBottom: "40px",
      padding: "20px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      borderRadius: "16px",
    },
    step: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 20px",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "600",
      color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
    },
    activeStep: {
      background: "#2e7cba",
      color: "#ffffff",
    },
    stepNumber: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      fontWeight: "700",
      background:
        theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
    },
    activeStepNumber: {
      background: "#ffffff",
      color: "#2e7cba",
    },
    employeeList: {
      maxHeight: "400px",
      overflowY: "auto",
      padding: "16px",
      background:
        theme === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.5)",
      borderRadius: "12px",
    },
    employeeItem: {
      padding: "16px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.9)",
      borderRadius: "12px",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      cursor: "pointer",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
    },
    previewCard: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      borderRadius: "16px",
      padding: "24px",
      marginBottom: "20px",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
    },
    previewTotal: {
      marginTop: "20px",
      padding: "20px",
      background: "#2e7cba",
      color: "#ffffff",
      borderRadius: "12px",
      fontSize: "24px",
      fontWeight: "800",
      textAlign: "center",
    },
    actionButtons: {
      display: "flex",
      gap: "16px",
      justifyContent: "flex-end",
      marginTop: "40px",
      paddingTop: "32px",
      borderTop:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.1)"
          : "2px solid rgba(0,0,0,0.1)",
    },
  };
  const [formData, setFormData] = useState({
    payrollPeriod: {
      month: selectedMonth?.month || new Date().getMonth() + 1,
      year: selectedMonth?.year || new Date().getFullYear(),
      day: null,
    },
    includeOvertime: true,
    includeBonuses: true,
    processTaxes: true,
    processInsurance: true,
    paymentMethod: "bank_transfer",
    paymentDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [previewData, setPreviewData] = useState(null);
  const [selectedAll, setSelectedAll] = useState(false);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [groupByMode, setGroupByMode] = useState("department"); // "department" | "company"
  const [processingStatus, setProcessingStatus] = useState("");

  // always up-to-date ref so handleProcessPayroll never reads stale formData
  const formDataRef = React.useRef(formData);
  React.useEffect(() => {
    formDataRef.current = formData;
  });

  useEffect(() => {
    if (processStep === 4 && selectedEmployees.length > 0) {
      calculatePreview();
    }
  }, [processStep, selectedEmployees]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Only sync month/year to parent, ignore day changes
    if (field === "payrollPeriod") {
      // Don't update if only day changed
      const prevPeriod = formData.payrollPeriod;
      if (value.month !== prevPeriod.month || value.year !== prevPeriod.year) {
        setSelectedMonth({
          month: value.month,
          year: value.year,
        });
      }
    }
  };

  const handleEmployeeSelection = (employee) => {
    setSelectedEmployees((prev) => {
      const isSelected = prev.some((emp) => emp.id === employee.id);
      if (isSelected) {
        return prev.filter((emp) => emp.id !== employee.id);
      } else {
        return [...prev, employee];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees([...payrollData]);
    }
    setSelectedAll(!selectedAll);
  };

  const calculatePreview = () => {
    const preview = {
      totalEmployees: selectedEmployees.length,
      totalGross: selectedEmployees.reduce(
        (sum, emp) => sum + emp.grossSalary,
        0,
      ),
      totalDeductions: selectedEmployees.reduce(
        (sum, emp) => sum + emp.totalDeductions,
        0,
      ),
      totalNet: selectedEmployees.reduce((sum, emp) => sum + emp.netSalary, 0),
      taxTotal: selectedEmployees.reduce((sum, emp) => sum + emp.taxMonth, 0),
      insuranceTotal: selectedEmployees.reduce(
        (sum, emp) => sum + emp.socialInsurance,
        0,
      ),
      employees: selectedEmployees.map((emp) => ({
        name: emp.employeeName,
        gross: emp.grossSalary,
        deductions: emp.totalDeductions,
        net: emp.netSalary,
      })),
    };
    setPreviewData(preview);
  };

  const handleProcessPayroll = async () => {
    setProcessingPayroll(true);
    setProcessingStatus("Preparing payroll processing...");
    try {
      const syncYear = Number(
        formDataRef.current?.payrollPeriod?.year || selectedMonth?.year,
      );
      const syncMonth = Number(
        formDataRef.current?.payrollPeriod?.month || selectedMonth?.month,
      );
      const syncDay = formDataRef.current?.payrollPeriod?.day
        ? Number(formDataRef.current.payrollPeriod.day)
        : null;

      const empInfCodes = [
        ...new Set(
          selectedEmployees
            .map((employee) => String(employee.infinityCode || "").trim())
            .filter(Boolean),
        ),
      ];

      if (!syncYear || !syncMonth) {
        throw new Error("Payroll period is required before processing");
      }

      if (!empInfCodes.length) {
        throw new Error("No employees selected for payroll processing");
      }

      console.log("🔄 Processing payroll via /process endpoint...", {
        year: syncYear,
        month: syncMonth,
        employees: empInfCodes.length,
      });

      setProcessingStatus(
        `Processing payroll for ${empInfCodes.length} employee(s) — ${syncYear}/${String(syncMonth).padStart(2, "0")}...`,
      );

      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/process?year=${syncYear}&month=${syncMonth}${syncDay ? `&day=${syncDay}` : ""}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ empInfCodes }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      console.log("✅ Payroll processed successfully:", result);

      const successMessage = t("payrollProcessedSuccess").replace(
        "{count}",
        result?.data?.payrollsUpdated ?? empInfCodes.length,
      );
      showAlert(t("success"), ` ${successMessage}`, "success");

      onClose();

      setTimeout(() => {
        onProcessComplete();
      }, 500);
    } catch (error) {
      console.error("❌ Error processing payroll:", error);
      const errorMessage = t("payrollProcessingError").replace(
        "{error}",
        error.message,
      );
      showAlert(t("error"), `❌ ${errorMessage}`, "error");
    } finally {
      setProcessingPayroll(false);
      setProcessingStatus("");
    }
  };

  // ✅ في كل Modal (ProcessPayrollModal, ExcelImportModal, EmployeeDetailsModal, DeductionsModal)
  // غيّر الـ modal overlay style:

  const steps = [
    { number: 1, title: t("selectPeriod"), icon: Calendar },
    { number: 2, title: t("chooseEmployees"), icon: Users },
    { number: 3, title: t("configureSettings"), icon: Settings },
    { number: 4, title: t("reviewProcess"), icon: CheckCircle },
  ];

  const renderStepContent = () => {
    switch (processStep) {
      case 1:
        return (
          <div style={modalStyles.formSection}>
            <h3 style={modalStyles.sectionTitle}>
              <Calendar size={20} />
              {t("payPeriod")}
            </h3>
            <div style={modalStyles.formGrid}>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>{t("month")}</label>
                <ReusableDropdown
                  items={[
                    { id: 1, label: t("january") },
                    { id: 2, label: t("february") },
                    { id: 3, label: t("march") },
                    { id: 4, label: t("april") },
                    { id: 5, label: t("may") },
                    { id: 6, label: t("june") },
                    { id: 7, label: t("july") },
                    { id: 8, label: t("august") },
                    { id: 9, label: t("september") },
                    { id: 10, label: t("october") },
                    { id: 11, label: t("november") },
                    { id: 12, label: t("december") },
                  ]}
                  value={
                    [
                      { id: 1, label: t("january") },
                      { id: 2, label: t("february") },
                      { id: 3, label: t("march") },
                      { id: 4, label: t("april") },
                      { id: 5, label: t("may") },
                      { id: 6, label: t("june") },
                      { id: 7, label: t("july") },
                      { id: 8, label: t("august") },
                      { id: 9, label: t("september") },
                      { id: 10, label: t("october") },
                      { id: 11, label: t("november") },
                      { id: 12, label: t("december") },
                    ].find((m) => m.id === formData.payrollPeriod.month)
                      ?.label || ""
                  }
                  onChange={(val) => {
                    const month = [
                      { id: 1, label: t("january") },
                      { id: 2, label: t("february") },
                      { id: 3, label: t("march") },
                      { id: 4, label: t("april") },
                      { id: 5, label: t("may") },
                      { id: 6, label: t("june") },
                      { id: 7, label: t("july") },
                      { id: 8, label: t("august") },
                      { id: 9, label: t("september") },
                      { id: 10, label: t("october") },
                      { id: 11, label: t("november") },
                      { id: 12, label: t("december") },
                    ].find((m) => m.label === val)?.id;
                    if (month)
                      handleInputChange("payrollPeriod", {
                        ...formData.payrollPeriod,
                        month,
                      });
                  }}
                  onSelect={(item) => {
                    handleInputChange("payrollPeriod", {
                      ...formData.payrollPeriod,
                      month: item.id,
                    });
                  }}
                  placeholder={t("selectMonth") || "Select Month"}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  theme={theme}
                  language={language}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>{t("year")}</label>
                <ReusableDropdown
                  items={[2023, 2024, 2025, 2026].map((year) => ({
                    id: year,
                    label: String(year),
                  }))}
                  value={String(formData.payrollPeriod.year)}
                  onChange={(val) =>
                    handleInputChange("payrollPeriod", {
                      ...formData.payrollPeriod,
                      year: parseInt(val),
                    })
                  }
                  onSelect={(item) => {
                    handleInputChange("payrollPeriod", {
                      ...formData.payrollPeriod,
                      year: item.id,
                    });
                  }}
                  placeholder={t("selectYear") || "Select Year"}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  theme={theme}
                  language={language}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>{t("day") || "Day"}</label>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: formData.payrollPeriod.day
                      ? theme === "dark"
                        ? "#60a5fa"
                        : "#2563eb"
                      : theme === "dark"
                        ? "rgba(203,213,225,0.55)"
                        : "rgba(100,116,139,0.85)",
                    marginBottom: "6px",
                    padding: "6px 10px",
                    borderRadius: "8px",
                    background: formData.payrollPeriod.day
                      ? theme === "dark"
                        ? "rgba(59,130,246,0.12)"
                        : "rgba(37,99,235,0.07)"
                      : theme === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.03)",
                    border: `1px solid ${
                      formData.payrollPeriod.day
                        ? theme === "dark"
                          ? "rgba(96,165,250,0.3)"
                          : "rgba(37,99,235,0.2)"
                        : theme === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.07)"
                    }`,
                    transition: "all 0.2s ease",
                  }}
                >
                  {formData.payrollPeriod.day
                    ? language === "ar"
                      ? `احتساب الراتب من اليوم 1 حتى اليوم ${formData.payrollPeriod.day}`
                      : `Calculate payroll from day 1 up to day ${formData.payrollPeriod.day}`
                    : language === "ar"
                      ? "اختر يوماً لتحديد نهاية فترة الاحتساب"
                      : "Select a day to set the payroll end day"}
                </div>
                <ReusableDropdown
                  items={Array.from(
                    {
                      length: new Date(
                        formData.payrollPeriod.year,
                        formData.payrollPeriod.month,
                        0,
                      ).getDate(),
                    },
                    (_, i) => ({ id: i + 1, label: String(i + 1) }),
                  )}
                  value={formData.payrollPeriod.day ?? null} // ← number, not String()
                  onChange={() => {}}
                  onSelect={(item) => {
                    setFormData((prev) => ({
                      ...prev,
                      payrollPeriod: { ...prev.payrollPeriod, day: item.id },
                    }));
                  }}
                  onClear={() => {
                    setFormData((prev) => ({
                      ...prev,
                      payrollPeriod: { ...prev.payrollPeriod, day: null },
                    }));
                  }}
                  placeholder={t("selectDay") || "Select Day"}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  theme={theme}
                  language={language}
                  maxItems={31}
                  showAllOnArrowClick={true}
                  showClear={true}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        const employeesByDepartment = payrollData.reduce((acc, employee) => {
          const dept = employee.department;
          if (!acc[dept]) {
            acc[dept] = [];
          }
          acc[dept].push(employee);
          return acc;
        }, {});

        const employeesByCompany = payrollData.reduce((acc, employee) => {
          const company = employee.workingCompany || "Unknown";
          if (!acc[company]) {
            acc[company] = [];
          }
          acc[company].push(employee);
          return acc;
        }, {});

        const activeGroups =
          groupByMode === "company"
            ? employeesByCompany
            : employeesByDepartment;

        const handleDepartmentSelection = (department, employees) => {
          const allSelected = employees.every((emp) =>
            selectedEmployees.some((selected) => selected.id === emp.id),
          );

          if (allSelected) {
            setSelectedEmployees((prev) =>
              prev.filter(
                (selected) => !employees.some((emp) => emp.id === selected.id),
              ),
            );
          } else {
            setSelectedEmployees((prev) => {
              const newEmployees = employees.filter(
                (emp) => !prev.some((selected) => selected.id === emp.id),
              );
              return [...prev, ...newEmployees];
            });
          }
        };

        const isDepartmentFullySelected = (employees) => {
          return employees.every((emp) =>
            selectedEmployees.some((selected) => selected.id === emp.id),
          );
        };

        const isDepartmentPartiallySelected = (employees) => {
          return (
            employees.some((emp) =>
              selectedEmployees.some((selected) => selected.id === emp.id),
            ) && !isDepartmentFullySelected(employees)
          );
        };

        return (
          <div
            style={{
              ...modalStyles.formSection,
              background: `${currentTheme.colors.primary}05`,
              border: `2px solid ${currentTheme.colors.primary}20`,
              borderRadius: "20px",
              boxShadow: "0 8px 32px rgba(59,130,246,0.08)",
              padding: "32px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
                borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
                paddingBottom: "12px",
              }}
            >
              <h3
                style={{
                  ...modalStyles.sectionTitle,
                  fontSize: "22px",
                  color: currentTheme.colors.textPrimary,
                  letterSpacing: "1px",
                  textShadow: `0 2px 8px ${currentTheme.colors.primary}10`,
                }}
              >
                <Users size={22} />
                {groupByMode === "company"
                  ? t("selectByCompany")
                  : t("selectByDepartment")}
              </h3>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                {/* Group-by toggle */}
                <div
                  style={{
                    display: "flex",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: `1px solid ${currentTheme.colors.primary}40`,
                  }}
                >
                  <button
                    onClick={() => setGroupByMode("department")}
                    style={{
                      padding: "6px 14px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      border: "none",
                      background:
                        groupByMode === "department"
                          ? currentTheme.colors.primary
                          : "transparent",
                      color:
                        groupByMode === "department"
                          ? "#fff"
                          : currentTheme.colors.textSecondary,
                      transition: "all 0.2s",
                    }}
                  >
                    {t("groupByDepartment")}
                  </button>
                  <button
                    onClick={() => setGroupByMode("company")}
                    style={{
                      padding: "6px 14px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      border: "none",
                      borderLeft: `1px solid ${currentTheme.colors.primary}40`,
                      background:
                        groupByMode === "company"
                          ? currentTheme.colors.primary
                          : "transparent",
                      color:
                        groupByMode === "company"
                          ? "#fff"
                          : currentTheme.colors.textSecondary,
                      transition: "all 0.2s",
                    }}
                  >
                    {t("groupByCompany")}
                  </button>
                </div>
                <div
                  style={{
                    ...modalStyles.checkboxGroup,
                    background: `${currentTheme.colors.primary}10`,
                    borderRadius: "8px",
                    padding: "8px 16px",
                    boxShadow: "0 2px 8px rgba(59,130,246,0.06)",
                  }}
                >
                  <input
                    type="checkbox"
                    id="selectAll"
                    checked={selectedAll}
                    onChange={handleSelectAll}
                    style={{
                      accentColor: currentTheme.colors.primary,
                      width: "18px",
                      height: "18px",
                      marginRight: "8px",
                      cursor: "pointer",
                    }}
                  />
                  <label
                    style={{
                      ...modalStyles.label,
                      fontWeight: "700",
                      color: currentTheme.colors.primary,
                    }}
                    htmlFor="selectAll"
                  >
                    {t("selectAll")}{" "}
                    <span
                      style={{
                        background: currentTheme.colors.primary,
                        color: "#fff",
                        borderRadius: "6px",
                        padding: "2px 8px",
                        marginLeft: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {payrollData.length}
                    </span>
                  </label>
                </div>
              </div>
              {/* end right-side flex wrapper */}
            </div>

            <div
              style={{
                ...modalStyles.employeeList,
                background: currentTheme.colors.cardBackground,
                border: `2px solid ${currentTheme.colors.primary}10`,
                borderRadius: "16px",
                boxShadow: "0 4px 24px rgba(59,130,246,0.04)",
                padding: "8px 0",
              }}
            >
              {Object.entries(activeGroups).map(([department, employees]) => {
                const isFullySelected = isDepartmentFullySelected(employees);
                const isPartiallySelected =
                  isDepartmentPartiallySelected(employees);
                const departmentTotal = employees.reduce(
                  (sum, emp) => sum + emp.netSalary,
                  0,
                );

                return (
                  <div
                    key={department}
                    style={{
                      marginBottom: "18px",
                      borderRadius: "14px",
                      overflow: "hidden",
                      boxShadow: "0 2px 12px rgba(59,130,246,0.03)",
                      background: `${currentTheme.colors.primary}04`,
                      border: `1px solid ${currentTheme.colors.primary}10`,
                    }}
                  >
                    <div
                      style={{
                        padding: "18px 24px",
                        background: `${currentTheme.colors.primary}10`,
                        borderBottom: `1px solid ${currentTheme.colors.primary}20`,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        boxShadow: "0 2px 8px rgba(59,130,246,0.04)",
                        transition: "background 0.2s",
                      }}
                      onClick={() =>
                        handleDepartmentSelection(department, employees)
                      }
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = `${currentTheme.colors.primary}20`)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = `${currentTheme.colors.primary}10`)
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isFullySelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isPartiallySelected;
                        }}
                        style={{
                          accentColor: currentTheme.colors.primary,
                          width: "20px",
                          height: "20px",
                          marginRight: "8px",
                          cursor: "pointer",
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "800",
                            fontSize: "17px",
                            color: currentTheme.colors.primary,
                            letterSpacing: "0.5px",
                          }}
                        >
                          {department}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: currentTheme.colors.textSecondary,
                            marginTop: "2px",
                          }}
                        >
                          <span
                            style={{
                              background: currentTheme.colors.primary,
                              color: "#fff",
                              borderRadius: "6px",
                              padding: "2px 8px",
                              fontWeight: "600",
                              fontSize: "12px",
                              marginRight: "8px",
                            }}
                          >
                            {employees.length}
                          </span>
                          employees • Total:{" "}
                          <span
                            style={{
                              color: currentTheme.colors.success,
                              fontWeight: "700",
                            }}
                          >
                            {`${departmentTotal.toLocaleString()} ${t(
                              "currency",
                            )}`}
                          </span>
                        </div>
                      </div>
                      <ChevronDown
                        size={22}
                        style={{
                          color: currentTheme.colors.textSecondary,
                          opacity: 0.7,
                        }}
                      />
                    </div>

                    <div
                      style={{
                        background: currentTheme.colors.cardBackground,
                        padding: "0 0 8px 0",
                      }}
                    >
                      {employees.map((employee) => (
                        <div
                          key={employee.id}
                          style={{
                            ...modalStyles.employeeItem,
                            paddingLeft: "64px",
                            paddingRight: "24px",
                            borderRadius: "10px",
                            margin: "6px 0",
                            background: selectedEmployees.some(
                              (emp) => emp.id === employee.id,
                            )
                              ? `${currentTheme.colors.success}10`
                              : "transparent",
                            border: selectedEmployees.some(
                              (emp) => emp.id === employee.id,
                            )
                              ? `2px solid ${currentTheme.colors.success}40`
                              : `1px solid ${currentTheme.colors.borderLight}`,
                            boxShadow: selectedEmployees.some(
                              (emp) => emp.id === employee.id,
                            )
                              ? "0 2px 12px rgba(16,185,129,0.08)"
                              : "none",
                            transition: "all 0.2s",
                          }}
                          onClick={() => handleEmployeeSelection(employee)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              selectedEmployees.some(
                                (emp) => emp.id === employee.id,
                              )
                                ? `${currentTheme.colors.success}20`
                                : `${currentTheme.colors.primary}08`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              selectedEmployees.some(
                                (emp) => emp.id === employee.id,
                              )
                                ? `${currentTheme.colors.success}10`
                                : "transparent";
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedEmployees.some(
                              (emp) => emp.id === employee.id,
                            )}
                            onChange={() => {}}
                            style={{
                              accentColor: currentTheme.colors.success,
                              width: "18px",
                              height: "18px",
                              marginRight: "12px",
                              cursor: "pointer",
                            }}
                          />

                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontWeight: "700",
                                color: currentTheme.colors.textPrimary,
                                fontSize: "15px",
                                marginBottom: "2px",
                              }}
                            >
                              {employee.employeeName}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: currentTheme.colors.textSecondary,
                              }}
                            ></div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div
                              style={{
                                fontWeight: "700",
                                color: currentTheme.colors.success,
                                fontSize: "15px",
                              }}
                            >
                              {employee.netSalary.toLocaleString()}{" "}
                              {t("currency")}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: currentTheme.colors.textMuted,
                              }}
                            >
                              Net Salary
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedEmployees.length > 0 && (
              <div
                style={{
                  marginTop: "24px",
                  padding: "18px",
                  background: `${currentTheme.colors.success}20`,
                  borderRadius: "12px",
                  border: `2px solid ${currentTheme.colors.success}30`,
                  boxShadow: "0 2px 12px rgba(16,185,129,0.08)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: currentTheme.colors.success,
                    fontWeight: "700",
                    fontSize: "16px",
                    letterSpacing: "0.5px",
                  }}
                >
                  <CheckCircle
                    size={18}
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  {selectedEmployees.length} employees selected • Total:{" "}
                  <span
                    style={{
                      color: currentTheme.colors.primary,
                      fontWeight: "800",
                    }}
                  >
                    {selectedEmployees
                      .reduce((sum, emp) => sum + emp.netSalary, 0)
                      .toLocaleString()}{" "}
                    {t("currency")}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      case 3:
        return (
          <div style={modalStyles.formSection}>
            <h3 style={modalStyles.sectionTitle}>
              <Settings size={20} />
              {t("payrollSettings")}
            </h3>

            <div style={modalStyles.formGrid}>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>{t("paymentMethod")}</label>
                <ReusableDropdown
                  items={[
                    { id: "bank_transfer", label: t("bankTransfer") },
                    { id: "cash", label: t("cash") },
                    { id: "check", label: t("check") },
                    { id: "digital_wallet", label: t("digitalWallet") },
                  ]}
                  value={
                    [
                      { id: "bank_transfer", label: t("bankTransfer") },
                      { id: "cash", label: t("cash") },
                      { id: "check", label: t("check") },
                      { id: "digital_wallet", label: t("digitalWallet") },
                    ].find((m) => m.id === formData.paymentMethod)?.label || ""
                  }
                  onChange={(val) => {
                    const method = [
                      { id: "bank_transfer", label: t("bankTransfer") },
                      { id: "cash", label: t("cash") },
                      { id: "check", label: t("check") },
                      { id: "digital_wallet", label: t("digitalWallet") },
                    ].find((m) => m.label === val)?.id;
                    if (method) handleInputChange("paymentMethod", method);
                  }}
                  onSelect={(item) => {
                    handleInputChange("paymentMethod", item.id);
                  }}
                  placeholder={t("paymentMethod")}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  theme={theme}
                  language={language}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>

              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>{t("paymentDate")}</label>
                <input
                  type="date"
                  style={{
                    ...modalStyles.input,
                    ...unifiedControlStyle(theme, language === "ar"),
                    padding: "12px 16px",
                  }}
                  value={formData.paymentDate}
                  onChange={(e) =>
                    handleInputChange("paymentDate", e.target.value)
                  }
                />
              </div>
            </div>

            <div style={{ marginTop: "20px" }}>
              <h4 style={{ ...modalStyles.sectionTitle, fontSize: "16px" }}>
                {t("processingOptions")}
              </h4>
              <div style={modalStyles.formGrid}>
                <div style={modalStyles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="includeOvertime"
                    checked={formData.includeOvertime}
                    onChange={(e) =>
                      handleInputChange("includeOvertime", e.target.checked)
                    }
                  />
                  <label style={modalStyles.label} htmlFor="includeOvertime">
                    {t("includeOvertime")}
                  </label>
                </div>

                <div style={modalStyles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="includeBonuses"
                    checked={formData.includeBonuses}
                    onChange={(e) =>
                      handleInputChange("includeBonuses", e.target.checked)
                    }
                  />
                  <label style={modalStyles.label} htmlFor="includeBonuses">
                    {t("includeBonuses")}
                  </label>
                </div>

                <div style={modalStyles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="processTaxes"
                    checked={formData.processTaxes}
                    onChange={(e) =>
                      handleInputChange("processTaxes", e.target.checked)
                    }
                  />
                  <label style={modalStyles.label} htmlFor="processTaxes">
                    {t("processTaxes")}
                  </label>
                </div>

                <div style={modalStyles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="processInsurance"
                    checked={formData.processInsurance}
                    onChange={(e) =>
                      handleInputChange("processInsurance", e.target.checked)
                    }
                  />
                  <label style={modalStyles.label} htmlFor="processInsurance">
                    {t("processInsurance")}
                  </label>
                </div>
              </div>
            </div>

            <div style={modalStyles.formGroupWithMargin}>
              <label style={modalStyles.label}>{t("notes")}</label>
              <textarea
                style={{
                  ...modalStyles.input,
                  minHeight: "80px",
                  resize: "vertical",
                }}
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder={t("addNotesPayroll")}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div style={modalStyles.formSection}>
            <h3 style={modalStyles.sectionTitle}>
              <CheckCircle size={20} />
              {t("reviewProcess")}
            </h3>

            {previewData && (
              <>
                <div style={modalStyles.previewCard}>
                  <h4
                    style={{
                      marginBottom: "16px",
                      color: currentTheme.colors.textPrimary,
                    }}
                  >
                    {t("payrollSummary")}
                  </h4>

                  <div style={modalStyles.formGrid}>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: currentTheme.colors.textSecondary,
                        }}
                      >
                        {t("employees")}
                      </div>
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "800",
                          color: currentTheme.colors.primary,
                        }}
                      >
                        {previewData.totalEmployees}
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: currentTheme.colors.textSecondary,
                        }}
                      >
                        {t("grossTotal")}
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: currentTheme.colors.info,
                        }}
                      >
                        {previewData.totalGross.toLocaleString()}{" "}
                        {t("currency")} {/* هتطبع جنيه هنا */}
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: currentTheme.colors.textSecondary,
                        }}
                      >
                        {t("deductions")}
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: currentTheme.colors.danger,
                        }}
                      >
                        {previewData.totalDeductions.toLocaleString()}{" "}
                        {t("currency")}
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: currentTheme.colors.textSecondary,
                        }}
                      >
                        {t("taxInsurance")}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: currentTheme.colors.warning,
                        }}
                      >
                        {(
                          previewData.taxTotal + previewData.insuranceTotal
                        ).toLocaleString()}{" "}
                        {t("currency")}
                      </div>
                    </div>
                  </div>

                  <div style={modalStyles.previewTotal}>
                    {t("netTotal")}: {previewData.totalNet.toLocaleString()}{" "}
                    {t("currency")}
                  </div>
                </div>

                <div style={modalStyles.previewCard}>
                  <h4
                    style={{
                      marginBottom: "12px",
                      color: currentTheme.colors.textPrimary,
                    }}
                  >
                    {t("employeeBreakdown")}
                  </h4>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {previewData.employees.map((emp, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 0",
                          borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
                        }}
                      >
                        <span
                          style={{ color: currentTheme.colors.textPrimary }}
                        >
                          {emp.name}
                        </span>
                        <div style={{ display: "flex", gap: "20px" }}>
                          <span
                            style={{
                              color: currentTheme.colors.info,
                              fontWeight: "600",
                            }}
                          >
                            {emp.gross.toLocaleString()} {t("currency")}
                          </span>
                          <span
                            style={{
                              color: currentTheme.colors.danger,
                              fontWeight: "600",
                            }}
                          >
                            -{emp.deductions.toLocaleString()} {t("currency")}
                          </span>
                          <span
                            style={{
                              color: currentTheme.colors.success,
                              fontWeight: "700",
                            }}
                          >
                            {emp.net.toLocaleString()} {t("currency")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div
              style={{
                background: `${currentTheme.colors.warning}10`,
                padding: "16px",
                borderRadius: "8px",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: currentTheme.colors.warning,
                }}
              >
                <AlertCircle size={16} />
                <strong>{t("important")}:</strong>{" "}
                {t("processPayrollWarning").replace(
                  "{count}",
                  selectedEmployees.length,
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleNext = () => {
    if (processStep === 4) {
      handleProcessPayroll();
    } else {
      setProcessStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setProcessStep((prev) => prev - 1);
  };

  const canProceed = () => {
    switch (processStep) {
      case 1:
        return formData.payrollPeriod.month && formData.payrollPeriod.year;
      case 2:
        return selectedEmployees.length > 0;
      case 3:
        return true;
      case 4:
        return previewData && selectedEmployees.length > 0;
      default:
        return false;
    }
  };

  return (
    <div
      style={modalStyles.modal}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div style={modalStyles.modalContent}>
        <div style={modalStyles.header}>
          <h1 style={modalStyles.title}>
            <CreditCard size={32} />
            {t("processNewPayroll")}
          </h1>
          {/* <button
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                border: "2px solid rgba(239, 68, 68, 0.3)",
                borderRadius: "12px",
                padding: "12px",
                cursor: "pointer",
                color: "#ef4444",
                transition: "all 0.3s ease",
              }}
              onClick={onClose}
              disabled={processingPayroll}
            >
              <X size={24} />
            </button> */}
        </div>

        <div style={modalStyles.stepIndicator}>
          {steps.map((step) => (
            <div
              key={step.number}
              style={{
                ...modalStyles.step,
                ...(processStep >= step.number ? modalStyles.activeStep : {}),
              }}
            >
              <div
                style={{
                  ...modalStyles.stepNumber,
                  ...(processStep >= step.number
                    ? modalStyles.activeStepNumber
                    : {}),
                }}
              >
                {step.number}
              </div>
              {/* <step size={16} /> */}
              {step.title}
            </div>
          ))}
        </div>

        {renderStepContent()}

        <div style={modalStyles.actionButtons}>
          {processStep > 1 && (
            <Mo3tiButton
              variant="secondary"
              onClick={handleBack}
              disabled={processingPayroll}
            >
              {t("back")}
            </Mo3tiButton>
          )}

          <Mo3tiButton
            variant="primary"
            onClick={handleNext}
            disabled={!canProceed() || processingPayroll}
            icon={
              processingPayroll
                ? Activity
                : processStep === 4
                  ? CheckCircle
                  : TrendingUp
            }
          >
            {processingPayroll
              ? t("processing")
              : processStep === 4
                ? t("processPayrollBtn")
                : t("nextStep")}
          </Mo3tiButton>
        </div>

        {processingPayroll && processingStatus && (
          <div
            style={{
              marginTop: "10px",
              color: currentTheme.colors.textSecondary,
              fontSize: "13px",
              textAlign: "right",
            }}
          >
            {processingStatus}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Medical Insurance Modal ──────────────────────────────────────────────────
const MedicalInsuranceModal = ({
  onClose,
  API_BASE_URL,
  clientName,
  workingCompany,
  theme,
  language,
}) => {
  const { currentTheme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const isArabic = language === "ar";
  const t = (key) =>
    translations[language]?.[key] || translations["en"]?.[key] || key;
  const empName = (emp) =>
    emp
      ? isArabic
        ? emp.arabicName || emp.name
        : emp.name || emp.arabicName
      : "";
  const overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  };
  const card = {
    background: isDark ? "#1e293b" : "#fff",
    borderRadius: "1rem",
    width: "100%",
    maxWidth: "860px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 25px 60px rgba(0,0,0,0.35)",
    overflow: "hidden",
  };
  const header = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "1.25rem 1.5rem",
    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
    background: isDark ? "#0f172a" : "#f8fafc",
    flexShrink: 0,
  };
  const body = { flex: 1, overflowY: "auto", padding: "1.5rem" };
  const inputStyle = {
    width: "100%",
    padding: "0.6rem 0.9rem",
    borderRadius: "0.5rem",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
    background: isDark ? "#1e293b" : "#fff",
    color: isDark ? "#fff" : "#1f2937",
    fontSize: "0.9rem",
    outline: "none",
  };
  const btnPrimary = {
    padding: "0.55rem 1.1rem",
    borderRadius: "0.5rem",
    background: "#2e7cba",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.875rem",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
  };
  const btnSecondary = {
    padding: "0.55rem 1.1rem",
    borderRadius: "0.5rem",
    background: "transparent",
    color: isDark ? "#94a3b8" : "#6b7280",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.875rem",
  };
  const btnDanger = {
    padding: "0.35rem 0.7rem",
    borderRadius: "0.4rem",
    background: "transparent",
    color: "#ef4444",
    border: "1px solid #ef444430",
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: 600,
  };
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.875rem",
  };
  const thStyle = {
    padding: "0.6rem 0.75rem",
    textAlign: "left",
    background: isDark ? "#0f172a" : "#f1f5f9",
    color: isDark ? "#94a3b8" : "#374151",
    fontWeight: 700,
    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
  };
  const tdStyle = {
    padding: "0.6rem 0.75rem",
    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6"}`,
    color: isDark ? "#e2e8f0" : "#374151",
  };
  const labelStyle = {
    display: "block",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: isDark ? "#94a3b8" : "#374151",
    marginBottom: "0.3rem",
  };

  const [step, setStep] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(true);
  const [empSearch, setEmpSearch] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [members, setMembers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newMember, setNewMember] = useState({
    name: "",
    relationship: "",
    tierId: "",
  });
  const [addingMember, setAddingMember] = useState(false);
  const [savingMember, setSavingMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null); // { id, tierId }
  const [savingEdit, setSavingEdit] = useState(false);
  const [memberError, setMemberError] = useState("");

  // Tiers management
  const [showTiersPanel, setShowTiersPanel] = useState(false);
  const [newTier, setNewTier] = useState({
    tierName: "",
    monthlyPrice: "",
    description: "",
  });
  const [savingTier, setSavingTier] = useState(false);
  const [tierError, setTierError] = useState("");

  const getAuthHeaders = () => {
    const candidates = [
      localStorage.getItem("token"),
      sessionStorage.getItem("token"),
      localStorage.getItem("accessToken"),
      sessionStorage.getItem("accessToken"),
    ];
    const token = candidates.find(Boolean);
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  };

  const fetchEmployees = async () => {
    setLoadingEmps(true);
    try {
      const r = await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/employees?clientName=${encodeURIComponent(clientName)}&viewType=admin`,
      );
      const d = await r.json();
      setEmployees(d.employees || []);
    } catch (e) {
      setEmployees([]);
    } finally {
      setLoadingEmps(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const params = new URLSearchParams({ clientName });
      if (workingCompany) params.set("workingCompany", workingCompany);
      const r = await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/tiers?${params}`,
      );
      const d = await r.json();
      setTiers(d.tiers || []);
    } catch (e) {
      setTiers([]);
    }
  };

  const fetchMembers = async (empId) => {
    setLoadingMembers(true);
    try {
      const r = await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/employees/${encodeURIComponent(empId)}/members`,
      );
      const d = await r.json();
      setMembers(d.members || []);
    } catch (e) {
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchTiers();
  }, []);

  const handleSelectEmployee = (emp) => {
    setSelectedEmp(emp);
    setStep("members");
    fetchMembers(emp.infinityCode);
    setMemberError("");
    setAddingMember(false);
    setNewMember({ name: "", relationship: "", tierId: tiers[0]?.id || "" });
  };

  const handleBack = () => {
    setStep("employees");
    setSelectedEmp(null);
    setMembers([]);
  };

  const handleAddMember = async () => {
    if (
      !newMember.name.trim() ||
      !newMember.relationship.trim() ||
      !newMember.tierId
    ) {
      setMemberError(t("miFillAllFields"));
      return;
    }
    setSavingMember(true);
    setMemberError("");
    try {
      const r = await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/employees/${encodeURIComponent(selectedEmp.infinityCode)}/members`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: newMember.name.trim(),
            relationship: newMember.relationship.trim(),
            tierId: parseInt(newMember.tierId),
          }),
        },
      );
      if (!r.ok) {
        const e = await r.json();
        setMemberError(e.error || t("miFailedToAdd"));
        return;
      }
      await fetchMembers(selectedEmp.infinityCode);
      await fetchEmployees();
      setNewMember({ name: "", relationship: "", tierId: tiers[0]?.id || "" });
      setAddingMember(false);
    } catch (e) {
      setMemberError(t("miNetworkError"));
    } finally {
      setSavingMember(false);
    }
  };

  const handleRemoveMember = async (id) => {
    try {
      await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/members/${id}`,
        { method: "DELETE", headers: getAuthHeaders() },
      );
      await fetchMembers(selectedEmp.infinityCode);
      await fetchEmployees();
    } catch (e) {}
  };

  const handleToggleMemberActive = async (id, currentActive) => {
    try {
      await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/members/${id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ isActive: !currentActive }),
        },
      );
      await fetchMembers(selectedEmp.infinityCode);
      await fetchEmployees();
    } catch (e) {}
  };

  const handleEditTier = async (memberId, newTierId) => {
    setEditingMember({ id: memberId, tierId: newTierId });
    setSavingEdit(true);
    try {
      await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/members/${memberId}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ tierId: parseInt(newTierId) }),
        },
      );
      await fetchMembers(selectedEmp.infinityCode);
      await fetchEmployees();
    } catch (e) {
    } finally {
      setSavingEdit(false);
      setEditingMember(null);
    }
  };

  const handleAddTier = async () => {
    if (!newTier.tierName.trim() || !newTier.monthlyPrice) {
      setTierError(t("miTierNamePriceRequired"));
      return;
    }
    setSavingTier(true);
    setTierError("");
    try {
      const r = await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/tiers`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            clientName,
            workingCompany: workingCompany || undefined,
            tierName: newTier.tierName.trim(),
            monthlyPrice: parseFloat(newTier.monthlyPrice),
            description: newTier.description.trim() || undefined,
          }),
        },
      );
      if (!r.ok) {
        const e = await r.json();
        setTierError(e.error || t("miNetworkError"));
        return;
      }
      await fetchTiers();
      setNewTier({ tierName: "", monthlyPrice: "", description: "" });
    } catch (e) {
      setTierError(t("miNetworkError"));
    } finally {
      setSavingTier(false);
    }
  };

  const handleDeleteTier = async (id) => {
    try {
      await apiFetch(`${API_BASE_URL}/payrollX/medical-insurance/tiers/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      await fetchTiers();
    } catch (e) {}
  };

  const totalMonthly = members.reduce(
    (s, m) => s + (m.isActive ? m.tier?.monthlyPrice || 0 : 0),
    0,
  );
  const filteredEmps = employees.filter((e) => {
    const q = empSearch.toLowerCase();
    return (
      !q ||
      empName(e).toLowerCase().includes(q) ||
      (e.name || "").toLowerCase().includes(q) ||
      (e.arabicName || "").toLowerCase().includes(q) ||
      (e.infinityCode || "").toLowerCase().includes(q)
    );
  });

  return (
    <div
      style={overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={card}>
        {/* Header */}
        <div style={header}>
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            {step === "members" && (
              <button
                onClick={handleBack}
                style={{ ...btnSecondary, padding: "0.4rem 0.7rem" }}
              >
                {t("miBack")}
              </button>
            )}
            <Heart size={22} color="#ef4444" />
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: isDark ? "#f1f5f9" : "#1e293b",
                }}
              >
                {step === "employees"
                  ? t("miManagement")
                  : `${empName(selectedEmp)} — ${t("miFamilyMembers")}`}
              </h2>
              {step === "members" && selectedEmp && (
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: isDark ? "#94a3b8" : "#6b7280",
                  }}
                >
                  {selectedEmp.department || ""}
                  {selectedEmp.workingCompany
                    ? ` · ${selectedEmp.workingCompany}`
                    : ""}
                </p>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {step === "employees" && (
              <button
                onClick={() => setShowTiersPanel(!showTiersPanel)}
                style={{ ...btnSecondary, fontSize: "0.8rem" }}
              >
                <Settings
                  size={14}
                  style={{ display: "inline", marginRight: 4 }}
                />
                {t("miManageTiers")}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: isDark ? "#94a3b8" : "#6b7280",
                padding: "0.25rem",
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={body}>
          {/* Tiers panel */}
          {showTiersPanel && step === "employees" && (
            <div
              style={{
                marginBottom: "1.5rem",
                padding: "1rem",
                borderRadius: "0.75rem",
                background: isDark ? "#0f172a" : "#f8fafc",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
              }}
            >
              <h4
                style={{
                  margin: "0 0 1rem 0",
                  fontWeight: 700,
                  color: isDark ? "#f1f5f9" : "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Shield size={16} /> {t("miInsuranceTiers")}
              </h4>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t("miTierName")}</th>
                    <th style={thStyle}>{t("miMonthlyPriceEgp")}</th>
                    <th style={thStyle}>{t("description")}</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((tier) => (
                    <tr key={tier.id}>
                      <td style={tdStyle}>{tier.tierName}</td>
                      <td style={tdStyle}>
                        {tier.monthlyPrice.toLocaleString()}
                      </td>
                      <td style={tdStyle}>{tier.description || "—"}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => handleDeleteTier(tier.id)}
                          style={btnDanger}
                        >
                          {t("miRemove")}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {tiers.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          ...tdStyle,
                          textAlign: "center",
                          color: isDark ? "#64748b" : "#9ca3af",
                        }}
                      >
                        {t("miNoTiers")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Add tier form */}
              <div
                style={{
                  marginTop: "1rem",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 2fr auto",
                  gap: "0.75rem",
                  alignItems: "end",
                }}
              >
                <div>
                  <label style={labelStyle}>{t("miTierName")}</label>
                  <input
                    style={inputStyle}
                    placeholder={t("miTierNamePlaceholder")}
                    value={newTier.tierName}
                    onChange={(e) =>
                      setNewTier((p) => ({ ...p, tierName: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t("miMonthlyPriceEgp")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    placeholder="0"
                    value={newTier.monthlyPrice}
                    onChange={(e) =>
                      setNewTier((p) => ({
                        ...p,
                        monthlyPrice: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t("miDescriptionOptional")}</label>
                  <input
                    style={inputStyle}
                    placeholder={t("miDescriptionPlaceholder")}
                    value={newTier.description}
                    onChange={(e) =>
                      setNewTier((p) => ({ ...p, description: e.target.value }))
                    }
                  />
                </div>
                <button
                  onClick={handleAddTier}
                  disabled={savingTier}
                  style={btnPrimary}
                >
                  {savingTier ? t("miSaving") : t("miAddTier")}
                </button>
              </div>
              {tierError && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "0.8rem",
                    marginTop: "0.5rem",
                  }}
                >
                  {tierError}
                </p>
              )}
            </div>
          )}

          {/* STEP 1 — Employee list */}
          {step === "employees" && (
            <>
              <div style={{ marginBottom: "1rem" }}>
                <input
                  style={{ ...inputStyle, paddingLeft: "0.9rem" }}
                  placeholder={t("miSearchPlaceholder")}
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                />
              </div>
              {loadingEmps ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: isDark ? "#64748b" : "#9ca3af",
                  }}
                >
                  {t("miLoadingEmployees")}
                </div>
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t("employee")}</th>
                      <th style={thStyle}>{t("employeeId")}</th>
                      <th style={thStyle}>{t("department")}</th>
                      <th style={thStyle}>{t("miMembers")}</th>
                      <th style={thStyle}>{t("miMonthlyCost")}</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmps.map((emp) => (
                      <tr
                        key={emp.infinityCode}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSelectEmployee(emp)}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = isDark
                            ? "rgba(255,255,255,0.04)"
                            : "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td style={tdStyle}>
                          <span style={{ fontWeight: 600 }}>
                            {empName(emp)}
                          </span>
                        </td>
                        <td style={tdStyle}>{emp.infinityCode}</td>
                        <td style={tdStyle}>
                          {emp.department || emp.workingCompany || "—"}
                        </td>
                        <td style={tdStyle}>
                          <span
                            style={{
                              fontWeight: 700,
                              color:
                                emp.familyMemberCount > 0
                                  ? "#2e7cba"
                                  : isDark
                                    ? "#64748b"
                                    : "#9ca3af",
                            }}
                          >
                            {emp.familyMemberCount}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {emp.totalMonthlyInsurance > 0 ? (
                            <span style={{ fontWeight: 700, color: "#10b981" }}>
                              {emp.totalMonthlyInsurance.toLocaleString()} EGP
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={tdStyle}>
                          <button
                            style={btnPrimary}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectEmployee(emp);
                            }}
                          >
                            {t("miManage")}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredEmps.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            ...tdStyle,
                            textAlign: "center",
                            padding: "2rem",
                            color: isDark ? "#64748b" : "#9ca3af",
                          }}
                        >
                          {t("miNoEmployees")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* STEP 2 — Family members */}
          {step === "members" && (
            <>
              {/* Summary bar */}
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  marginBottom: "1.25rem",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    padding: "0.75rem 1.25rem",
                    borderRadius: "0.75rem",
                    background: isDark ? "rgba(46,124,186,0.15)" : "#eff6ff",
                    border: "1px solid #2e7cba30",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#93c5fd" : "#1d4ed8",
                      fontWeight: 600,
                    }}
                  >
                    {t("miMembers")}:{" "}
                  </span>
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: 800,
                      color: isDark ? "#fff" : "#1e293b",
                    }}
                  >
                    {members.filter((m) => m.isActive).length}
                  </span>
                </div>
                <div
                  style={{
                    padding: "0.75rem 1.25rem",
                    borderRadius: "0.75rem",
                    background: isDark ? "rgba(16,185,129,0.15)" : "#f0fdf4",
                    border: "1px solid #10b98130",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#6ee7b7" : "#065f46",
                      fontWeight: 600,
                    }}
                  >
                    {t("miTotalMonthly")}:{" "}
                  </span>
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: 800,
                      color: "#10b981",
                    }}
                  >
                    {totalMonthly.toLocaleString()} EGP
                  </span>
                </div>
              </div>

              {loadingMembers ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: isDark ? "#64748b" : "#9ca3af",
                  }}
                >
                  {t("loading")}
                </div>
              ) : (
                <>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t("fullName")}</th>
                        <th style={thStyle}>{t("miRelationship")}</th>
                        <th style={thStyle}>{t("miInsuranceTier")}</th>
                        <th style={thStyle}>{t("miMonthlyCost")}</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr
                          key={m.id}
                          style={{ opacity: m.isActive ? 1 : 0.5 }}
                        >
                          <td style={{ ...tdStyle, fontWeight: 600 }}>
                            {m.name}
                            {!m.isActive && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: "0.7rem",
                                  padding: "0.15rem 0.45rem",
                                  borderRadius: "0.4rem",
                                  background: isDark
                                    ? "rgba(239,68,68,0.15)"
                                    : "#fee2e2",
                                  color: "#ef4444",
                                  fontWeight: 600,
                                }}
                              >
                                {t("miInactive")}
                              </span>
                            )}
                          </td>
                          <td style={tdStyle}>{m.relationship}</td>
                          <td style={tdStyle}>
                            <select
                              value={
                                editingMember?.id === m.id
                                  ? editingMember.tierId
                                  : m.tierId
                              }
                              disabled={
                                (savingEdit && editingMember?.id === m.id) ||
                                !m.isActive
                              }
                              onChange={(e) =>
                                handleEditTier(m.id, e.target.value)
                              }
                              style={{
                                ...inputStyle,
                                width: "auto",
                                minWidth: "160px",
                                padding: "0.35rem 0.6rem",
                              }}
                            >
                              {tiers.map((tier) => (
                                <option key={tier.id} value={tier.id}>
                                  {tier.tierName} —{" "}
                                  {tier.monthlyPrice.toLocaleString()} EGP
                                </option>
                              ))}
                            </select>
                            {savingEdit && editingMember?.id === m.id && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: "0.75rem",
                                  color: "#2e7cba",
                                }}
                              >
                                {t("miSaving")}
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              color: m.isActive
                                ? "#10b981"
                                : isDark
                                  ? "#64748b"
                                  : "#9ca3af",
                              fontWeight: 700,
                            }}
                          >
                            {m.isActive
                              ? (m.tier?.monthlyPrice || 0).toLocaleString() +
                                " EGP"
                              : "—"}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              display: "flex",
                              gap: "0.4rem",
                              flexWrap: "nowrap",
                            }}
                          >
                            <button
                              onClick={() =>
                                handleToggleMemberActive(m.id, m.isActive)
                              }
                              style={
                                m.isActive
                                  ? btnDanger
                                  : {
                                      ...btnPrimary,
                                      background: "#10b981",
                                      borderColor: "#10b981",
                                    }
                              }
                            >
                              {m.isActive ? t("miDeactivate") : t("miActivate")}
                            </button>
                            <button
                              onClick={() => handleRemoveMember(m.id)}
                              style={{
                                ...btnDanger,
                                background: "#991b1b",
                                borderColor: "#991b1b",
                              }}
                            >
                              {t("miDelete")}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {members.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              ...tdStyle,
                              textAlign: "center",
                              padding: "2rem",
                              color: isDark ? "#64748b" : "#9ca3af",
                            }}
                          >
                            {t("miNoFamilyMembers")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Add member form */}
                  <div
                    style={{
                      marginTop: "1.25rem",
                      padding: "1rem",
                      borderRadius: "0.75rem",
                      background: isDark ? "#0f172a" : "#f8fafc",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
                    }}
                  >
                    {!addingMember ? (
                      <button
                        onClick={() => {
                          setAddingMember(true);
                          setNewMember({
                            name: "",
                            relationship: "",
                            tierId: tiers[0]?.id || "",
                          });
                          setMemberError("");
                        }}
                        style={btnPrimary}
                      >
                        <Plus size={16} /> {t("miAddFamilyMember")}
                      </button>
                    ) : (
                      <>
                        <h4
                          style={{
                            margin: "0 0 0.75rem 0",
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                            fontSize: "0.9rem",
                          }}
                        >
                          {t("miNewFamilyMember")}
                        </h4>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr auto auto",
                            gap: "0.75rem",
                            alignItems: "end",
                          }}
                        >
                          <div>
                            <label style={labelStyle}>{t("fullName")}</label>
                            <input
                              style={inputStyle}
                              placeholder={t("miFullNamePlaceholder")}
                              value={newMember.name}
                              onChange={(e) =>
                                setNewMember((p) => ({
                                  ...p,
                                  name: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>
                              {t("miRelationship")}
                            </label>
                            <input
                              style={inputStyle}
                              placeholder={t("miRelationshipPlaceholder")}
                              value={newMember.relationship}
                              onChange={(e) =>
                                setNewMember((p) => ({
                                  ...p,
                                  relationship: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>
                              {t("miInsuranceTier")}
                            </label>
                            <select
                              style={inputStyle}
                              value={newMember.tierId}
                              onChange={(e) =>
                                setNewMember((p) => ({
                                  ...p,
                                  tierId: e.target.value,
                                }))
                              }
                            >
                              <option value="">{t("miSelectTier")}</option>
                              {tiers.map((tier) => (
                                <option key={tier.id} value={tier.id}>
                                  {tier.tierName} —{" "}
                                  {tier.monthlyPrice.toLocaleString()} EGP
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            onClick={handleAddMember}
                            disabled={savingMember}
                            style={{ ...btnPrimary, alignSelf: "flex-end" }}
                          >
                            {savingMember ? t("miSaving") : t("save")}
                          </button>
                          <button
                            onClick={() => {
                              setAddingMember(false);
                              setMemberError("");
                            }}
                            style={{ ...btnSecondary, alignSelf: "flex-end" }}
                          >
                            {t("cancel")}
                          </button>
                        </div>
                        {memberError && (
                          <p
                            style={{
                              color: "#ef4444",
                              fontSize: "0.8rem",
                              marginTop: "0.5rem",
                              marginBottom: 0,
                            }}
                          >
                            {memberError}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ProfessionalPayrollSystem = () => {
  // Fetch visibility rules on mount to ensure payrolls are created for all visible employees
  useEffect(() => {
    fetchVisibilityRules();
  }, []);
  //const { theme } = useContext(ThemeContext);
  // ✅ أضف الـ Hover Handlers هنا - بعد const { theme }
  // const handlePrimaryBtnMouseEnter = (e) => {
  //   e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
  //   e.currentTarget.style.boxShadow = "0 6px 20px 0 rgba(37, 99, 235, 0.5)";
  // };

  // const handlePrimaryBtnMouseLeave = (e) => {
  //   e.currentTarget.style.transform = "translateY(0) scale(1)";
  //   e.currentTarget.style.boxShadow = "0 4px 14px 0 rgba(37, 99, 235, 0.39)";
  // };

  // const handleSecondaryBtnMouseEnter = (e) => {
  //   e.currentTarget.style.background = theme === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.08)";
  //   e.currentTarget.style.transform = "translateY(-2px)";
  // };

  // const handleSecondaryBtnMouseLeave = (e) => {
  //   e.currentTarget.style.background = theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)";
  //   e.currentTarget.style.transform = "translateY(0)";
  // };
  // Get theme context
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const t = (key) => translations[language][key] || key;
  const isRTL = language === "ar";
  const [visibleItems, setVisibleItems] = useState(10);
  const itemsPerPage = 10;
  const currentTheme = {
    colors: {
      primary: theme === "dark" ? "#2e7cba" : "#2e7cba",
      success: "#10b981",
      danger: "#ef4444",
      warning: "#f59e0b",
      info: "#2e7cba",
      textPrimary: theme === "dark" ? "#ffffff" : "#1a1a1a",
      textSecondary:
        theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
      textMuted:
        theme === "dark" ? "rgba(255, 255, 255, 1)" : "rgba(0,0,0,0.5)",
      cardBackground:
        theme === "dark" ? "rgba(30, 41, 59, 1.6)" : "rgba(255, 255, 255, 0.9)",
      inputBackground:
        theme === "dark"
          ? "rgba(30, 41, 59, 2.8)"
          : "rgba(255, 255, 255, 0.95)",
      border: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      borderLight:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
    },
    shadows: {
      card:
        theme === "dark"
          ? "0 8px 32px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(0,0,0,0.1)",
    },
  };
  const [screenSize, setScreenSize] = useState(getScreenSize());
  const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
  const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));
  const [container, setContainer] = useState(
    getResponsiveContainer(screenSize),
  );
  const [gridCols, setGridCols] = useState(getResponsiveGrid(screenSize));

  // AlertModal state
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
  });

  // Helper function to show alerts
  const showAlert = (title, message, variant = "info", showCancel = false) => {
    setAlertModal({
      open: true,
      title,
      message,
      variant,
      showCancel,
    });
  };

  const closeAlert = () => {
    setAlertModal({ ...alertModal, open: false });
  };

  // ✅ Hover Handlers - مكان واحد بس
  const handlePrimaryBtnMouseEnter = (e) => {
    e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
    e.currentTarget.style.boxShadow = "0 6px 20px 0 rgba(37, 99, 235, 0.5)";
  };

  const handlePrimaryBtnMouseLeave = (e) => {
    e.currentTarget.style.transform = "translateY(0) scale(1)";
    e.currentTarget.style.boxShadow = "0 4px 14px 0 rgba(37, 99, 235, 0.39)";
  };

  const handleSecondaryBtnMouseEnter = (e) => {
    e.currentTarget.style.background =
      theme === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.08)";
    e.currentTarget.style.transform = "translateY(-2px)";
  };

  const handleSecondaryBtnMouseLeave = (e) => {
    e.currentTarget.style.background =
      theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)";
    e.currentTarget.style.transform = "translateY(0)";
  };
  // NOTE: moved modal body-scroll locking effect down below (after modal state declarations)

  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = getScreenSize();
      setScreenSize(newScreenSize);
      setSpacing(getResponsiveSpacing(newScreenSize));
      setFonts(getResponsiveFontSizes(newScreenSize));
      setContainer(getResponsiveContainer(newScreenSize));
      setGridCols(getResponsiveGrid(newScreenSize));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // const { theme } = useContext(ThemeContext);

  // State management
  const [employeeCount, setEmployeeCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  // Use an object for selectedMonth to match modal output
  const [selectedMonth, setSelectedMonth] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedCompany, setSelectedCompany] = useState("All");
  // local editable values for dropdowns so typing shows suggestions and X clears
  const [statusSearch, setStatusSearch] = useState(
    statusFilter === "All"
      ? t("allStatuses")
      : statusFilter === "paid"
        ? t("paid")
        : statusFilter === "pending"
          ? t("pending")
          : statusFilter === "approved"
            ? t("approved")
            : t("rejected"),
  );
  const [departmentSearch, setDepartmentSearch] = useState(
    selectedDepartment === "All" ? t("allDepartments") : selectedDepartment,
  );
  const [companySearch, setCompanySearch] = useState(
    selectedCompany === "All" ? t("allCompanies") : selectedCompany,
  );
  const [yearSearch, setYearSearch] = useState(
    (selectedMonth.year || new Date().getFullYear()).toString(),
  );
  const [monthSearch, setMonthSearch] = useState(
    t(
      [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
      ][(selectedMonth.month || new Date().getMonth() + 1) - 1],
    ),
  );
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [processStep, setProcessStep] = useState(1);
  // const { language } = useContext(LanguageContext);
  // const t = (key) => translations[language][key] || key;
  // const isRTL = language === 'ar';
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [importSettings, setImportSettings] = useState({
    updateExisting: false,
    skipErrors: true,
    calculateMissing: true,
    defaultYear: new Date().getFullYear(),
    defaultMonth: new Date().getMonth() + 1,
  });

  // Benefits info modal
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);

  // Medical Insurance modal
  const [showMedicalInsuranceModal, setShowMedicalInsuranceModal] =
    useState(false);

  // Visibility Rules State (UI removed; keep data state used for filtering)
  const [visibilityRules, setVisibilityRules] = useState([]);
  const [loadingVisibility, setLoadingVisibility] = useState(false);
  const [visibilityError, setVisibilityError] = useState(null);

  // Egyptian Tax System Configuration (2024)
  const taxBrackets = [
    { min: 0, max: 21000, rate: 0.0, description: "Tax-free threshold" },
    { min: 21001, max: 45000, rate: 0.1, description: "Low income bracket" },
    {
      min: 45001,
      max: 60000,
      rate: 0.15,
      description: "Middle income bracket",
    },
    {
      min: 60001,
      max: 200000,
      rate: 0.2,
      description: "Upper-middle income bracket",
    },
    {
      min: 200001,
      max: 400000,
      rate: 0.225,
      description: "High income bracket",
    },
    {
      min: 400001,
      max: Infinity,
      rate: 0.25,
      description: "Very high income bracket",
    },
  ];

  // Calculate Egyptian income tax
  const calculateEgyptianTax = (grossSalary) => {
    const annualGross = grossSalary * 12;
    let totalTax = 0;
    let remainingIncome = Math.max(0, annualGross - 21000);

    for (const bracket of taxBrackets.slice(1)) {
      if (remainingIncome <= 0) break;

      const bracketSize =
        bracket.max === Infinity
          ? remainingIncome
          : Math.min(remainingIncome, bracket.max - bracket.min + 1);
      totalTax += bracketSize * bracket.rate;
      remainingIncome -= bracketSize;
    }

    return {
      annualTax: totalTax,
      monthlyTax: totalTax / 12,
      effectiveRate: (totalTax / annualGross) * 100,
      taxableIncome: annualGross,
    };
  };

  // Calculate social insurance
  const calculateSocialInsurance = (grossSalary) => {
    const employeeRate = 0.11;
    const employerRate = 0.1875;

    return {
      employeeContribution: grossSalary * employeeRate,
      employerContribution: grossSalary * employerRate,
      totalContribution: grossSalary * (employeeRate + employerRate),
    };
  };

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const clientName = profile?.clientName || localStorage.getItem("clientName");
  const workingCompany = profile?.workingCompany || localStorage.getItem("workingCompany");

  // ✅ أضف هنا - دالة للحصول على headers مع token
  const getToken = () => {
    // Try common storage locations and token key names
    const candidates = [
      localStorage.getItem("token"),
      sessionStorage.getItem("token"),
      localStorage.getItem("accessToken"),
      sessionStorage.getItem("accessToken"),
    ];

    for (let t of candidates) {
      if (t) return t.startsWith("Bearer ") ? t.slice(7) : t;
    }

    // Try user object stored in storage
    const userStr =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        if (u.token)
          return u.token.startsWith("Bearer ") ? u.token.slice(7) : u.token;
        if (u.accessToken) return u.accessToken;
      } catch (e) {
        // ignore
      }
    }

    // Try cookie named `token`
    try {
      const m =
        document.cookie && document.cookie.match(/(?:^|; )token=([^;]+)/);
      if (m) return decodeURIComponent(m[1]);
    } catch (e) {}

    return null;
  };

  const decodeJwtPayload = (token) => {
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    try {
      // base64url -> base64
      let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const pad = payload.length % 4;
      if (pad) payload += "=".repeat(4 - pad);
      return JSON.parse(atob(payload));
    } catch (e) {
      return null;
    }
  };

  const getAuthHeaders = (isJson = true) => {
    const token = getToken();
    const headers = { Accept: "application/json" };
    if (isJson) headers["Content-Type"] = "application/json";
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  };

  // Permissions for payroll features (fetched from admin-level or company config)
  const [modulePermissions, setModulePermissions] = useState({
    payrollManagement: true,
    processPayroll: true,
    dataImport: true,
    dataExport: true,
  });
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoadingPermissions(true);
        const BASE_URL = API_BASE_URL || process.env.REACT_APP_BASE_URL || "";
        const headers = getAuthHeaders(false);

        // Try admin-level resolver first (requires Authorization header and workingCompany)
        try {
          const client =
            workingCompany || localStorage.getItem("workingCompany") || "";
          if (!client) {
            console.warn(
              "No workingCompany (clientName) found — skipping admin-level permissions fetch",
            );
          } else {
            const resp = await apiFetch(
              `${BASE_URL}/api/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(
                client,
              )}`,
              { method: "GET" },
            );
            if (resp.ok) {
              const json = await resp.json();
              const p = json.data ?? json.permissions ?? json;

              const normalize = (obj) => {
                const defaults = {
                  payrollManagement: true,
                  processPayroll: true,
                  dataImport: true,
                  dataExport: true,
                };
                if (!obj) return defaults;
                if (obj.isSuperAdmin || obj.allModulesAllowed) return defaults;

                const modules = Array.isArray(obj.modules) ? obj.modules : [];
                const features = obj.features || {};

                const moduleCandidates = [
                  "payroll",
                  "payrollManagement",
                  "payroll-management",
                  "payroll_management",
                  "PayrollManagement",
                ];
                let moduleKey = moduleCandidates.find((k) =>
                  modules.includes(k),
                );
                if (!moduleKey && features && typeof features === "object") {
                  moduleKey = Object.keys(features).find((k) =>
                    moduleCandidates.includes(k),
                  );
                }

                let payrollEnabled = false;
                let processEnabled = false;
                let importEnabled = false;
                let exportEnabled = false;

                const PROCESS_FEATURE_ALIASES = ["process", "processPayroll"];

                const IMPORT_FEATURE_ALIASES = [
                  "import",
                  "dataImport",
                  "importPayroll",
                  "importAttendance",
                  "importAttendanceDeduction",
                  "importAttendanceDeductions",
                  "importAllowancesDeductions",
                  "importAllowanceDeduction",
                ];

                const EXPORT_FEATURE_ALIASES = [
                  "export",
                  "dataExport",
                  "exportPayroll",
                ];

                const hasAnyFeatureFlag = (entry, aliases) => {
                  if (Array.isArray(entry)) {
                    return aliases.some((k) => entry.includes(k));
                  }
                  if (entry && typeof entry === "object") {
                    return aliases.some((k) => Boolean(entry[k]));
                  }
                  return false;
                };

                if (moduleKey) {
                  payrollEnabled = true;
                  const entry = features[moduleKey];
                  processEnabled = hasAnyFeatureFlag(
                    entry,
                    PROCESS_FEATURE_ALIASES,
                  );
                  importEnabled = hasAnyFeatureFlag(
                    entry,
                    IMPORT_FEATURE_ALIASES,
                  );
                  exportEnabled = hasAnyFeatureFlag(
                    entry,
                    EXPORT_FEATURE_ALIASES,
                  );

                  // Also accept attendance import permission for attendance-linked
                  // allowances/deductions import on payroll page.
                  if (!importEnabled) {
                    const attendanceEntry =
                      features.attendance ||
                      features.attendanceManagement ||
                      features["attendance-management"] ||
                      features.attendance_management;
                    importEnabled = hasAnyFeatureFlag(
                      attendanceEntry,
                      IMPORT_FEATURE_ALIASES,
                    );
                  }
                } else {
                  payrollEnabled = Boolean(
                    obj.payrollManagement ??
                    obj.payroll ??
                    modules.includes("payrollManagement"),
                  );
                  processEnabled = Boolean(
                    obj.processPayroll ?? obj.process ?? false,
                  );
                  importEnabled = Boolean(
                    obj.dataImport ??
                    obj.import ??
                    obj.importPayroll ??
                    obj.importAttendance ??
                    obj.importAttendanceDeduction ??
                    obj.importAllowancesDeductions ??
                    false,
                  );
                  exportEnabled = Boolean(
                    obj.dataExport ?? obj.export ?? false,
                  );
                }

                return {
                  payrollManagement: payrollEnabled,
                  processPayroll: processEnabled || payrollEnabled,
                  dataImport: importEnabled,
                  dataExport: exportEnabled,
                };
              };

              setModulePermissions(normalize(p));
              setLoadingPermissions(false);
              return;
            }
          }
        } catch (err) {
          console.warn(
            "admin-level permissions fetch failed, falling back to company config:",
            err,
          );
        }

        // fallback to company configuration
        try {
          const client = clientName || localStorage.getItem("clientName") || "";
          if (!client) {
            setModulePermissions({
              payrollManagement: true,
              processPayroll: true,
              dataImport: true,
              dataExport: true,
            });
            setLoadingPermissions(false);
            return;
          }
          const res2 = await apiFetch(
            `${BASE_URL}/api/configurations/${encodeURIComponent(client)}`,
            { method: "GET" },
          );
          if (res2.ok) {
            const json2 = await res2.json();
            setModulePermissions(
              json2.data || {
                payrollManagement: true,
                processPayroll: true,
                dataImport: true,
                dataExport: true,
              },
            );
            setLoadingPermissions(false);
            return;
          }
        } catch (err2) {
          console.error("Error fetching company configuration fallback:", err2);
        }

        setModulePermissions({
          payrollManagement: true,
          processPayroll: true,
          dataImport: true,
          dataExport: true,
        });
      } catch (err) {
        console.error("Unexpected error fetching permissions:", err);
        setModulePermissions({
          payrollManagement: true,
          processPayroll: true,
          dataImport: true,
          dataExport: true,
        });
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, []);

  // State for payroll data
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [togglingIgnoredIds, setTogglingIgnoredIds] = useState(new Set());
  // ==================== AUTO-CREATE PAYROLL ON LOAD ====================
  // ==================== AUTO-CREATE PAYROLL ON LOAD ====================
  useEffect(() => {
    // Wait until loading is DONE
    if (loading) return;

    const ensurePayrollForAll = async () => {
      const endYear = selectedMonth?.year || new Date().getFullYear();
      const endMonth = selectedMonth?.month || new Date().getMonth() + 1;

      // Use all visible employees, not just those in payrollData
      let codes = [];
      // Try to use visibilityRules if available
      if (Array.isArray(visibilityRules) && visibilityRules.length > 0) {
        codes = visibilityRules
          .map((r) => String(r.employeeInfinityCode || "").trim())
          .filter(Boolean);
      } else {
        // fallback: use payrollData as before
        codes = Array.from(
          new Set(
            payrollData
              .map((p) => String(p.infinityCode || "").trim())
              .filter(Boolean),
          ),
        );
      }

      if (!codes.length) return;

      console.log(
        `🔄 Ensuring payroll exists for ${codes.length} employees (payroll month ${endYear}/${endMonth})...`,
      );

      try {
        const response = await apiFetch(
          `${API_BASE_URL}/payrollX/ensure-exists`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              infinityCodes: codes,
              year: endYear,
              month: endMonth,
            }),
          },
        );

        const result = await response.json();
        const created = result?.data?.created ?? 0;

        if (created > 0) {
          console.log(
            `✅ Auto-created ${created} payroll record(s) — refreshing...`,
          );
          setRefreshTrigger((prev) => prev + 1);
        } else {
          console.log(`✅ All payroll records already exist`);
        }
      } catch (err) {
        console.warn("⚠️ ensurePayrollExists batch call failed:", err.message);
      }
    };

    ensurePayrollForAll();
  }, [loading, payrollData, selectedMonth]); // ✅ add `loading` to deps // Re-run when month changes too
  // Fetch payroll data from API
  useEffect(() => {
    const fetchPayrollData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("Starting payroll data fetch...");
        console.log("API Base URL:", API_BASE_URL);

        const params = new URLSearchParams();

        // Ensure selectedMonth is an object with month/year
        if (selectedMonth && typeof selectedMonth === "object") {
          params.append("year", selectedMonth.year || new Date().getFullYear());
          params.append(
            "month",
            selectedMonth.month || new Date().getMonth() + 1,
          );
        } else {
          params.append("year", new Date().getFullYear());
          params.append("month", new Date().getMonth() + 1);
        }

        if (statusFilter && statusFilter !== "All") {
          params.append("paymentStatus", statusFilter.toLowerCase());
        }

        // selectedDepartment is still passed through (used as single employee filter)
        if (
          selectedDepartment &&
          selectedDepartment !== "All" &&
          !isNaN(selectedDepartment)
        ) {
          params.append("infinityCode", selectedDepartment);
        }

        // Resolve current user and role
        let userDataStr =
          localStorage.getItem("user") || sessionStorage.getItem("user");
        let currentUser = null;
        try {
          currentUser = userDataStr ? JSON.parse(userDataStr) : null;
        } catch (e) {
          currentUser = null;
        }
        const currentRole =
          profile?.role ||
          currentUser?.role ||
          localStorage.getItem("role") ||
          sessionStorage.getItem("role");
        let currentAdminInfinityCode =
          currentUser?.infinityCode ||
          currentUser?.employeeInfinityCode ||
          currentUser?.id ||
          null;

        // Try token fallback if needed (robust)
        if (!currentAdminInfinityCode) {
          const token = getToken();
          if (token) {
            try {
              const payload = decodeJwtPayload(token);
              currentAdminInfinityCode =
                payload?.infinityCode ||
                payload?.infinity_code ||
                payload?.id ||
                payload?.employeeInfinityCode ||
                null;
            } catch (e) {
              // ignore
            }
          }
        }

        // Fetch visible employees list for non-superadmin/non-admin roles
        let visibleCodes = null; // null means "no filtering"
        if (
          currentRole &&
          currentRole !== "superadmin" &&
          currentRole !== "admin"
        ) {
          if (currentAdminInfinityCode) {
            try {
              const visResp = await apiFetch(
                `${API_BASE_URL}/payrollX/visibility/admin/${String(
                  currentAdminInfinityCode,
                )}/employees`,
                { method: "GET" },
              );
              if (visResp.ok) {
                const visBody = await visResp.json();
                visibleCodes = (visBody.data || []).map((e) =>
                  String(e.infinityCode),
                );
                console.log(
                  "Visible employee codes for current admin:",
                  visibleCodes,
                );
              } else {
                // fallback to rules endpoint
                const visRulesResp = await apiFetch(
                  `${API_BASE_URL}/payrollX/visibility/rules?adminInfinityCode=${String(
                    currentAdminInfinityCode,
                  )}`,
                  { method: "GET" },
                );
                if (visRulesResp.ok) {
                  const vr = await visRulesResp.json();
                  visibleCodes = (vr.data || []).map((r) =>
                    String(r.employeeInfinityCode),
                  );
                }
              }
            } catch (e) {
              console.warn(
                "Failed to fetch visibility list, proceeding with empty result (no access)",
                e,
              );
              // If visibility fetch fails for a manager, default to empty set to prevent leakage
              visibleCodes = [];
            }
          } else {
            // no admin code -> show nothing
            visibleCodes = [];
          }
        }

        const pageSize = 200;
        params.set("limit", String(pageSize));

        const buildPayrollUrl = (queryParams) => {
          if (clientName != "Infinity") {
            return `${API_BASE_URL}/payrollX/${clientName}/all?${queryParams.toString()}`;
          }
          return `${API_BASE_URL}/payrollX?${queryParams.toString()}`;
        };

        const fetchPayrollPage = async (pageNumber) => {
          const pageParams = new URLSearchParams(params);
          pageParams.set("page", String(pageNumber));

          const pageUrl = buildPayrollUrl(pageParams);
          console.log(`Fetching payroll page ${pageNumber}:`, pageUrl);

          const response = await apiFetch(pageUrl, { method: "GET" });

          if (!response.ok) {
            const errorText = await response.text();
            console.error("Error response:", errorText);
            throw new Error(
              `HTTP error! status: ${response.status} - ${errorText}`,
            );
          }

          return response.json();
        };

        const firstResult = await fetchPayrollPage(1);
        console.log("Received response (page 1):", firstResult);

        let data = Array.isArray(firstResult?.data)
          ? [...firstResult.data]
          : Array.isArray(firstResult)
            ? [...firstResult]
            : [];

        const totalPages = Number(firstResult?.pagination?.totalPages) || 1;

        for (let page = 2; page <= totalPages; page++) {
          const nextResult = await fetchPayrollPage(page);
          const nextData = Array.isArray(nextResult?.data)
            ? nextResult.data
            : Array.isArray(nextResult)
              ? nextResult
              : [];
          data = data.concat(nextData);
        }

        if (!Array.isArray(data)) {
          console.error("Expected array but got:", typeof data, data);
          throw new Error("Invalid response format from server");
        }

        console.log("Number of records before filtering:", data.length);

        // Transform payroll entries
        const transformedData = data.map((payroll) => {
          const employee = payroll.employee || {};

          const allowances = payroll.allowances || [];
          const overtimeAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "overtime",
          );
          const transportationAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "transportation",
          );
          const mealAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "meal",
          );
          const mobileAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "mobile",
          );
          const bonusAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "bonus",
          );
          const kpiAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "kpi_adjustment",
          );
          const expensesAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "expenses",
          );

          const deductions = payroll.deductions || [];
          const penaltyDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "penalty",
          );
          const advanceDeduction = deductions.find(
            (d) =>
              d.type?.toLowerCase() === "advancement" ||
              d.type?.toLowerCase() === "advance",
          );
          const insuranceDeduction = deductions.find(
            (d) =>
              d.type?.toLowerCase() === "insurance" ||
              d.type?.toLowerCase() === "social_insurance",
          );
          const absenceDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "absence",
          );
          const sickLeaveDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "sick_leave_25",
          );

          const allowancesTotal = allowances.reduce(
            (sum, a) => sum + (Number(a.amount) || 0),
            0,
          );
          const deductionsTotal = deductions.reduce(
            (sum, d) => sum + (Number(d.amount) || 0),
            0,
          );

          return {
            id: payroll.id,
            employeeName: (() => {
              const name =
                language === "ar" ? employee.arabicName : employee.sr;

              return typeof name === "string" && name.trim()
                ? name.trim().split(/\s+/).slice(0, 3).join(" ")
                : "N/A";
            })(),

            employeeId: `EMP${String(payroll.infinityCode).padStart(3, "0")}`,
            infinityCode: payroll.infinityCode,
            department: employee.employeeType,
            workingCompany: employee.workingCompany,
            position: employee.employeeType || "N/A",

            basicSalary: Number(payroll.basicSalary) || 0,

            bonus: Number(bonusAllowance?.amount || payroll.bonusValue) || 0,
            bonusAdjustments: Number(kpiAllowance?.amount) || 0,
            kpiScore: Number(payroll.achievement) || 0,

            overtimeHours:
              Number(overtimeAllowance?.hours || payroll.overtimeHours) || 0,
            overtimeTotal:
              Number(overtimeAllowance?.amount || payroll.overtimeValue) || 0,

            transportationAllowance:
              Number(transportationAllowance?.amount) || 0,
            mealAllowance: Number(mealAllowance?.amount) || 0,
            mobileAllowance: Number(mobileAllowance?.amount) || 0,
            expenses:
              Number(expensesAllowance?.amount || payroll.expenses) || 0,

            grossSalary: Number(payroll.grossSalary) || 0,
            netSalary: Number(payroll.netSalary) || 0,

            taxMonth: Number(payroll.monthlyTax) || 0,
            martyrsFund: Number(payroll.martyrsFund) || 0,

            socialInsurance:
              Number(insuranceDeduction?.amount) ||
              Number(employee.insuranceSalary) * 0.11 ||
              Number(payroll.basicSalary) * 0.11 ||
              0,
            penalty:
              Number(penaltyDeduction?.amount || payroll.penalityDaysValue) ||
              0,
            advancePayment: Number(advanceDeduction?.amount) || 0,
            absenceDeduction:
              Number(absenceDeduction?.amount || payroll.absenceDaysValue) || 0,
            sickLeaveDeduction:
              Number(
                sickLeaveDeduction?.amount || payroll.sickLeave25DeductionValue,
              ) || 0,
            totalDeductions: Number(payroll.totalDeductions) || deductionsTotal,
            totalAllowances: Number(payroll.totalAllowances) || allowancesTotal,

            workingDays: payroll.workingDays || 0,
            absenceDays: payroll.absenceDays || 0,
            penalityDays: payroll.penalityDays || 0,
            sickLeave25DeductionDays: payroll.sickLeave25DeductionDays || 0,

            status: payroll.paymentStatus,
            paidDate: payroll.paymentDate,
            paymentMethod: payroll.paymentMethod,

            year: payroll.year,
            month: payroll.month,
            payrollStartDate: payroll.payrollStartDate,
            payrollEndDate: payroll.payrollEndDate,

            personalInfo: {
              email: employee.email || "",
              phone: employee.phoneNumber || "",
              emergencyPhone: employee.emergencyPhoneNumber || "",
              nationalId: employee.nationalId || "",
              dateOfBirth: employee.dob || "",
              gender: employee.gender || "",
              maritalStatus: employee.maritalStatus || "",
              nationality: employee.nationality || "",
              address: employee.address || "",
              city: employee.city || "",
              country: employee.country || "Egypt",
              religion: employee.religion || "",
              age: employee.age || null,
            },

            workInfo: {
              hiringDate: employee.hiringDate || "",
              contractType: employee.laborMode || "Full-time",
              contractCompany: employee.contractCompany || "",
              workingHours: "40 hours/week",
              probationPeriod: employee.periodForBeingRegular || "3 months",
              contractExpiration: employee.contractExpiration || "",
              directManager: employee.firstLineManagerName || "",
              workLocation: employee.location || "",
              employeeType: employee.employeeType || "",
              beRegularDate: employee.beRegularDate || "",
              insuranceStatus: employee.insuranceStatus || "",
              insuranceNumber: employee.insuranceNumber || "",
              insuranceSalary: employee.insuranceSalary || 0,
            },

            bankInfo: {
              bankName: employee.bankName || "",
              accountNumber: employee.accountNumber || "",
              iban: employee.iban || "",
              branchCode: employee.branchCode || "",
            },

            notes: payroll.notes || "",
            isIgnored: payroll.isIgnored || false,
            calculatedBy: payroll.calculatedBy,
            approvedBy: payroll.approvedBy,
            approvedDate: payroll.approvedDate,

            allowances: allowances.map((a) => ({
              ...a,
              amount: Number(a.amount) || 0,
            })),
            deductions: deductions.map((d) => ({
              ...d,
              amount: Number(d.amount) || 0,
            })),
          };
        });

        // If visibleCodes is an array (not null), filter the transformed data
        let finalData = transformedData;
        if (Array.isArray(visibleCodes)) {
          finalData = transformedData.filter((p) =>
            visibleCodes.includes(String(p.infinityCode)),
          );
          console.log(
            "Number of records after visibility filter:",
            finalData.length,
          );
        }

        console.log("Transformed data sample:", finalData[0]);
        setPayrollData(finalData);
      } catch (err) {
        console.error("❌ Error fetching payroll data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPayrollData();
  }, [
    selectedMonth,
    statusFilter,
    selectedDepartment,
    selectedCompany,
    refreshTrigger,
  ]);
  // أضف useEffect بعد باقي الـ useEffects:

  useEffect(() => {
    setVisibleItems(10); // Reset to 10 items when filters change
  }, [
    searchTerm,
    statusFilter,
    selectedDepartment,
    selectedCompany,
    selectedMonth,
  ]);
  // Helper function to map payment status
  const mapPaymentStatus = (status) => {
    if (!status) return "Pending";

    const statusMap = {
      paid: "Paid",
      approved: "Processing",
      rejected: "On Hold",
      pending: "Pending",
    };

    return statusMap[status.toLowerCase()] || "Pending";
  };

  // Function to refresh data
  const refreshData = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Toggle isIgnored for a payroll record and reprocess attendance
  const handleToggleIsIgnored = async (payrollId) => {
    setTogglingIgnoredIds((prev) => new Set([...prev, payrollId]));
    try {
      const resp = await apiFetch(
        `${API_BASE_URL}/payrollX/${payrollId}/toggle-ignored`,
        {
          method: "PATCH",
        },
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to toggle");
      // Refresh full data so recalculated payroll numbers are reflected
      refreshData();
    } catch (err) {
      console.error("Toggle isIgnored failed:", err.message);
    } finally {
      setTogglingIgnoredIds((prev) => {
        const next = new Set(prev);
        next.delete(payrollId);
        return next;
      });
    }
  };

  // ==================== VISIBILITY RULES FUNCTIONS ====================

  // Fetch visibility rules (prefers admin->employees endpoint, falls back to rules)
  const fetchVisibilityRules = async () => {
    setLoadingVisibility(true);
    setVisibilityError(null);
    try {
      // Robustly resolve current user's infinity code from several fallbacks
      const userDataStr =
        localStorage.getItem("user") || sessionStorage.getItem("user");
      let currentAdminInfinityCode = null;

      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          currentAdminInfinityCode =
            userData.infinityCode ||
            userData.employeeInfinityCode ||
            userData.id ||
            userData.infinity_code;
        } catch (e) {
          // ignore parse error
        }
      }

      // Fallback: try to decode JWT token payload to extract infinityCode
      if (!currentAdminInfinityCode) {
        const token = getToken();
        if (token) {
          try {
            const payload = decodeJwtPayload(token);
            currentAdminInfinityCode =
              payload?.infinityCode ||
              payload?.infinity_code ||
              payload?.id ||
              payload?.employeeInfinityCode ||
              null;
          } catch (e) {
            // ignore
          }
        }
      }

      if (!currentAdminInfinityCode) {
        throw new Error("Admin infinity code not found");
      }

      // Normalize to Number when used in queries
      const adminCodeNum = String(currentAdminInfinityCode);
      // Do not setSelectedAdmin here - UI for selecting admin was removed

      // First, try the endpoint that returns visible employees directly
      try {
        const resp = await apiFetch(
          `${API_BASE_URL}/payrollX/visibility/admin/${adminCodeNum}/employees`,
          { method: "GET" },
        );

        if (resp.ok) {
          const body = await resp.json();
          // Map to rule-like objects to keep existing shape used elsewhere
          const mapped = (body.data || []).map((emp) => ({
            adminInfinityCode: adminCodeNum,
            employeeInfinityCode: emp.infinityCode,
            employee: emp,
          }));
          setVisibilityRules(mapped);
          return;
        }
      } catch (err) {
        // ignore and fallback to rules endpoint
        console.warn(
          "Visibility employees endpoint failed, falling back to rules endpoint",
          err,
        );
      }

      // Fallback: fetch rules filtered by admin
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules?adminInfinityCode=${adminCodeNum}`,
        { method: "GET" },
      );

      if (response.ok) {
        const result = await response.json();
        setVisibilityRules(result.data || []);
      } else {
        throw new Error("Failed to fetch visibility rules");
      }
    } catch (err) {
      console.error("Error fetching visibility rules:", err);
      setVisibilityError(err.message);
    } finally {
      setLoadingVisibility(false);
    }
  };

  // Note: admin/employee list fetching removed (Manage Visibility UI removed)

  // Add visibility rule
  const addVisibilityRule = async (adminInfinityCode, employeeInfinityCode) => {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adminInfinityCode: String(adminInfinityCode),
            employeeInfinityCode: String(employeeInfinityCode),
          }),
        },
      );

      if (response.ok) {
        showAlert(t.success, t.visibilityAdded, "success");
        fetchVisibilityRules(); // Refresh the list
        return true;
      } else {
        const error = await response.json();
        showAlert(
          t.error,
          error.error || "Failed to add visibility rule",
          "error",
        );
        return false;
      }
    } catch (err) {
      console.error("Error adding visibility rule:", err);
      showAlert(t.error, err.message, "error");
      return false;
    }
  };

  // Remove visibility rule
  const removeVisibilityRule = async (
    adminInfinityCode,
    employeeInfinityCode,
  ) => {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules/${adminInfinityCode}/${employeeInfinityCode}`,
        {
          method: "DELETE",
        },
      );

      if (response.ok) {
        showAlert(t.success, t.visibilityRemoved, "success");
        fetchVisibilityRules(); // Refresh the list
        return true;
      } else {
        showAlert(t.error, "Failed to remove visibility rule", "error");
        return false;
      }
    } catch (err) {
      console.error("Error removing visibility rule:", err);
      showAlert(t.error, err.message, "error");
      return false;
    }
  };

  // Bulk add visibility rules
  const bulkAddVisibilityRules = async (
    adminInfinityCode,
    employeeInfinityCodes,
  ) => {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules/bulk`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adminInfinityCode: String(adminInfinityCode),
            employeeInfinityCodes: employeeInfinityCodes.map(Number),
          }),
        },
      );

      if (response.ok) {
        const result = await response.json();
        showAlert(
          t.success,
          result.message || `Added ${result.data.added} visibility rules`,
          "success",
        );
        fetchVisibilityRules(); // Refresh the list
        return true;
      } else {
        showAlert(t.error, "Failed to bulk add visibility rules", "error");
        return false;
      }
    } catch (err) {
      console.error("Error bulk adding visibility rules:", err);
      showAlert(t.error, err.message, "error");
      return false;
    }
  };

  // Helpers to check visibility (used by filtering payrolls)
  const getVisibleEmployeeCodes = () => {
    return visibilityRules.map((r) => String(r.employeeInfinityCode));
  };

  const isEmployeeVisible = (employeeInfinityCode) => {
    const codes = getVisibleEmployeeCodes();
    return codes.includes(String(employeeInfinityCode));
  };

  // Load visibility rules (UI removed)
  const openVisibilityModal = () => {
    // legacy function name retained for any callers; now only loads visibility rules
    fetchVisibilityRules();
  };

  // ==================== END VISIBILITY RULES FUNCTIONS ====================

  // Function to refresh data
  const refreshPayrollData = () => {
    console.log("🔄 Refreshing payroll data...");
    setRefreshTrigger((prev) => prev + 1);
  };

  // Enhanced benefits data
  const benefits = [
    {
      name: t("medicalInsurance"),
      status: t("active"),
      icon: Heart,
      coverage: t("fullFamily"),
      //value: t('comprehensiveCoverage'),
      description: t("comprehensiveCoverage"),
    },
    {
      name: t("socialInsurance"),
      status: t("active"),
      icon: Shield,
      coverage: t("govMandated"),
      //value: "29.75%",
      description: t("pensionCoverage"),
    },
    {
      name: t("annualLeave"),
      status: "21 " + t("daysRemaining"),
      icon: Calendar,
      coverage: t("paidTimeOff"),
      //value: "21 " + t('daysRemaining'),
      description: t("annualVacation"),
    },
    {
      name: t("training"),
      status: t("available"),
      icon: GraduationCap,
      coverage: t("professionalGrowth"),
      //value: t('companySponsored'),
      description: t("profDevelopment"),
    },
  ];

  // Filter payroll data
  const filteredPayrollData = payrollData.filter((payroll) => {
    const matchesSearch =
      payroll.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payroll.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All" || payroll.status === statusFilter;
    const matchesDepartment =
      selectedDepartment === "All" || payroll.department === selectedDepartment;
    const matchesCompany =
      selectedCompany === "All" ||
      (payroll.workingCompany || "") === selectedCompany;

    // Keep month/year filter in frontend as well so table and exports stay in sync
    // even if backend response includes extra rows.
    const fallbackDate = payroll.paidDate || payroll.payrollStartDate || null;
    const rowYear = Number(
      payroll.year || (fallbackDate ? new Date(fallbackDate).getFullYear() : 0),
    );
    const rowMonth = Number(
      payroll.month ||
        (fallbackDate ? new Date(fallbackDate).getMonth() + 1 : 0),
    );

    const selectedYear = Number(selectedMonth?.year || 0);
    const selectedMonthValue = Number(selectedMonth?.month || 0);

    const matchesYear = !selectedYear || !rowYear || rowYear === selectedYear;
    const matchesMonth =
      !selectedMonthValue || !rowMonth || rowMonth === selectedMonthValue;

    return (
      matchesSearch &&
      matchesStatus &&
      matchesDepartment &&
      matchesCompany &&
      matchesYear &&
      matchesMonth
    );
  });

  // Calculate totals
  const totalGrossSalary = filteredPayrollData.reduce(
    (sum, emp) => sum + emp.grossSalary,
    0,
  );
  const totalNetSalary = filteredPayrollData.reduce(
    (sum, emp) => sum + emp.netSalary,
    0,
  );
  const totalDeductions = filteredPayrollData.reduce(
    (sum, emp) => sum + emp.totalDeductions,
    0,
  );
  const averageKPI =
    filteredPayrollData.reduce((sum, emp) => sum + emp.kpiScore, 0) /
    filteredPayrollData.length;

  // Salary statistics
  // Salary statistics
  // ✅ استبدل salaryStats بالكود ده
  const salaryStats = [
    {
      icon: DollarSign,
      value: `${totalGrossSalary.toLocaleString()} ${t("currency")}`,
      label: t("totalGross"),
      color: currentTheme.colors.primary,
      subtitle: t("monthlyGross"),
    },
    {
      icon: UserCheck,
      value: payrollData.filter((p) => p.status === "paid").length.toString(),
      label: t("employeesPaid"),
      color: currentTheme.colors.success,
      subtitle: t("currentMonth"),
    },
    {
      icon: Clock,
      value: payrollData
        .filter((p) => p.status === "pending")
        .length.toString(),
      label: t("pendingPayments"),
      color: currentTheme.colors.warning,
      subtitle: t("awaitingApproval"),
    },
    {
      icon: TrendingUp,
      value: `${averageKPI.toFixed(1)}%`,
      label: t("averageKPI"),
      color: currentTheme.colors.info,
      subtitle: t("performanceIndicator"),
    },
  ];

  // Get KPI badge style
  // const getKPIBadge = (score) => {
  //   let bgColor, textColor;
  //   if (score >= 90) {
  //     bgColor = "rgba(16,185,129,0.2)";
  //     textColor = "#10b981";
  //   } else if (score >= 80) {
  //     bgColor = "rgba(59,130,246,0.2)";
  //     textColor = "#2e7cba";
  //   } else if (score >= 70) {
  //     bgColor = "rgba(245,158,11,0.2)";
  //     textColor = "#f59e0b";
  //   } else {
  //     bgColor = "rgba(239,68,68,0.2)";
  //     textColor = "#ef4444";
  //   }

  //   return (
  //     <span
  //       style={{
  //         display: "inline-flex",
  //         alignItems: "center",
  //         gap: "6px",
  //         padding: "6px 12px",
  //         borderRadius: "10px",
  //         fontSize: "12px",
  //         fontWeight: "700",
  //         textTransform: "uppercase",
  //         letterSpacing: "0.5px",
  //         background: bgColor,
  //         color: textColor,
  //         border: `1px solid ${textColor}40`,
  //       }}
  //     >
  //       <Award size={12} />
  //       {score}%
  //     </span>
  //   );
  // };

  const DeductionsModal = ({ isOpen, employee, onClose }) => {
    const [deductions, setDeductions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDeduction, setNewDeduction] = useState({
      type: "",
      description: "",
      amount: "",
      days: "",
    });
    const [lateCalcLoading, setLateCalcLoading] = useState(false);
    const [lateRates, setLateRates] = useState(null); // { dailyRate, lateDeductionPerHour }

    useEffect(() => {
      if (isOpen && employee?.id) {
        fetchDeductions();
      }
    }, [isOpen, employee?.id]);

    const fetchDeductions = async () => {
      setLoading(true);
      try {
        const url = `${API_BASE_URL}/payrollX/${employee.id}/deductions`;
        const response = await apiFetch(url, { method: "GET" });

        if (response.ok) {
          const result = await response.json();
          setDeductions(result?.data?.deductions || []);
        }
      } catch (err) {
        console.error("Error fetching deductions:", err);
      } finally {
        setLoading(false);
      }
    };

    const handleAddDeduction = async () => {
      if (!newDeduction.type) {
        showAlert(t("important"), t("fillTypeAmount"), "info");
        return;
      }

      let days = newDeduction.days ? parseFloat(newDeduction.days) : null;
      let amount = newDeduction.amount ? parseFloat(newDeduction.amount) : 0;

      // Always calculate amount from days if days is provided
      if (days && (!amount || amount === 0)) {
        let dailyRate = 0;
        if (
          newDeduction.type === "late_arrival" &&
          lateRates &&
          lateRates.dailyRate
        ) {
          dailyRate = lateRates.dailyRate;
        } else if (employee && employee.dailyRate) {
          dailyRate = employee.dailyRate;
        } else if (employee && employee.basicSalary) {
          dailyRate = Number(employee.basicSalary) / 30;
        }
        amount = days * dailyRate;
      }

      // If neither amount nor days is valid, show alert
      if (
        (!amount || isNaN(amount) || amount === 0) &&
        (!days || isNaN(days) || days === 0)
      ) {
        showAlert(t("important"), t("fillTypeAmount"), "info");
        return;
      }

      setLoading(true);
      try {
        const url = `${API_BASE_URL}/payrollX/${employee.id}/deductions`;
        const response = await apiFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: newDeduction.type,
            description: newDeduction.description,
            amount: amount,
            days: days,
          }),
        });

        if (response.ok) {
          await fetchDeductions();
          setNewDeduction({
            type: "",
            description: "",
            amount: "",
            days: "",
          });
          setShowAddForm(false);
          showAlert(t("success"), t("deductionAddedSuccess"), "success");
        }
      } catch (err) {
        console.error("Error adding deduction:", err);
        showAlert(t("error"), t("errorAddingDeduction"), "error");
      } finally {
        setLoading(false);
      }
    };

    const calculateTotal = () => {
      return deductions.reduce((sum, d) => sum + Number(d.amount || 0), 0);
    };

    const fetchLateDeduction = async () => {
      if (!employee?.id) return;
      setLateCalcLoading(true);
      try {
        const url = `${API_BASE_URL}/payrollX/${employee.id}/late-deduction-calc`;
        const response = await apiFetch(url, { method: "GET" });
        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;
          setLateRates({
            dailyRate: data.dailyRate || 0,
            lateDeductionPerHour: data.lateDeductionPerHour || 1,
          });
          // Reset amount so user enters hours
          setNewDeduction((prev) => ({
            ...prev,
            type: "late_arrival",
            amount: "",
            description: t("lateArrivalCalcDesc"),
          }));
        } else {
          console.error("Failed to fetch late deduction rates");
          setLateRates(null);
        }
      } catch (err) {
        console.error("Error fetching late deduction rates:", err);
        setLateRates(null);
      } finally {
        setLateCalcLoading(false);
      }
    };

    const handleLateHoursChange = (hours) => {
      const h = Number(hours) || 0;
      if (lateRates) {
        // Half a day per hour late: 1 hour = 0.5 days
        const amount = (h / 2) * lateRates.dailyRate;
        setNewDeduction((prev) => ({
          ...prev,
          days: hours,
          amount: amount > 0 ? amount.toFixed(2) : "",
          description:
            h > 0
              ? `${t("lateArrivalCalcDesc")} - ${h} ${language === "ar" ? "ساعة" : "hrs"}`
              : t("lateArrivalCalcDesc"),
        }));
      } else {
        setNewDeduction((prev) => ({ ...prev, days: hours }));
      }
    };

    if (!isOpen) return null;

    const formStyles = {
      modal: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
        overflowY: "auto",
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        WebkitOverflowScrolling: "touch", // للـ iOS
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
      },
      modalContent: {
        background: currentTheme.colors.cardBackground,
        backdropFilter: "blur(25px)",
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "24px",
        padding: "40px",
        width: "100%",
        maxWidth: "900px",
        margin: "20px auto",
        maxHeight: "90vh",
        overflowY: "auto",
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        WebkitOverflowScrolling: "touch", // للـ iOS
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        boxShadow: currentTheme.shadows.card,
      },
      header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "32px",
        paddingBottom: "20px",
        borderBottom: `2px solid ${currentTheme.colors.border}`,
      },
      statCard1: {
        background:
          theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
        padding: "1.55rem",
        marginTop: -40,
        marginLeft: 5,
        borderRadius:
          screenSize === "xs"
            ? "1rem"
            : screenSize === "sm"
              ? "1.25rem"
              : "1rem",
        transition: "all 0.3s ease",
        boxShadow:
          theme === "dark"
            ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
            : "0 0.5rem 2rem rgba(0,0,0,0.1)",
        backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
        WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
        border:
          theme === "dark"
            ? "1px solid rgba(255,255,255,0.15)"
            : "1px solid rgba(0,0,0,0.05)",
        marginBottom: 70,
        cursor: "pointer",
      },

      title: {
        color: currentTheme.colors.textPrimary,
        fontSize: "28px",
        fontWeight: "800",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "12px",
      },
      section: {
        background: currentTheme.colors.inputBackground,
        borderRadius: "20px",
        padding: "24px",
        border: `2px solid ${currentTheme.colors.border}`,
        marginBottom: "24px",
      },
      deductionCard: {
        background: currentTheme.colors.cardBackground,
        borderRadius: "12px",
        padding: "20px",
        border: `2px solid ${currentTheme.colors.borderLight}`,
        marginBottom: "16px",
        transition: "all 0.3s ease",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`, // ← هتبقى 4 columns
        gap: "16px",
        marginBottom: "20px",
      },
      formGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      },
      label: {
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
      input: {
        background: currentTheme.colors.inputBackground,
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "12px",
        padding: "12px 16px",
        color: currentTheme.colors.textPrimary,
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.3s ease",
        outline: "none",
      },
      totalCard: {
        background: `${currentTheme.colors.danger}10`,
        border: `2px solid ${currentTheme.colors.danger}40`,
        borderRadius: "16px",
        padding: "24px",
        textAlign: "center",
      },
    };

    return (
      <div
        style={formStyles.modal}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div style={formStyles.modalContent}>
          <div style={formStyles.header}>
            <h1 style={formStyles.title}>
              <UserCheck size={32} />
              {t("employeeDeductions")}{" "}
              {employee?.employeeName?.split(" ").slice(0, 2).join(" ")}
            </h1>
            {/* <button
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "2px solid rgba(239, 68, 68, 0.3)",
              borderRadius: "12px",
              padding: "12px",
              cursor: "pointer",
              color: "#ef4444",
              transition: "all 0.3s ease",
            }}
            onClick={onClose}
          >
            <X size={24} />
          </button> */}
          </div>

          {/* Add Deduction Form */}
          <div style={formStyles.section}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: currentTheme.colors.textPrimary,
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                {t("addDeduction")}
              </h3>
              <Mo3tiButton
                variant={showAddForm ? "secondary" : "primary"}
                icon={showAddForm ? X : Plus}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? t("cancel") : t("addDeduction")}
              </Mo3tiButton>
            </div>

            {showAddForm && (
              <div>
                <div style={formStyles.formGrid}>
                  <div style={formStyles.formGroup}>
                    <label style={formStyles.label}>{t("deductionType")}</label>
                    <ReusableDropdown
                      items={[
                        { id: "absence", label: t("absence") },
                        { id: "penalty", label: t("penalty") },
                        { id: "advance", label: t("advance") },
                        { id: "insurance", label: t("insurance") },
                        { id: "sick_leave_25", label: t("sickLeave") },
                        { id: "late_arrival", label: t("lateArrival") },
                        { id: "other", label: t("other") },
                      ]}
                      value={
                        newDeduction.type
                          ? [
                              { id: "absence", label: t("absence") },
                              { id: "penalty", label: t("penalty") },
                              { id: "advance", label: t("advance") },
                              { id: "insurance", label: t("insurance") },
                              { id: "sick_leave_25", label: t("sickLeave") },
                              { id: "late_arrival", label: t("lateArrival") },
                              { id: "other", label: t("other") },
                            ].find((item) => item.id === newDeduction.type)
                              ?.label || ""
                          : ""
                      }
                      onChange={(val) => {
                        const selectedItem = [
                          { id: "absence", label: t("absence") },
                          { id: "penalty", label: t("penalty") },
                          { id: "advance", label: t("advance") },
                          { id: "insurance", label: t("insurance") },
                          { id: "sick_leave_25", label: t("sickLeave") },
                          { id: "late_arrival", label: t("lateArrival") },
                          { id: "other", label: t("other") },
                        ].find((item) => item.label === val);
                        if (selectedItem) {
                          setNewDeduction({
                            ...newDeduction,
                            type: selectedItem.id,
                          });
                          if (selectedItem.id === "late_arrival") {
                            fetchLateDeduction();
                          }
                        }
                      }}
                      onSelect={(item) => {
                        setNewDeduction({ ...newDeduction, type: item.id });
                        if (item.id === "late_arrival") {
                          fetchLateDeduction();
                        }
                      }}
                      placeholder={t("selectType")}
                      theme={theme}
                      language={language}
                      maxItems={7}
                      getItemKey={(item) => item.id}
                      getItemLabel={(item) => item.label}
                      showAllOnArrowClick={true}
                      showClear={true}
                      onClear={() =>
                        setNewDeduction({
                          ...newDeduction,
                          type: "",
                          amount: "",
                          description: "",
                        })
                      }
                      inputStyle={formStyles.input}
                    />
                  </div>

                  <div style={formStyles.formGroup}>
                    <label style={formStyles.label}>{t("amount")}</label>
                    {lateCalcLoading && newDeduction.type === "late_arrival" ? (
                      <div
                        style={{
                          ...formStyles.input,
                          display: "flex",
                          alignItems: "center",
                          color: theme === "dark" ? "#aaa" : "#888",
                        }}
                      >
                        {t("calculatingLate")}
                      </div>
                    ) : (
                      <input
                        type="number"
                        style={{
                          ...formStyles.input,
                          ...(newDeduction.type === "late_arrival"
                            ? {
                                backgroundColor:
                                  theme === "dark" ? "#333" : "#f0f0f0",
                                cursor: "not-allowed",
                              }
                            : {}),
                        }}
                        value={newDeduction.amount}
                        onChange={(e) => {
                          if (newDeduction.type !== "late_arrival") {
                            setNewDeduction({
                              ...newDeduction,
                              amount: e.target.value,
                            });
                          }
                        }}
                        readOnly={newDeduction.type === "late_arrival"}
                        placeholder="0.00"
                      />
                    )}
                  </div>

                  <div style={formStyles.formGroup}>
                    <label style={formStyles.label}>
                      {newDeduction.type === "late_arrival"
                        ? t("lateHours")
                        : t("days")}
                    </label>
                    <input
                      type="number"
                      style={formStyles.input}
                      value={newDeduction.days}
                      onChange={(e) => {
                        if (newDeduction.type === "late_arrival") {
                          handleLateHoursChange(e.target.value);
                        } else {
                          setNewDeduction({
                            ...newDeduction,
                            days: e.target.value,
                          });
                        }
                      }}
                      placeholder={
                        newDeduction.type === "late_arrival" ? "0" : "0"
                      }
                      step={newDeduction.type === "late_arrival" ? "0.5" : "1"}
                    />
                  </div>
                </div>

                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("description")}</label>
                  <textarea
                    style={{
                      ...formStyles.input,
                      minHeight: "80px",
                      resize: "vertical",
                    }}
                    value={newDeduction.description}
                    onChange={(e) =>
                      setNewDeduction({
                        ...newDeduction,
                        description: e.target.value,
                      })
                    }
                    placeholder={t("descriptionPlaceholder")}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                    marginTop: "20px",
                  }}
                >
                  <Mo3tiButton
                    variant="primary"
                    icon={Save}
                    onClick={handleAddDeduction}
                    disabled={loading}
                  >
                    {loading ? t("saving") : t("saveDeduction")}
                  </Mo3tiButton>
                </div>
              </div>
            )}
          </div>

          {/* Deductions List */}
          <div style={formStyles.section}>
            <h3
              style={{
                margin: "0 0 20px 0",
                color: currentTheme.colors.textPrimary,
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              {t("currentDeductions")} ({deductions.length})
            </h3>

            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: currentTheme.colors.textSecondary,
                }}
              >
                <Activity
                  size={32}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                <p>{t("loading")}</p>
              </div>
            ) : deductions.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: currentTheme.colors.textSecondary,
                }}
              >
                <Minus
                  size={48}
                  style={{ opacity: 0.3, marginBottom: "16px" }}
                />
                <p>{t("noDeductions")}</p>
              </div>
            ) : (
              deductions.map((deduction, index) => (
                <div
                  key={index}
                  style={formStyles.deductionCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = currentTheme.shadows.card;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <span
                          style={{
                            background: `${currentTheme.colors.danger}20`,
                            color: currentTheme.colors.danger,
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "700",
                            textTransform: "uppercase",
                          }}
                        >
                          {t(deduction.type)}
                        </span>
                        {deduction.days && (
                          <span
                            style={{
                              fontSize: "13px",
                              color: currentTheme.colors.textMuted,
                            }}
                          >
                            {deduction.days} {t("days")}
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          fontSize: "15px",
                          color: currentTheme.colors.textPrimary,
                          marginBottom: "8px",
                          fontWeight: "600",
                        }}
                      >
                        {deduction.description || t("noDescription")}
                      </div>

                      <div
                        style={{
                          fontSize: "13px",
                          color: currentTheme.colors.textMuted,
                        }}
                      >
                        {t("date")}:{" "}
                        {deduction.attendance?.date
                          ? new Date(
                              deduction.attendance.date,
                            ).toLocaleDateString(isRTL ? "ar-EG" : "en-GB")
                          : "N/A"}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "800",
                          color: currentTheme.colors.danger,
                          fontFamily: "monospace",
                        }}
                      >
                        -{Number(deduction.amount || 0).toLocaleString()}{" "}
                        {t("currency")}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Employee Details Modal with Form View
  const EmployeeDetailsModal = ({ employee, onClose }) => {
    const [formData, setFormData] = useState({
      employeeName: employee?.employeeName || "",
      employeeId: employee?.employeeId || "",
      email: employee?.personalInfo?.email || "",
      phone: employee?.personalInfo?.phone || "",
      emergencyPhone: employee?.personalInfo?.emergencyPhone || "",
      nationalId: employee?.personalInfo?.nationalId || "",
      dateOfBirth: employee?.personalInfo?.dateOfBirth || "",
      gender: employee?.personalInfo?.gender || "",
      maritalStatus: employee?.personalInfo?.maritalStatus || "",
      nationality: employee?.personalInfo?.nationality || "",
      address: employee?.personalInfo?.address || "",
      city: employee?.personalInfo?.city || "",
      country: employee?.personalInfo?.country || "",
      department: employee?.department || "",
      position: employee?.position || "",
      hiringDate: employee?.workInfo?.hiringDate || "",
      contractType: employee?.workInfo?.contractType || "",
      workingHours: employee?.workInfo?.workingHours || "",
      contractExpiration: employee?.workInfo?.contractExpiration || "",
      directManager: employee?.workInfo?.directManager || "",
      workLocation: employee?.workInfo?.workLocation || "",
      basicSalary: employee?.basicSalary || 0,
      bonus: employee?.bonus || 0,
      overtimeHours: employee?.overtimeHours || 0,
      overtimeTotal: employee?.overtimeTotal || 0,
      transportationAllowance: employee?.transportationAllowance || 0,
      mealAllowance: employee?.mealAllowance || 0,
      mobileAllowance: employee?.mobileAllowance || 0,
      bonusAdjustments: employee?.bonusAdjustments || 0,
      grossSalary: employee?.grossSalary || 0,
      taxMonth: employee?.taxMonth || 0,
      socialInsurance: employee?.socialInsurance || 0,
      penalty: employee?.penalty || 0,
      advancePayment: employee?.advancePayment || 0,
      totalDeductions: employee?.totalDeductions || 0,
      netSalary: employee?.netSalary || 0,
      kpiScore: employee?.kpiScore || 0,
      status: employee?.status || "Active",
      bankName: employee?.bankInfo?.bankName || "",
      accountNumber: employee?.bankInfo?.accountNumber || "",
      iban: employee?.bankInfo?.iban || "",
      branchCode: employee?.bankInfo?.branchCode || "",
    });

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSave = async () => {
      setLoading(true);
      try {
        console.log("Saving employee data:", formData);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsEditing(false);
        showAlert(t("success"), t("employeeDataUpdatedSuccess"), "success");
      } catch (error) {
        console.error("Error saving employee data:", error);
        showAlert(t("error"), t("errorSavingEmployeeData"), "error");
      } finally {
        setLoading(false);
      }
    };

    const formStyles = {
      modal: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 2000,
        padding: "20px",
        overflowY: "auto",
      },
      modalContent: {
        background: currentTheme.colors.cardBackground,
        backdropFilter: "blur(25px)",
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "24px",
        padding: "40px",
        width: "100%",
        maxWidth: "1200px",
        margin: "20px auto",
        maxHeight: "90vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch", // للـ iOS
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        //boxShadow: currentTheme.shadows.card,
        direction: isRTL ? "rtl" : "ltr",
      },
      header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "40px",
        paddingBottom: "20px",
        borderBottom: `2px solid ${currentTheme.colors.border}`,
      },
      title: {
        color: currentTheme.colors.textPrimary,
        fontSize: "32px",
        fontWeight: "800",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "16px",
      },
      closeButton: {
        background: "rgba(239, 68, 68, 0.1)",
        border: "2px solid rgba(239, 68, 68, 0.3)",
        borderRadius: "12px",
        padding: "12px",
        cursor: "pointer",
        color: "#ef4444",
        transition: "all 0.3s ease",
      },
      formContainer: {
        display: "grid",
        gap: "32px",
      },
      section: {
        background: currentTheme.colors.inputBackground,
        borderRadius: "20px",
        padding: "32px",
        border: `2px solid ${currentTheme.colors.border}`,
      },
      sectionTitle: {
        color: currentTheme.colors.textPrimary,
        fontSize: "24px",
        fontWeight: "700",
        marginBottom: "24px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        textTransform: "uppercase",
        letterSpacing: "1px",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "24px",
      },
      formGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      },
      label: {
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
      input: {
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.7)",
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "12px",
        padding: "16px",
        // color: currentTheme.colors.textPrimary,
        fontSize: "16px",
        fontWeight: "500",
        transition: "all 0.3s ease",
        outline: "none",
      },
      readOnlyInput: {
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.96)",
        border: `2px solid ${currentTheme.colors.borderLight}`,
        borderRadius: "12px",
        padding: "16px",
        color: currentTheme.colors.textMuted,
        fontSize: "16px",
        fontWeight: "500",
        cursor: "not-allowed",
      },
      actionButtons: {
        display: "flex",
        gap: "16px",
        justifyContent: "center",
        marginTop: "40px",
        paddingTop: "32px",
        borderTop: `2px solid ${currentTheme.colors.border}`,
      },
    };

    return (
      <div
        style={formStyles.modal}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div style={formStyles.modalContent}>
          <div style={formStyles.header}>
            <h1 style={formStyles.title}>
              <User size={32} />
              {t("employeeDetailsTitle")}
            </h1>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              {/* <button
              style={formStyles.closeButton}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <X size={24} />
            </button> */}
            </div>
          </div>

          <div style={formStyles.formContainer}>
            {/* Personal Information Section */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <User size={24} />
                {t("personalInformation")}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("fullName")}</label>
                  <input
                    type="text"
                    name="employeeName"
                    value={formData.employeeName}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("employeeId")}</label>
                  <input
                    type="text"
                    name="employeeId"
                    value={formData.employeeId}
                    style={formStyles.readOnlyInput}
                    readOnly
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("emailAddress")}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("phoneNumber")}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("emergencyPhone")}</label>
                  <input
                    type="tel"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("nationalId")}</label>
                  <input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("dateOfBirth")}</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("gender")}</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    disabled={!isEditing}
                  >
                    <option value="">{t("selectGender")}</option>
                    <option value="Male">{t("male")}</option>
                    <option value="Female">{t("female")}</option>
                  </select>
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("maritalStatus")}</label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    disabled={!isEditing}
                  >
                    <option value="">{t("selectStatus")}</option>
                    <option value="Single">{t("single")}</option>
                    <option value="Married">{t("married")}</option>
                    <option value="Divorced">{t("divorced")}</option>
                    <option value="Widowed">{t("widowed")}</option>
                  </select>
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("nationality")}</label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("city")}</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("country")}</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
              </div>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t("address")}</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  style={
                    isEditing ? formStyles.input : formStyles.readOnlyInput
                  }
                  readOnly={!isEditing}
                />
              </div>
            </div>

            {/* Work Information Section */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <Building size={24} />
                {t("workInformation")}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("department")}</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("position")}</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("hiringDate")}</label>
                  <input
                    type="date"
                    name="hiringDate"
                    value={formData.hiringDate}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("contractType")}</label>
                  <select
                    name="contractType"
                    value={formData.contractType}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    disabled={!isEditing}
                  >
                    <option value="">{t("selectType")}</option>
                    <option value="Full-time">{t("fullTime")}</option>
                    <option value="Part-time">{t("partTime")}</option>
                    <option value="Contract">{t("contract")}</option>
                    <option value="Temporary">{t("temporary")}</option>
                  </select>
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("workingHours")}</label>
                  <input
                    type="text"
                    name="workingHours"
                    value={formData.workingHours}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("contractExpiration")}
                  </label>
                  <input
                    type="date"
                    name="contractExpiration"
                    value={formData.contractExpiration}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("directManager")}</label>
                  <input
                    type="text"
                    name="directManager"
                    value={formData.directManager}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("workLocation")}</label>
                  <input
                    type="text"
                    name="workLocation"
                    value={formData.workLocation}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("employmentStatus")}
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <StatusBadge
                      status={formData.status}
                      language={language}
                      translations={translations}
                    />
                    {isEditing && (
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        style={{ ...formStyles.input, flex: 1 }}
                      >
                        <option value="Active">{t("active")}</option>
                        <option value="Paid">{t("paid")}</option>
                        <option value="Pending">{t("pending")}</option>
                        <option value="Processing">{t("processing")}</option>
                        <option value="On Hold">{t("onHold")}</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Salary & Earnings Section */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <DollarSign size={24} />
                {t("salaryAndEarnings")}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("basicSalary")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="basicSalary"
                    value={formData.basicSalary}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("bonus")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="bonus"
                    value={formData.bonus}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("overtimeHours")}</label>
                  <input
                    type="number"
                    name="overtimeHours"
                    value={formData.overtimeHours}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("overtimeTotal")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="overtimeTotal"
                    value={formData.overtimeTotal}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("transportationAllowance")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="transportationAllowance"
                    value={formData.transportationAllowance}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("mealAllowance")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="mealAllowance"
                    value={formData.mealAllowance}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("mobileAllowance")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="mobileAllowance"
                    value={formData.mobileAllowance}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("bonusAdjustments")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="bonusAdjustments"
                    value={formData.bonusAdjustments}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("grossSalary")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="grossSalary"
                    value={formData.grossSalary}
                    style={formStyles.readOnlyInput}
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Banking Information Section */}
            {/* <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <CreditCard size={24} />
                {t('bankingInformation')}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('bankName')}</label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('accountNumber')}</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('iban')}</label>
                  <input
                    type="text"
                    name="iban"
                    value={formData.iban}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('branchCode')}</label>
                  <input
                    type="text"
                    name="branchCode"
                    value={formData.branchCode}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
              </div>
            </div> */}

            {/* Action Buttons */}
            <div style={formStyles.actionButtons}>
              {isEditing && (
                <Mo3tiButton
                  variant="primary"
                  icon={Save}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? t("saving") : t("saveChanges")}
                </Mo3tiButton>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // // ProcessPayrollModal Component
  // const ProcessPayrollModal = ({ onClose, onProcessComplete }) => {
  //   const { currentTheme } = useContext(ThemeContext);
  //   const { language } = useContext(LanguageContext);
  //   const t = (key) => translations[language][key] || key;

  //   // Access setSelectedMonth from parent scope
  //   // (Assume setSelectedMonth is available in the closure)

  //   // ✅ حط الـ styles هنا جوه الـ Modal
  //   const modalStyles = {
  //     modal: {
  //       position: "fixed",
  //       top: 0,
  //       left: 0,
  //       right: 0,
  //       bottom: 0,
  //       background: "rgba(0, 0, 0, 0.8)",
  //       display: "flex",
  //       alignItems: "center",
  //       justifyContent: "center",
  //       zIndex: 3000,
  //       padding: "20px",
  //       overflowY: "auto",
  //       scrollbarWidth: "none", // للـ Firefox
  //       msOverflowStyle: "none", // للـ IE و Edge
  //       WebkitOverflowScrolling: "touch", // للـ iOS
  //       scrollbarWidth: "none", // للـ Firefox
  //       msOverflowStyle: "none", // للـ IE و Edge
  //     },
  //     modalContent: {
  //       background:
  //         theme === "dark"
  //           ? "rgba(30, 41, 59, 0.95)"
  //           : "rgba(255, 255, 255, 0.95)",
  //       backdropFilter: "blur(25px)",
  //       border:
  //         theme === "dark"
  //           ? "1px solid rgba(255,255,255,0.1)"
  //           : "1px solid rgba(0,0,0,0.1)",
  //       borderRadius: "24px",
  //       padding: "40px",
  //       width: "100%",
  //       maxWidth: "1200px",
  //       margin: "20px auto",
  //       maxHeight: "90vh",
  //       overflowY: "auto",
  //       scrollbarWidth: "none", // للـ Firefox
  //       msOverflowStyle: "none", // للـ IE و Edge
  //       WebkitOverflowScrolling: "touch", // للـ iOS
  //       scrollbarWidth: "none", // للـ Firefox
  //       msOverflowStyle: "none", // للـ IE و Edge
  //       boxShadow:
  //         theme === "dark"
  //           ? "0 20px 60px rgba(0,0,0,0.5)"
  //           : "0 20px 60px rgba(0,0,0,0.2)",
  //     },
  //     header: {
  //       display: "flex",
  //       alignItems: "center",
  //       justifyContent: "space-between",
  //       marginBottom: "40px",
  //       paddingBottom: "20px",
  //       borderBottom:
  //         theme === "dark"
  //           ? "2px solid rgba(255,255,255,0.1)"
  //           : "2px solid rgba(0,0,0,0.1)",
  //     },
  //     title: {
  //       color: theme === "dark" ? "#ffffff" : "#1a1a1a",
  //       fontSize: "32px",
  //       fontWeight: "800",
  //       margin: 0,
  //       display: "flex",
  //       alignItems: "center",
  //       gap: "16px",
  //     },
  //     formSection: {
  //       background:
  //         theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
  //       borderRadius: "20px",
  //       padding: "32px",
  //       border:
  //         theme === "dark"
  //           ? "2px solid rgba(255,255,255,0.1)"
  //           : "2px solid rgba(0,0,0,0.05)",
  //       marginBottom: "24px",
  //     },
  //     sectionTitle: {
  //       color: theme === "dark" ? "#ffffff" : "#1a1a1a",
  //       fontSize: "24px",
  //       fontWeight: "700",
  //       marginBottom: "24px",
  //       display: "flex",
  //       alignItems: "center",
  //       gap: "12px",
  //     },
  //     formGrid: {
  //       display: "grid",
  //       gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  //       gap: "24px",
  //     },
  //     formGroup: {
  //       display: "flex",
  //       flexDirection: "column",
  //       gap: "8px",
  //     },
  //     formGroupWithMargin: {
  //       display: "flex",
  //       flexDirection: "column",
  //       gap: "8px",
  //       marginTop: "20px",
  //     },
  //     label: {
  //       color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
  //       fontSize: "14px",
  //       fontWeight: "600",
  //       textTransform: "uppercase",
  //       letterSpacing: "0.5px",
  //     },
  //     input: {
  //       background:
  //         theme === "dark"
  //           ? "rgba(30, 41, 59, 0.8)"
  //           : "rgba(255, 255, 255, 0.95)",
  //       border:
  //         theme === "dark"
  //           ? "2px solid rgba(255,255,255,0.1)"
  //           : "2px solid rgba(0,0,0,0.1)",
  //       borderRadius: "12px",
  //       padding: "16px",
  //       color: theme === "dark" ? "#ffffff" : "#1a1a1a",
  //       fontSize: "16px",
  //       fontWeight: "500",
  //       outline: "none",
  //     },
  //     checkboxGroup: {
  //       display: "flex",
  //       alignItems: "center",
  //       gap: "12px",
  //     },
  //     stepIndicator: {
  //       display: "flex",
  //       alignItems: "center",
  //       justifyContent: "center",
  //       gap: "20px",
  //       marginBottom: "40px",
  //       padding: "20px",
  //       background:
  //         theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
  //       borderRadius: "16px",
  //     },
  //     step: {
  //       display: "flex",
  //       alignItems: "center",
  //       gap: "12px",
  //       padding: "12px 20px",
  //       borderRadius: "12px",
  //       fontSize: "14px",
  //       fontWeight: "600",
  //       color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
  //     },
  //     activeStep: {
  //       background: "#2e7cba",
  //       color: "#ffffff",
  //     },
  //     stepNumber: {
  //       width: "32px",
  //       height: "32px",
  //       borderRadius: "50%",
  //       display: "flex",
  //       alignItems: "center",
  //       justifyContent: "center",
  //       fontSize: "14px",
  //       fontWeight: "700",
  //       background:
  //         theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
  //     },
  //     activeStepNumber: {
  //       background: "#ffffff",
  //       color: "#2e7cba",
  //     },
  //     employeeList: {
  //       maxHeight: "400px",
  //       overflowY: "auto",
  //       padding: "16px",
  //       background:
  //         theme === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.5)",
  //       borderRadius: "12px",
  //     },
  //     employeeItem: {
  //       padding: "16px",
  //       background:
  //         theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.9)",
  //       borderRadius: "12px",
  //       marginBottom: "12px",
  //       display: "flex",
  //       alignItems: "center",
  //       gap: "16px",
  //       cursor: "pointer",
  //       border:
  //         theme === "dark"
  //           ? "1px solid rgba(255,255,255,0.1)"
  //           : "1px solid rgba(0,0,0,0.1)",
  //     },
  //     previewCard: {
  //       background:
  //         theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
  //       borderRadius: "16px",
  //       padding: "24px",
  //       marginBottom: "20px",
  //       border:
  //         theme === "dark"
  //           ? "1px solid rgba(255,255,255,0.1)"
  //           : "1px solid rgba(0,0,0,0.1)",
  //     },
  //     previewTotal: {
  //       marginTop: "20px",
  //       padding: "20px",
  //       background: "#2e7cba",
  //       color: "#ffffff",
  //       borderRadius: "12px",
  //       fontSize: "24px",
  //       fontWeight: "800",
  //       textAlign: "center",
  //     },
  //     actionButtons: {
  //       display: "flex",
  //       gap: "16px",
  //       justifyContent: "flex-end",
  //       marginTop: "40px",
  //       paddingTop: "32px",
  //       borderTop:
  //         theme === "dark"
  //           ? "2px solid rgba(255,255,255,0.1)"
  //           : "2px solid rgba(0,0,0,0.1)",
  //     },
  //   };
  //   const [formData, setFormData] = useState({
  //     payrollPeriod: {
  //       month: selectedMonth?.month || new Date().getMonth() + 1,
  //       year: selectedMonth?.year || new Date().getFullYear(),
  //       day: null,
  //     },
  //     includeOvertime: true,
  //     includeBonuses: true,
  //     processTaxes: true,
  //     processInsurance: true,
  //     paymentMethod: "bank_transfer",
  //     paymentDate: new Date().toISOString().split("T")[0],
  //     notes: "",
  //   });

  //   const [previewData, setPreviewData] = useState(null);
  //   const [selectedAll, setSelectedAll] = useState(false);
  //   const [processingPayroll, setProcessingPayroll] = useState(false);
  //   const [processingStatus, setProcessingStatus] = useState("");

  //   // always up-to-date ref so handleProcessPayroll never reads stale formData
  //   const formDataRef = React.useRef(formData);
  //   React.useEffect(() => { formDataRef.current = formData; });

  //   useEffect(() => {
  //     if (processStep === 4 && selectedEmployees.length > 0) {
  //       calculatePreview();
  //     }
  //   }, [processStep, selectedEmployees]);

  //   const handleInputChange = (field, value) => {
  //     setFormData((prev) => ({
  //       ...prev,
  //       [field]: value,
  //     }));
  //     // Only sync month/year to parent, ignore day changes
  //     if (field === "payrollPeriod") {
  //       // Don't update if only day changed
  //       const prevPeriod = formData.payrollPeriod;
  //       if (value.month !== prevPeriod.month || value.year !== prevPeriod.year) {
  //         setSelectedMonth({
  //           month: value.month,
  //           year: value.year,
  //         });
  //       }
  //     }
  //   };

  //   const handleEmployeeSelection = (employee) => {
  //     setSelectedEmployees((prev) => {
  //       const isSelected = prev.some((emp) => emp.id === employee.id);
  //       if (isSelected) {
  //         return prev.filter((emp) => emp.id !== employee.id);
  //       } else {
  //         return [...prev, employee];
  //       }
  //     });
  //   };

  //   const handleSelectAll = () => {
  //     if (selectedAll) {
  //       setSelectedEmployees([]);
  //     } else {
  //       setSelectedEmployees([...payrollData]);
  //     }
  //     setSelectedAll(!selectedAll);
  //   };

  //   const calculatePreview = () => {
  //     const preview = {
  //       totalEmployees: selectedEmployees.length,
  //       totalGross: selectedEmployees.reduce(
  //         (sum, emp) => sum + emp.grossSalary,
  //         0
  //       ),
  //       totalDeductions: selectedEmployees.reduce(
  //         (sum, emp) => sum + emp.totalDeductions,
  //         0
  //       ),
  //       totalNet: selectedEmployees.reduce(
  //         (sum, emp) => sum + emp.netSalary,
  //         0
  //       ),
  //       taxTotal: selectedEmployees.reduce((sum, emp) => sum + emp.taxMonth, 0),
  //       insuranceTotal: selectedEmployees.reduce(
  //         (sum, emp) => sum + emp.socialInsurance,
  //         0
  //       ),
  //       employees: selectedEmployees.map((emp) => ({
  //         name: emp.employeeName,
  //         gross: emp.grossSalary,
  //         deductions: emp.totalDeductions,
  //         net: emp.netSalary,
  //       })),
  //     };
  //     setPreviewData(preview);
  //   };

  //   const handleProcessPayroll = async () => {
  //     setProcessingPayroll(true);
  //     setProcessingStatus("Preparing payroll processing...");
  //     try {
  //       const syncYear = Number(
  //         formDataRef.current?.payrollPeriod?.year || selectedMonth?.year
  //       );
  //       const syncMonth = Number(
  //         formDataRef.current?.payrollPeriod?.month || selectedMonth?.month
  //       );
  //       const syncDay = formDataRef.current?.payrollPeriod?.day
  //         ? Number(formDataRef.current.payrollPeriod.day)
  //         : null;

  //       const empInfCodes = [
  //         ...new Set(
  //           selectedEmployees
  //             .map((employee) => String(employee.infinityCode || "").trim())
  //             .filter(Boolean)
  //         ),
  //       ];

  //       if (!syncYear || !syncMonth) {
  //         throw new Error("Payroll period is required before processing");
  //       }

  //       if (!empInfCodes.length) {
  //         throw new Error("No employees selected for payroll processing");
  //       }

  //       console.log("🔄 Processing payroll via /process endpoint...", {
  //         year: syncYear,
  //         month: syncMonth,
  //         employees: empInfCodes.length,
  //       });

  //       setProcessingStatus(
  //         `Processing payroll for ${empInfCodes.length} employee(s) — ${syncYear}/${String(syncMonth).padStart(2, "0")}...`
  //       );

  //       const response = await apiFetch(
  //         `${API_BASE_URL}/payrollX/process?year=${syncYear}&month=${syncMonth}${syncDay ? `&day=${syncDay}` : ""}`,
  //         {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify({ empInfCodes }),
  //         }
  //       );

  //       if (!response.ok) {
  //         const errorText = await response.text();
  //         throw new Error(`HTTP ${response.status}: ${errorText}`);
  //       }

  //       const result = await response.json();

  //       console.log("✅ Payroll processed successfully:", result);

  //       const successMessage = t("payrollProcessedSuccess").replace(
  //         "{count}",
  //         result?.data?.payrollsUpdated ?? empInfCodes.length
  //       );
  //       showAlert(t("success"), ` ${successMessage}`, "success");

  //       onClose();

  //       setTimeout(() => {
  //         onProcessComplete();
  //       }, 500);
  //     } catch (error) {
  //       console.error("❌ Error processing payroll:", error);
  //       const errorMessage = t("payrollProcessingError").replace(
  //         "{error}",
  //         error.message
  //       );
  //       showAlert(t("error"), `❌ ${errorMessage}`, "error");
  //     } finally {
  //       setProcessingPayroll(false);
  //       setProcessingStatus("");
  //     }
  //   };

  //   // ✅ في كل Modal (ProcessPayrollModal, ExcelImportModal, EmployeeDetailsModal, DeductionsModal)
  //   // غيّر الـ modal overlay style:

  //   const steps = [
  //     { number: 1, title: t("selectPeriod"), icon: Calendar },
  //     { number: 2, title: t("chooseEmployees"), icon: Users },
  //     { number: 3, title: t("configureSettings"), icon: Settings },
  //     { number: 4, title: t("reviewProcess"), icon: CheckCircle },
  //   ];

  //   const renderStepContent = () => {
  //     switch (processStep) {
  //       case 1:
  //         return (
  //           <div style={modalStyles.formSection}>
  //             <h3 style={modalStyles.sectionTitle}>
  //               <Calendar size={20} />
  //               {t("payPeriod")}
  //             </h3>
  //             <div style={modalStyles.formGrid}>
  //               <div style={modalStyles.formGroup}>
  //                 <label style={modalStyles.label}>{t("month")}</label>
  //                 <ReusableDropdown
  //                   items={[
  //                     { id: 1, label: t("january") },
  //                     { id: 2, label: t("february") },
  //                     { id: 3, label: t("march") },
  //                     { id: 4, label: t("april") },
  //                     { id: 5, label: t("may") },
  //                     { id: 6, label: t("june") },
  //                     { id: 7, label: t("july") },
  //                     { id: 8, label: t("august") },
  //                     { id: 9, label: t("september") },
  //                     { id: 10, label: t("october") },
  //                     { id: 11, label: t("november") },
  //                     { id: 12, label: t("december") },
  //                   ]}
  //                   value={
  //                     [
  //                       { id: 1, label: t("january") },
  //                       { id: 2, label: t("february") },
  //                       { id: 3, label: t("march") },
  //                       { id: 4, label: t("april") },
  //                       { id: 5, label: t("may") },
  //                       { id: 6, label: t("june") },
  //                       { id: 7, label: t("july") },
  //                       { id: 8, label: t("august") },
  //                       { id: 9, label: t("september") },
  //                       { id: 10, label: t("october") },
  //                       { id: 11, label: t("november") },
  //                       { id: 12, label: t("december") },
  //                     ].find((m) => m.id === formData.payrollPeriod.month)
  //                       ?.label || ""
  //                   }
  //                   onChange={(val) => {
  //                     const month = [
  //                       { id: 1, label: t("january") },
  //                       { id: 2, label: t("february") },
  //                       { id: 3, label: t("march") },
  //                       { id: 4, label: t("april") },
  //                       { id: 5, label: t("may") },
  //                       { id: 6, label: t("june") },
  //                       { id: 7, label: t("july") },
  //                       { id: 8, label: t("august") },
  //                       { id: 9, label: t("september") },
  //                       { id: 10, label: t("october") },
  //                       { id: 11, label: t("november") },
  //                       { id: 12, label: t("december") },
  //                     ].find((m) => m.label === val)?.id;
  //                     if (month)
  //                       handleInputChange("payrollPeriod", {
  //                         ...formData.payrollPeriod,
  //                         month,
  //                       });
  //                   }}
  //                   onSelect={(item) => {
  //                     handleInputChange("payrollPeriod", {
  //                       ...formData.payrollPeriod,
  //                       month: item.id,
  //                     });
  //                   }}
  //                   placeholder={t("selectMonth") || "Select Month"}
  //                   getItemKey={(item) => item.id}
  //                   getItemLabel={(item) => item.label}
  //                   theme={theme}
  //                   language={language}
  //                   inputStyle={unifiedControlStyle(theme, language === "ar")}
  //                 />
  //               </div>
  //               <div style={modalStyles.formGroup}>
  //                 <label style={modalStyles.label}>{t("year")}</label>
  //                 <ReusableDropdown
  //                   items={[2023, 2024, 2025, 2026].map((year) => ({
  //                     id: year,
  //                     label: String(year),
  //                   }))}
  //                   value={String(formData.payrollPeriod.year)}
  //                   onChange={(val) =>
  //                     handleInputChange("payrollPeriod", {
  //                       ...formData.payrollPeriod,
  //                       year: parseInt(val),
  //                     })
  //                   }
  //                   onSelect={(item) => {
  //                     handleInputChange("payrollPeriod", {
  //                       ...formData.payrollPeriod,
  //                       year: item.id,
  //                     });
  //                   }}
  //                   placeholder={t("selectYear") || "Select Year"}
  //                   getItemKey={(item) => item.id}
  //                   getItemLabel={(item) => item.label}
  //                   theme={theme}
  //                   language={language}
  //                   inputStyle={unifiedControlStyle(theme, language === "ar")}
  //                 />
  //               </div>
  //               <div style={modalStyles.formGroup}>
  //                 <label style={modalStyles.label}>{t("day") || "Day"}</label>
  //                 <ReusableDropdown
  //                   items={Array.from(
  //                     { length: new Date(formData.payrollPeriod.year, formData.payrollPeriod.month, 0).getDate() },
  //                     (_, i) => ({ id: i + 1, label: String(i + 1) })
  //                   )}
  //                   value={formData.payrollPeriod.day ?? null}  // ← number, not String()
  //                   onChange={() => { }}
  //                   onSelect={(item) => {
  //                     setFormData((prev) => ({
  //                       ...prev,
  //                       payrollPeriod: { ...prev.payrollPeriod, day: item.id },
  //                     }));
  //                   }}
  //                   onClear={() => {
  //                     setFormData((prev) => ({
  //                       ...prev,
  //                       payrollPeriod: { ...prev.payrollPeriod, day: null },
  //                     }));
  //                   }}
  //                   placeholder={t("selectDay") || "Select Day"}
  //                   getItemKey={(item) => item.id}
  //                   getItemLabel={(item) => item.label}
  //                   theme={theme}
  //                   language={language}
  //                   showAllOnArrowClick={true}
  //                   showClear={true}
  //                   inputStyle={unifiedControlStyle(theme, language === "ar")}
  //                 />
  //               </div>
  //             </div>
  //           </div>
  //         );

  //       case 2:
  //         const employeesByDepartment = payrollData.reduce((acc, employee) => {
  //           const dept = employee.department;
  //           if (!acc[dept]) {
  //             acc[dept] = [];
  //           }
  //           acc[dept].push(employee);
  //           return acc;
  //         }, {});

  //         const handleDepartmentSelection = (department, employees) => {
  //           const allSelected = employees.every((emp) =>
  //             selectedEmployees.some((selected) => selected.id === emp.id)
  //           );

  //           if (allSelected) {
  //             setSelectedEmployees((prev) =>
  //               prev.filter(
  //                 (selected) => !employees.some((emp) => emp.id === selected.id)
  //               )
  //             );
  //           } else {
  //             setSelectedEmployees((prev) => {
  //               const newEmployees = employees.filter(
  //                 (emp) => !prev.some((selected) => selected.id === emp.id)
  //               );
  //               return [...prev, ...newEmployees];
  //             });
  //           }
  //         };

  //         const isDepartmentFullySelected = (employees) => {
  //           return employees.every((emp) =>
  //             selectedEmployees.some((selected) => selected.id === emp.id)
  //           );
  //         };

  //         const isDepartmentPartiallySelected = (employees) => {
  //           return (
  //             employees.some((emp) =>
  //               selectedEmployees.some((selected) => selected.id === emp.id)
  //             ) && !isDepartmentFullySelected(employees)
  //           );
  //         };

  //         return (
  //           <div
  //             style={{
  //               ...modalStyles.formSection,
  //               background: `${currentTheme.colors.primary}05`,
  //               border: `2px solid ${currentTheme.colors.primary}20`,
  //               borderRadius: "20px",
  //               boxShadow: "0 8px 32px rgba(59,130,246,0.08)",
  //               padding: "32px",
  //             }}
  //           >
  //             <div
  //               style={{
  //                 display: "flex",
  //                 justifyContent: "space-between",
  //                 alignItems: "center",
  //                 marginBottom: "24px",
  //                 borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
  //                 paddingBottom: "12px",
  //               }}
  //             >
  //               <h3
  //                 style={{
  //                   ...modalStyles.sectionTitle,
  //                   fontSize: "22px",
  //                   color: currentTheme.colors.textPrimary,
  //                   letterSpacing: "1px",
  //                   textShadow: `0 2px 8px ${currentTheme.colors.primary}10`,
  //                 }}
  //               >
  //                 <Users size={22} />
  //                 {t("selectByDepartment")}
  //               </h3>
  //               <div
  //                 style={{
  //                   ...modalStyles.checkboxGroup,
  //                   background: `${currentTheme.colors.primary}10`,
  //                   borderRadius: "8px",
  //                   padding: "8px 16px",
  //                   boxShadow: "0 2px 8px rgba(59,130,246,0.06)",
  //                 }}
  //               >
  //                 <input
  //                   type="checkbox"
  //                   id="selectAll"
  //                   checked={selectedAll}
  //                   onChange={handleSelectAll}
  //                   style={{
  //                     accentColor: currentTheme.colors.primary,
  //                     width: "18px",
  //                     height: "18px",
  //                     marginRight: "8px",
  //                     cursor: "pointer",
  //                   }}
  //                 />
  //                 <label
  //                   style={{
  //                     ...modalStyles.label,
  //                     fontWeight: "700",
  //                     color: currentTheme.colors.primary,
  //                   }}
  //                   htmlFor="selectAll"
  //                 >
  //                   {t("selectAll")}{" "}
  //                   <span
  //                     style={{
  //                       background: currentTheme.colors.primary,
  //                       color: "#fff",
  //                       borderRadius: "6px",
  //                       padding: "2px 8px",
  //                       marginLeft: "6px",
  //                       fontSize: "12px",
  //                       fontWeight: "600",
  //                     }}
  //                   >
  //                     {payrollData.length}
  //                   </span>
  //                 </label>
  //               </div>
  //             </div>

  //             <div
  //               style={{
  //                 ...modalStyles.employeeList,
  //                 background: currentTheme.colors.cardBackground,
  //                 border: `2px solid ${currentTheme.colors.primary}10`,
  //                 borderRadius: "16px",
  //                 boxShadow: "0 4px 24px rgba(59,130,246,0.04)",
  //                 padding: "8px 0",
  //               }}
  //             >
  //               {Object.entries(employeesByDepartment).map(
  //                 ([department, employees]) => {
  //                   const isFullySelected =
  //                     isDepartmentFullySelected(employees);
  //                   const isPartiallySelected =
  //                     isDepartmentPartiallySelected(employees);
  //                   const departmentTotal = employees.reduce(
  //                     (sum, emp) => sum + emp.netSalary,
  //                     0
  //                   );

  //                   return (
  //                     <div
  //                       key={department}
  //                       style={{
  //                         marginBottom: "18px",
  //                         borderRadius: "14px",
  //                         overflow: "hidden",
  //                         boxShadow: "0 2px 12px rgba(59,130,246,0.03)",
  //                         background: `${currentTheme.colors.primary}04`,
  //                         border: `1px solid ${currentTheme.colors.primary}10`,
  //                       }}
  //                     >
  //                       <div
  //                         style={{
  //                           padding: "18px 24px",
  //                           background: `${currentTheme.colors.primary}10`,
  //                           borderBottom: `1px solid ${currentTheme.colors.primary}20`,
  //                           cursor: "pointer",
  //                           display: "flex",
  //                           alignItems: "center",
  //                           gap: "14px",
  //                           position: "sticky",
  //                           top: 0,
  //                           zIndex: 2,
  //                           boxShadow: "0 2px 8px rgba(59,130,246,0.04)",
  //                           transition: "background 0.2s",
  //                         }}
  //                         onClick={() =>
  //                           handleDepartmentSelection(department, employees)
  //                         }
  //                         onMouseEnter={(e) =>
  //                           (e.currentTarget.style.background = `${currentTheme.colors.primary}20`)
  //                         }
  //                         onMouseLeave={(e) =>
  //                           (e.currentTarget.style.background = `${currentTheme.colors.primary}10`)
  //                         }
  //                       >
  //                         <input
  //                           type="checkbox"
  //                           checked={isFullySelected}
  //                           ref={(el) => {
  //                             if (el) el.indeterminate = isPartiallySelected;
  //                           }}
  //                           style={{
  //                             accentColor: currentTheme.colors.primary,
  //                             width: "20px",
  //                             height: "20px",
  //                             marginRight: "8px",
  //                             cursor: "pointer",
  //                           }}
  //                         />
  //                         <div style={{ flex: 1 }}>
  //                           <div
  //                             style={{
  //                               fontWeight: "800",
  //                               fontSize: "17px",
  //                               color: currentTheme.colors.primary,
  //                               letterSpacing: "0.5px",
  //                             }}
  //                           >
  //                             {department}
  //                           </div>
  //                           <div
  //                             style={{
  //                               fontSize: "13px",
  //                               color: currentTheme.colors.textSecondary,
  //                               marginTop: "2px",
  //                             }}
  //                           >
  //                             <span
  //                               style={{
  //                                 background: currentTheme.colors.primary,
  //                                 color: "#fff",
  //                                 borderRadius: "6px",
  //                                 padding: "2px 8px",
  //                                 fontWeight: "600",
  //                                 fontSize: "12px",
  //                                 marginRight: "8px",
  //                               }}
  //                             >
  //                               {employees.length}
  //                             </span>
  //                             employees • Total:{" "}
  //                             <span
  //                               style={{
  //                                 color: currentTheme.colors.success,
  //                                 fontWeight: "700",
  //                               }}
  //                             >
  //                               {`${totalGrossSalary.toLocaleString()} ${t(
  //                                 "currency"
  //                               )}`}
  //                             </span>
  //                           </div>
  //                         </div>
  //                         <ChevronDown
  //                           size={22}
  //                           style={{
  //                             color: currentTheme.colors.textSecondary,
  //                             opacity: 0.7,
  //                           }}
  //                         />
  //                       </div>

  //                       <div
  //                         style={{
  //                           background: currentTheme.colors.cardBackground,
  //                           padding: "0 0 8px 0",
  //                         }}
  //                       >
  //                         {employees.map((employee) => (
  //                           <div
  //                             key={employee.id}
  //                             style={{
  //                               ...modalStyles.employeeItem,
  //                               paddingLeft: "64px",
  //                               paddingRight: "24px",
  //                               borderRadius: "10px",
  //                               margin: "6px 0",
  //                               background: selectedEmployees.some(
  //                                 (emp) => emp.id === employee.id
  //                               )
  //                                 ? `${currentTheme.colors.success}10`
  //                                 : "transparent",
  //                               border: selectedEmployees.some(
  //                                 (emp) => emp.id === employee.id
  //                               )
  //                                 ? `2px solid ${currentTheme.colors.success}40`
  //                                 : `1px solid ${currentTheme.colors.borderLight}`,
  //                               boxShadow: selectedEmployees.some(
  //                                 (emp) => emp.id === employee.id
  //                               )
  //                                 ? "0 2px 12px rgba(16,185,129,0.08)"
  //                                 : "none",
  //                               transition: "all 0.2s",
  //                             }}
  //                             onClick={() => handleEmployeeSelection(employee)}
  //                             onMouseEnter={(e) => {
  //                               e.currentTarget.style.background =
  //                                 selectedEmployees.some(
  //                                   (emp) => emp.id === employee.id
  //                                 )
  //                                   ? `${currentTheme.colors.success}20`
  //                                   : `${currentTheme.colors.primary}08`;
  //                             }}
  //                             onMouseLeave={(e) => {
  //                               e.currentTarget.style.background =
  //                                 selectedEmployees.some(
  //                                   (emp) => emp.id === employee.id
  //                                 )
  //                                   ? `${currentTheme.colors.success}10`
  //                                   : "transparent";
  //                             }}
  //                           >
  //                             <input
  //                               type="checkbox"
  //                               checked={selectedEmployees.some(
  //                                 (emp) => emp.id === employee.id
  //                               )}
  //                               onChange={() => { }}
  //                               style={{
  //                                 accentColor: currentTheme.colors.success,
  //                                 width: "18px",
  //                                 height: "18px",
  //                                 marginRight: "12px",
  //                                 cursor: "pointer",
  //                               }}
  //                             />

  //                             <div style={{ flex: 1 }}>
  //                               <div
  //                                 style={{
  //                                   fontWeight: "700",
  //                                   color: currentTheme.colors.textPrimary,
  //                                   fontSize: "15px",
  //                                   marginBottom: "2px",
  //                                 }}
  //                               >
  //                                 {employee.employeeName}
  //                               </div>
  //                               <div
  //                                 style={{
  //                                   fontSize: "12px",
  //                                   color: currentTheme.colors.textSecondary,
  //                                 }}
  //                               ></div>
  //                             </div>
  //                             <div style={{ textAlign: "right" }}>
  //                               <div
  //                                 style={{
  //                                   fontWeight: "700",
  //                                   color: currentTheme.colors.success,
  //                                   fontSize: "15px",
  //                                 }}
  //                               >
  //                                 {employee.netSalary.toLocaleString()}{" "}
  //                                 {t("currency")}
  //                               </div>
  //                               <div
  //                                 style={{
  //                                   fontSize: "12px",
  //                                   color: currentTheme.colors.textMuted,
  //                                 }}
  //                               >
  //                                 Net Salary
  //                               </div>
  //                             </div>
  //                           </div>
  //                         ))}
  //                       </div>
  //                     </div>
  //                   );
  //                 }
  //               )}
  //             </div>

  //             {selectedEmployees.length > 0 && (
  //               <div
  //                 style={{
  //                   marginTop: "24px",
  //                   padding: "18px",
  //                   background: `${currentTheme.colors.success}20`,
  //                   borderRadius: "12px",
  //                   border: `2px solid ${currentTheme.colors.success}30`,
  //                   boxShadow: "0 2px 12px rgba(16,185,129,0.08)",
  //                   textAlign: "center",
  //                 }}
  //               >
  //                 <div
  //                   style={{
  //                     color: currentTheme.colors.success,
  //                     fontWeight: "700",
  //                     fontSize: "16px",
  //                     letterSpacing: "0.5px",
  //                   }}
  //                 >
  //                   <CheckCircle
  //                     size={18}
  //                     style={{ marginRight: "8px", verticalAlign: "middle" }}
  //                   />
  //                   {selectedEmployees.length} employees selected • Total:{" "}
  //                   <span
  //                     style={{
  //                       color: currentTheme.colors.primary,
  //                       fontWeight: "800",
  //                     }}
  //                   >
  //                     {selectedEmployees
  //                       .reduce((sum, emp) => sum + emp.netSalary, 0)
  //                       .toLocaleString()}{" "}
  //                     {t("currency")}
  //                   </span>
  //                 </div>
  //               </div>
  //             )}
  //           </div>
  //         );
  //       case 3:
  //         return (
  //           <div style={modalStyles.formSection}>
  //             <h3 style={modalStyles.sectionTitle}>
  //               <Settings size={20} />
  //               {t("payrollSettings")}
  //             </h3>

  //             <div style={modalStyles.formGrid}>
  //               <div style={modalStyles.formGroup}>
  //                 <label style={modalStyles.label}>{t("paymentMethod")}</label>
  //                 <ReusableDropdown
  //                   items={[
  //                     { id: "bank_transfer", label: t("bankTransfer") },
  //                     { id: "cash", label: t("cash") },
  //                     { id: "check", label: t("check") },
  //                     { id: "digital_wallet", label: t("digitalWallet") },
  //                   ]}
  //                   value={
  //                     [
  //                       { id: "bank_transfer", label: t("bankTransfer") },
  //                       { id: "cash", label: t("cash") },
  //                       { id: "check", label: t("check") },
  //                       { id: "digital_wallet", label: t("digitalWallet") },
  //                     ].find((m) => m.id === formData.paymentMethod)?.label ||
  //                     ""
  //                   }
  //                   onChange={(val) => {
  //                     const method = [
  //                       { id: "bank_transfer", label: t("bankTransfer") },
  //                       { id: "cash", label: t("cash") },
  //                       { id: "check", label: t("check") },
  //                       { id: "digital_wallet", label: t("digitalWallet") },
  //                     ].find((m) => m.label === val)?.id;
  //                     if (method) handleInputChange("paymentMethod", method);
  //                   }}
  //                   onSelect={(item) => {
  //                     handleInputChange("paymentMethod", item.id);
  //                   }}
  //                   placeholder={t("paymentMethod")}
  //                   getItemKey={(item) => item.id}
  //                   getItemLabel={(item) => item.label}
  //                   theme={theme}
  //                   language={language}
  //                   inputStyle={unifiedControlStyle(theme, language === "ar")}
  //                 />
  //               </div>

  //               <div style={modalStyles.formGroup}>
  //                 <label style={modalStyles.label}>{t("paymentDate")}</label>
  //                 <input
  //                   type="date"
  //                   style={{
  //                     ...modalStyles.input,
  //                     ...unifiedControlStyle(theme, language === "ar"),
  //                     padding: "12px 16px",
  //                   }}
  //                   value={formData.paymentDate}
  //                   onChange={(e) =>
  //                     handleInputChange("paymentDate", e.target.value)
  //                   }
  //                 />
  //               </div>
  //             </div>

  //             <div style={{ marginTop: "20px" }}>
  //               <h4 style={{ ...modalStyles.sectionTitle, fontSize: "16px" }}>
  //                 {t("processingOptions")}
  //               </h4>
  //               <div style={modalStyles.formGrid}>
  //                 <div style={modalStyles.checkboxGroup}>
  //                   <input
  //                     type="checkbox"
  //                     id="includeOvertime"
  //                     checked={formData.includeOvertime}
  //                     onChange={(e) =>
  //                       handleInputChange("includeOvertime", e.target.checked)
  //                     }
  //                   />
  //                   <label style={modalStyles.label} htmlFor="includeOvertime">
  //                     {t("includeOvertime")}
  //                   </label>
  //                 </div>

  //                 <div style={modalStyles.checkboxGroup}>
  //                   <input
  //                     type="checkbox"
  //                     id="includeBonuses"
  //                     checked={formData.includeBonuses}
  //                     onChange={(e) =>
  //                       handleInputChange("includeBonuses", e.target.checked)
  //                     }
  //                   />
  //                   <label style={modalStyles.label} htmlFor="includeBonuses">
  //                     {t("includeBonuses")}
  //                   </label>
  //                 </div>

  //                 <div style={modalStyles.checkboxGroup}>
  //                   <input
  //                     type="checkbox"
  //                     id="processTaxes"
  //                     checked={formData.processTaxes}
  //                     onChange={(e) =>
  //                       handleInputChange("processTaxes", e.target.checked)
  //                     }
  //                   />
  //                   <label style={modalStyles.label} htmlFor="processTaxes">
  //                     {t("processTaxes")}
  //                   </label>
  //                 </div>

  //                 <div style={modalStyles.checkboxGroup}>
  //                   <input
  //                     type="checkbox"
  //                     id="processInsurance"
  //                     checked={formData.processInsurance}
  //                     onChange={(e) =>
  //                       handleInputChange("processInsurance", e.target.checked)
  //                     }
  //                   />
  //                   <label style={modalStyles.label} htmlFor="processInsurance">
  //                     {t("processInsurance")}
  //                   </label>
  //                 </div>
  //               </div>
  //             </div>

  //             <div style={modalStyles.formGroupWithMargin}>
  //               <label style={modalStyles.label}>{t("notes")}</label>
  //               <textarea
  //                 style={{
  //                   ...modalStyles.input,
  //                   minHeight: "80px",
  //                   resize: "vertical",
  //                 }}
  //                 value={formData.notes}
  //                 onChange={(e) => handleInputChange("notes", e.target.value)}
  //                 placeholder={t("addNotesPayroll")}
  //               />
  //             </div>
  //           </div>
  //         );

  //       case 4:
  //         return (
  //           <div style={modalStyles.formSection}>
  //             <h3 style={modalStyles.sectionTitle}>
  //               <CheckCircle size={20} />
  //               {t("reviewProcess")}
  //             </h3>

  //             {previewData && (
  //               <>
  //                 <div style={modalStyles.previewCard}>
  //                   <h4
  //                     style={{
  //                       marginBottom: "16px",
  //                       color: currentTheme.colors.textPrimary,
  //                     }}
  //                   >
  //                     {t("payrollSummary")}
  //                   </h4>

  //                   <div style={modalStyles.formGrid}>
  //                     <div style={{ textAlign: "center" }}>
  //                       <div
  //                         style={{
  //                           fontSize: "12px",
  //                           color: currentTheme.colors.textSecondary,
  //                         }}
  //                       >
  //                         {t("employees")}
  //                       </div>
  //                       <div
  //                         style={{
  //                           fontSize: "24px",
  //                           fontWeight: "800",
  //                           color: currentTheme.colors.primary,
  //                         }}
  //                       >
  //                         {previewData.totalEmployees}
  //                       </div>
  //                     </div>

  //                     <div style={{ textAlign: "center" }}>
  //                       <div
  //                         style={{
  //                           fontSize: "12px",
  //                           color: currentTheme.colors.textSecondary,
  //                         }}
  //                       >
  //                         {t("grossTotal")}
  //                       </div>
  //                       <div
  //                         style={{
  //                           fontSize: "20px",
  //                           fontWeight: "700",
  //                           color: currentTheme.colors.info,
  //                         }}
  //                       >
  //                         {previewData.totalGross.toLocaleString()}{" "}
  //                         {t("currency")} {/* هتطبع جنيه هنا */}
  //                       </div>
  //                     </div>

  //                     <div style={{ textAlign: "center" }}>
  //                       <div
  //                         style={{
  //                           fontSize: "12px",
  //                           color: currentTheme.colors.textSecondary,
  //                         }}
  //                       >
  //                         {t("deductions")}
  //                       </div>
  //                       <div
  //                         style={{
  //                           fontSize: "20px",
  //                           fontWeight: "700",
  //                           color: currentTheme.colors.danger,
  //                         }}
  //                       >
  //                         {previewData.totalDeductions.toLocaleString()}{" "}
  //                         {t("currency")}
  //                       </div>
  //                     </div>

  //                     <div style={{ textAlign: "center" }}>
  //                       <div
  //                         style={{
  //                           fontSize: "12px",
  //                           color: currentTheme.colors.textSecondary,
  //                         }}
  //                       >
  //                         {t("taxInsurance")}
  //                       </div>
  //                       <div
  //                         style={{
  //                           fontSize: "16px",
  //                           fontWeight: "600",
  //                           color: currentTheme.colors.warning,
  //                         }}
  //                       >
  //                         {(
  //                           previewData.taxTotal + previewData.insuranceTotal
  //                         ).toLocaleString()}{" "}
  //                         {t("currency")}
  //                       </div>
  //                     </div>
  //                   </div>

  //                   <div style={modalStyles.previewTotal}>
  //                     {t("netTotal")}: {previewData.totalNet.toLocaleString()}{" "}
  //                     {t("currency")}
  //                   </div>
  //                 </div>

  //                 <div style={modalStyles.previewCard}>
  //                   <h4
  //                     style={{
  //                       marginBottom: "12px",
  //                       color: currentTheme.colors.textPrimary,
  //                     }}
  //                   >
  //                     {t("employeeBreakdown")}
  //                   </h4>
  //                   <div style={{ maxHeight: "200px", overflowY: "auto" }}>
  //                     {previewData.employees.map((emp, index) => (
  //                       <div
  //                         key={index}
  //                         style={{
  //                           display: "flex",
  //                           justifyContent: "space-between",
  //                           alignItems: "center",
  //                           padding: "8px 0",
  //                           borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
  //                         }}
  //                       >
  //                         <span
  //                           style={{ color: currentTheme.colors.textPrimary }}
  //                         >
  //                           {emp.name}
  //                         </span>
  //                         <div style={{ display: "flex", gap: "20px" }}>
  //                           <span
  //                             style={{
  //                               color: currentTheme.colors.info,
  //                               fontWeight: "600",
  //                             }}
  //                           >
  //                             {emp.gross.toLocaleString()} {t("currency")}
  //                           </span>
  //                           <span
  //                             style={{
  //                               color: currentTheme.colors.danger,
  //                               fontWeight: "600",
  //                             }}
  //                           >
  //                             -{emp.deductions.toLocaleString()} {t("currency")}
  //                           </span>
  //                           <span
  //                             style={{
  //                               color: currentTheme.colors.success,
  //                               fontWeight: "700",
  //                             }}
  //                           >
  //                             {emp.net.toLocaleString()} {t("currency")}
  //                           </span>
  //                         </div>
  //                       </div>
  //                     ))}
  //                   </div>
  //                 </div>
  //               </>
  //             )}

  //             <div
  //               style={{
  //                 background: `${currentTheme.colors.warning}10`,
  //                 padding: "16px",
  //                 borderRadius: "8px",
  //                 marginTop: "16px",
  //               }}
  //             >
  //               <div
  //                 style={{
  //                   display: "flex",
  //                   alignItems: "center",
  //                   gap: "8px",
  //                   color: currentTheme.colors.warning,
  //                 }}
  //               >
  //                 <AlertCircle size={16} />
  //                 <strong>{t("important")}:</strong>{" "}
  //                 {t("processPayrollWarning").replace(
  //                   "{count}",
  //                   selectedEmployees.length
  //                 )}
  //               </div>
  //             </div>
  //           </div>
  //         );

  //       default:
  //         return null;
  //     }
  //   };

  //   const handleNext = () => {
  //     if (processStep === 4) {
  //       handleProcessPayroll();
  //     } else {
  //       setProcessStep((prev) => prev + 1);
  //     }
  //   };

  //   const handleBack = () => {
  //     setProcessStep((prev) => prev - 1);
  //   };

  //   const canProceed = () => {
  //     switch (processStep) {
  //       case 1:
  //         return formData.payrollPeriod.month && formData.payrollPeriod.year;
  //       case 2:
  //         return selectedEmployees.length > 0;
  //       case 3:
  //         return true;
  //       case 4:
  //         return previewData && selectedEmployees.length > 0;
  //       default:
  //         return false;
  //     }
  //   };

  //   return (
  //     <div
  //       style={modalStyles.modal}
  //       onClick={(e) => {
  //         if (e.target === e.currentTarget) {
  //           onClose();
  //         }
  //       }}
  //     >
  //       <div style={modalStyles.modalContent}>
  //         <div style={modalStyles.header}>
  //           <h1 style={modalStyles.title}>
  //             <CreditCard size={32} />
  //             {t("processNewPayroll")}
  //           </h1>
  //           {/* <button
  //             style={{
  //               background: "rgba(239, 68, 68, 0.1)",
  //               border: "2px solid rgba(239, 68, 68, 0.3)",
  //               borderRadius: "12px",
  //               padding: "12px",
  //               cursor: "pointer",
  //               color: "#ef4444",
  //               transition: "all 0.3s ease",
  //             }}
  //             onClick={onClose}
  //             disabled={processingPayroll}
  //           >
  //             <X size={24} />
  //           </button> */}
  //         </div>

  //         <div style={modalStyles.stepIndicator}>
  //           {steps.map((step) => (
  //             <div
  //               key={step.number}
  //               style={{
  //                 ...modalStyles.step,
  //                 ...(processStep >= step.number ? modalStyles.activeStep : {}),
  //               }}
  //             >
  //               <div
  //                 style={{
  //                   ...modalStyles.stepNumber,
  //                   ...(processStep >= step.number
  //                     ? modalStyles.activeStepNumber
  //                     : {}),
  //                 }}
  //               >
  //                 {step.number}
  //               </div>
  //               {/* <step size={16} /> */}
  //               {step.title}
  //             </div>
  //           ))}
  //         </div>

  //         {renderStepContent()}

  //         <div style={modalStyles.actionButtons}>
  //           {processStep > 1 && (
  //             <Mo3tiButton
  //               variant="secondary"
  //               onClick={handleBack}
  //               disabled={processingPayroll}
  //             >
  //               {t("back")}
  //             </Mo3tiButton>
  //           )}

  //           <Mo3tiButton
  //             variant="primary"
  //             onClick={handleNext}
  //             disabled={!canProceed() || processingPayroll}
  //             icon={
  //               processingPayroll
  //                 ? Activity
  //                 : processStep === 4
  //                   ? CheckCircle
  //                   : TrendingUp
  //             }
  //           >
  //             {processingPayroll
  //               ? t("processing")
  //               : processStep === 4
  //                 ? t("processPayrollBtn")
  //                 : t("nextStep")}
  //           </Mo3tiButton>
  //         </div>

  //         {processingPayroll && processingStatus && (
  //           <div
  //             style={{
  //               marginTop: "10px",
  //               color: currentTheme.colors.textSecondary,
  //               fontSize: "13px",
  //               textAlign: "right",
  //             }}
  //           >
  //             {processingStatus}
  //           </div>
  //         )}
  //       </div>
  //     </div>
  //   );
  // };

  // ExcelImportModal Component
  const ExcelImportModal = ({ onClose, onImportComplete }) => {
    const { currentTheme } = useContext(ThemeContext);

    const columnMapping = {
      "Infinity Code": "infinityCode",
      "Employee Name": "employeeName",
      "Payroll Start date": "payrollStartDate",
      "Payroll End date": "payrollEndDate",
      "Working days": "workingDays",
      "Absence Days": "absenceDays",
      "Absence Days Value": "absenceDaysValue",
      "unpaid & deduction days": "unpaidDeductionDays",
      "unpaid & deduction value": "unpaidDeductionValue",
      "OverTime Days": "overtimeDays",
      "OverTime value": "overtimeValue",
      "Bonus Days": "bonusDays",
      "Bonus Value": "bonusValue",
      expenses: "expenses",
      "Penality Days": "penalityDays",
      "Penality Days Value": "penalityDaysValue",
      "25% Deduction (Sick Leave) Days": "sickLeave25DeductionDays",
      "25% Deduction (Sick Leave) Value": "sickLeave25DeductionValue",
      Achievement: "achievement",
      "Actual Net Salary": "netSalary",
      Basic: "basicSalary",
    };

    const handleFileSelect = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];

      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(xlsx|xls|csv)$/i)
      ) {
        setImportError(
          "Please upload a valid Excel file (.xlsx, .xls, or .csv)",
        );
        return;
      }

      setImportFile(file);
      setImportError(null);
      setImportLoading(true);

      try {
        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
              raw: false,
              defval: null,
            });

            console.log("📊 Parsed Excel data:", jsonData);

            if (jsonData.length === 0) {
              throw new Error("Excel file is empty or has no valid data");
            }

            const transformedData = jsonData.map((row, index) => {
              const mapped = {};

              Object.keys(columnMapping).forEach((excelCol) => {
                const dbField = columnMapping[excelCol];
                if (
                  row[excelCol] !== undefined &&
                  row[excelCol] !== null &&
                  row[excelCol] !== ""
                ) {
                  if (
                    [
                      "infinityCode",
                      "workingDays",
                      "absenceDays",
                      "unpaidDeductionDays",
                      "overtimeDays",
                      "bonusDays",
                      "penalityDays",
                      "sickLeave25DeductionDays",
                    ].includes(dbField)
                  ) {
                    mapped[dbField] = parseInt(row[excelCol]) || 0;
                  } else if (
                    [
                      "absenceDaysValue",
                      "unpaidDeductionValue",
                      "overtimeValue",
                      "bonusValue",
                      "expenses",
                      "penalityDaysValue",
                      "sickLeave25DeductionValue",
                      "achievement",
                      "netSalary",
                    ].includes(dbField)
                  ) {
                    mapped[dbField] = parseFloat(row[excelCol]) || 0;
                  } else if (
                    ["payrollStartDate", "payrollEndDate"].includes(dbField)
                  ) {
                    const dateValue = row[excelCol];
                    if (dateValue) {
                      if (typeof dateValue === "number") {
                        const excelEpoch = new Date(1899, 11, 30);
                        const date = new Date(
                          excelEpoch.getTime() + dateValue * 86400000,
                        );
                        mapped[dbField] = date.toISOString().split("T")[0];
                      } else {
                        const date = new Date(dateValue);
                        if (!isNaN(date)) {
                          mapped[dbField] = date.toISOString().split("T")[0];
                        }
                      }
                    }
                  } else {
                    mapped[dbField] = String(row[excelCol]);
                  }
                }
              });

              if (mapped.payrollStartDate) {
                const date = new Date(mapped.payrollStartDate);
                mapped.year = date.getFullYear();
                mapped.month = date.getMonth() + 1;
              } else {
                mapped.year = importSettings.defaultYear;
                mapped.month = importSettings.defaultMonth;
              }

              const errors = [];
              if (!mapped.infinityCode) {
                errors.push("Missing Infinity Code");
              }
              if (!mapped.year || !mapped.month) {
                errors.push("Missing or invalid payroll period");
              }

              return {
                rowNumber: index + 2,
                data: mapped,
                errors: errors,
                isValid: errors.length === 0,
              };
            });

            console.log("✅ Transformed data:", transformedData);

            setImportData(transformedData);
            setImportStep(2);
          } catch (parseError) {
            console.error("Parse error:", parseError);
            setImportError(`Failed to parse Excel file: ${parseError.message}`);
          } finally {
            setImportLoading(false);
          }
        };

        reader.onerror = () => {
          setImportError("Failed to read file");
          setImportLoading(false);
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error("File processing error:", error);
        setImportError(`Error processing file: ${error.message}`);
        setImportLoading(false);
      }
    };

    const handleImport = async () => {
      if (!importData || importData.length === 0) {
        setImportError("No data to import");
        return;
      }

      setImportLoading(true);
      setImportProgress(0);
      setImportError(null);
      setImportResults(null);

      const results = {
        total: importData.length,
        successful: 0,
        failed: 0,
        skipped: 0,
        errors: [],
      };

      try {
        const validRecords = importData
          .filter((row) => row.isValid)
          .map((row) => row.data);

        const invalidCount = importData.length - validRecords.length;
        results.skipped = invalidCount;

        if (validRecords.length === 0) {
          throw new Error(
            `No valid records to import (${invalidCount} records failed validation)`,
          );
        }

        console.log(`Importing ${validRecords.length} valid records...`);

        setImportProgress(20);

        const response = await apiFetch(
          `${API_BASE_URL}/payrollX/import/excel`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              payrollRecords: validRecords,
            }),
            timeout: 300000,
          },
        );

        setImportProgress(80);

        if (!response.ok) {
          let errorData;
          try {
            const contentType = response.headers.get("content-type");
            errorData =
              contentType && contentType.includes("application/json")
                ? await response.json()
                : await response.text();
          } catch {
            errorData = `HTTP ${response.status}: ${response.statusText}`;
          }

          const errorMessage =
            errorData?.error || errorData?.message || errorData;
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (!result.data) {
          throw new Error("Invalid response format from server");
        }

        results.successful = result.data.successful || 0;
        results.failed = result.data.failed || 0;
        results.total = result.data.total || validRecords.length;
        results.errors = result.data.errors || [];

        console.log("Import completed:", results);

        setImportResults(results);
        setImportStep(3);
        setImportProgress(100);

        if (results.failed > 0) {
          console.warn(
            `Import completed with ${results.failed} failures. Check errors for details.`,
          );
        }
      } catch (error) {
        console.error("Import error:", error);

        const errorMessage =
          error.message || "An unexpected error occurred during import";

        setImportError(errorMessage);
        setImportResults({
          ...results,
          successful: 0,
          failed: results.total,
        });

        setImportStep(2);
      } finally {
        setImportLoading(false);
        setImportProgress(100);
      }
    };

    const modalStyles = {
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "20px",
        backdropFilter: "blur(4px)",
      },
      modal: {
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 1.6)"
            : "rgba(255, 255, 255, 0.9)",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "16px",
        width: "100%",
        maxWidth: "1100px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        //boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      },
      header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "24px 32px",
        borderBottom: `1px solid ${currentTheme.colors.border}`,
        flexShrink: 0,
      },
      title: {
        color: currentTheme.colors.textPrimary,
        fontSize: "24px",
        fontWeight: "700",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "12px",
      },
      closeButton: {
        background: "transparent",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "8px",
        padding: "8px",
        cursor: "pointer",
        color: currentTheme.colors.textSecondary,
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      content: {
        padding: "32px",
        overflowY: "auto",
        flex: 1,
      },
      footer: {
        display: "flex",
        gap: "12px",
        justifyContent: "flex-end",
        padding: "20px 32px",
        borderBottom: `1px solid ${currentTheme.colors.border}`,
        flexShrink: 0,
      },
      uploadArea: {
        border: `2px dashed ${currentTheme.colors.border}`,
        borderRadius: "12px",
        padding: "48px 32px",
        textAlign: "center",
        background: `${currentTheme.colors.primary}05`,
        cursor: "pointer",
        transition: "all 0.3s ease",
        outline: "none",
        display: "block",
      },
      statsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 4fr))",
        gap: "16px",
        marginBottom: "24px",
      },
      statCard: {
        padding: "20px",
        background: currentTheme.colors.inputBackground,
        borderRadius: "12px",
        border: `1px solid ${currentTheme.colors.border}`,
        textAlign: "center",
      },
      statValue: {
        fontSize: "32px",
        fontWeight: "700",
        marginBottom: "8px",
      },
      statLabel: {
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "500",
      },
      infoBox: {
        padding: "16px",
        background: `${currentTheme.colors.info}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.info}30`,
        marginTop: "24px",
      },
      warningBox: {
        padding: "16px",
        background: `${currentTheme.colors.warning}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.warning}30`,
        marginTop: "16px",
      },
      errorBox: {
        padding: "16px",
        background: `${currentTheme.colors.danger}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.danger}30`,
        marginTop: "16px",
      },
      successBox: {
        padding: "16px",
        background: `${currentTheme.colors.success}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.success}30`,
        marginBottom: "16px",
      },
      table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px",
        background: currentTheme.colors.cardBackground,
      },
      tableContainer: {
        maxHeight: "400px",
        overflowY: "auto",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "10px",
        marginTop: "16px",
      },
    };
    const enhancedTableStyles = (currentTheme, isRTL) => ({
      tableContainer: {
        overflowX: "auto",
        borderRadius: "20px",
        border: `2px solid ${currentTheme.colors.borderLight}`,
        background: currentTheme.colors.cardBackground,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      },
      table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: "0",
        fontSize: "14px",
        direction: isRTL ? "rtl" : "ltr",
      },
      thead: {
        background: ` (135deg, ${currentTheme.colors.primary}0, ${currentTheme.colors.primary}05)`,
        position: "sticky",
        top: 0,
        zIndex: 10,
      },
      th: {
        padding: "20px 16px",
        textAlign: isRTL ? "right" : "left",
        color: currentTheme.colors.textPrimary,
        fontWeight: "800",
        fontSize: "13px",
        textTransform: "uppercase",
        letterSpacing: "1.2px",
        borderBottom: `3px solid ${currentTheme.colors.primary}40`,
        whiteSpace: "nowrap",
        background: currentTheme.colors.inputBackground,
      },
      thCenter: {
        textAlign: "center",
      },
      tr: {
        background: currentTheme.colors.cardBackground,
        borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
      },
      td: {
        padding: "20px 16px",
        borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
        verticalAlign: "middle",
      },
      employeeCell: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
      },
      employeeName: {
        color: currentTheme.colors.textPrimary,
        fontSize: "16px",
        fontWeight: "700",
        marginBottom: "4px",
      },
      employeeDepartment: {
        color: currentTheme.colors.textSecondary,
        fontSize: "13px",
      },
      employeePosition: {
        color: currentTheme.colors.textMuted,
        fontSize: "12px",
      },
      salaryValue: {
        color: currentTheme.colors.textPrimary,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      allowanceValue: {
        color: currentTheme.colors.success,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      deductionValue: {
        color: currentTheme.colors.danger,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      netSalaryValue: {
        color: currentTheme.colors.success,
        fontSize: "18px",
        fontWeight: "900",
        fontFamily: "monospace",
        textShadow: `0 2px 4px ${currentTheme.colors.success}30`,
      },
      subText: {
        fontSize: "11px",
        color: currentTheme.colors.textMuted,
        marginTop: "4px",
      },
      actionButtons: {
        display: "flex",
        gap: "8px",
        justifyContent: "center",
      },
    });

    const renderStepContent = () => {
      switch (importStep) {
        case 1:
          return (
            <div>
              <input
                type="file"
                id="excel-file-input"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              <label
                htmlFor="excel-file-input"
                style={modalStyles.uploadArea}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor =
                    currentTheme.colors.primary;
                  e.currentTarget.style.background = `${currentTheme.colors.primary}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor =
                    currentTheme.colors.border;
                  e.currentTarget.style.background = `${currentTheme.colors.primary}05`;
                }}
              >
                <Download
                  size={48}
                  color={currentTheme.colors.primary}
                  style={{ marginBottom: "16px" }}
                />
                <h3
                  style={{
                    color: currentTheme.colors.textPrimary,
                    marginBottom: "8px",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  {importFile ? importFile.name : t("uploadFile")}
                </h3>
                <p
                  style={{
                    color: currentTheme.colors.textSecondary,
                    marginBottom: "16px",
                    fontSize: "14px",
                  }}
                >
                  {t("supportedFormats")}
                </p>
                {importFile && (
                  <div
                    style={{
                      padding: "8px 16px",
                      background: currentTheme.colors.success,
                      color: "#fff",
                      borderRadius: "6px",
                      display: "inline-block",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    ✓ {(importFile.size / 1024).toFixed(2)} KB
                  </div>
                )}
              </label>

              {importError && (
                <div style={modalStyles.errorBox}>
                  <AlertCircle
                    size={18}
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    {importError}
                  </span>
                </div>
              )}

              {importLoading && (
                <div style={{ marginTop: "20px", textAlign: "center" }}>
                  <Activity
                    size={32}
                    color={currentTheme.colors.primary}
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                  <p
                    style={{
                      color: currentTheme.colors.textSecondary,
                      marginTop: "12px",
                      fontSize: "14px",
                    }}
                  >
                    {t("processingFile")}
                  </p>
                </div>
              )}

              <div style={modalStyles.infoBox}>
                <h4
                  style={{
                    color: currentTheme.colors.textPrimary,
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                  }}
                >
                  <Info size={18} />
                  {t("expectedFormat")}
                </h4>
                <p
                  style={{
                    color: currentTheme.colors.textSecondary,
                    fontSize: "13px",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  {t("formatDescription")}
                </p>
              </div>

              <div
                style={{
                  marginTop: "20px",
                  padding: "16px",
                  background: currentTheme.colors.inputBackground,
                  borderRadius: "10px",
                  border: `1px solid ${currentTheme.colors.border}`,
                }}
              >
                <h4
                  style={{
                    color: currentTheme.colors.textPrimary,
                    marginBottom: "12px",
                    fontSize: "15px",
                    fontWeight: "600",
                  }}
                >
                  {t("importSettings")}
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: currentTheme.colors.textSecondary,
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={importSettings.skipErrors}
                      onChange={(e) =>
                        setImportSettings({
                          ...importSettings,
                          skipErrors: e.target.checked,
                        })
                      }
                    />
                    {t("skipErrors")}
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: currentTheme.colors.textSecondary,
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={importSettings.calculateMissing}
                      onChange={(e) =>
                        setImportSettings({
                          ...importSettings,
                          calculateMissing: e.target.checked,
                        })
                      }
                    />
                    {t("autoCalculate")}
                  </label>
                </div>
              </div>
            </div>
          );

        case 2:
          const validRows = importData?.filter((row) => row.isValid) || [];
          const invalidRows = importData?.filter((row) => !row.isValid) || [];

          return (
            <div>
              <div style={modalStyles.statsGrid}>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.primary,
                    }}
                  >
                    {importData?.length || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("totalRows")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.success,
                    }}
                  >
                    {validRows.length}
                  </div>
                  <div style={modalStyles.statLabel}>{t("validRows")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.danger,
                    }}
                  >
                    {invalidRows.length}
                  </div>
                  <div style={modalStyles.statLabel}>{t("errorRows")}</div>
                </div>
              </div>

              <div style={modalStyles.tableContainer}>
                <table style={modalStyles.table}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("row")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("code")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("name")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("period")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("status")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData?.map((row, index) => (
                      <tr
                        key={index}
                        style={{
                          background: row.isValid
                            ? "transparent"
                            : `${currentTheme.colors.danger}08`,
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textSecondary,
                            fontSize: "13px",
                          }}
                        >
                          {row.rowNumber}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textPrimary,
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          {row.data.infinityCode || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textSecondary,
                            fontSize: "13px",
                          }}
                        >
                          {row.data.employeeName || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textSecondary,
                            fontSize: "13px",
                          }}
                        >
                          {row.data.year}/{row.data.month}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            fontSize: "13px",
                          }}
                        >
                          {row.isValid ? (
                            <span
                              style={{
                                color: currentTheme.colors.success,
                                fontWeight: "600",
                              }}
                            >
                              ✓ {t("valid")}
                            </span>
                          ) : (
                            <span
                              style={{
                                color: currentTheme.colors.danger,
                                fontWeight: "500",
                                fontSize: "12px",
                              }}
                            >
                              {row.errors.join(", ")}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {invalidRows.length > 0 && (
                <div style={modalStyles.warningBox}>
                  <AlertCircle
                    size={18}
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    {t("errorsWillBeSkipped").replace(
                      "{count}",
                      invalidRows.length,
                    )}
                  </span>
                </div>
              )}
            </div>
          );

        case 3:
          return (
            <div>
              <div style={modalStyles.statsGrid}>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.primary,
                    }}
                  >
                    {importResults?.total || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("total")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.success,
                    }}
                  >
                    {importResults?.successful || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("successful")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.danger,
                    }}
                  >
                    {importResults?.failed || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("failed")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.warning,
                    }}
                  >
                    {importResults?.skipped || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("skipped")}</div>
                </div>
              </div>

              {importResults?.successful > 0 && (
                <div style={modalStyles.successBox}>
                  <CheckCircle
                    size={20}
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    {t("successMessage").replace(
                      "{count}",
                      importResults.successful,
                    )}
                  </span>
                </div>
              )}

              {importResults?.errors && importResults.errors.length > 0 && (
                <div
                  style={{
                    border: `1px solid ${currentTheme.colors.danger}30`,
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      background: `${currentTheme.colors.danger}10`,
                      borderBottom: `1px solid ${currentTheme.colors.danger}30`,
                      fontWeight: "600",
                      color: currentTheme.colors.danger,
                      fontSize: "14px",
                    }}
                  >
                    Errors ({importResults.errors.length})
                  </div>
                  <div
                    style={{
                      padding: "16px",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {importResults.errors.map((error, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "12px",
                          background: currentTheme.colors.inputBackground,
                          borderRadius: "8px",
                          marginBottom: "8px",
                          border: `1px solid ${currentTheme.colors.border}`,
                        }}
                      >
                        <div
                          style={{
                            color: currentTheme.colors.textPrimary,
                            fontWeight: "600",
                            marginBottom: "4px",
                            fontSize: "13px",
                          }}
                        >
                          {t("row")} {error.row} - {t("employee")}{" "}
                          {error.infinityCode || t("unknown")}
                        </div>
                        <div
                          style={{
                            color: currentTheme.colors.danger,
                            fontSize: "12px",
                          }}
                        >
                          {error.error || error.errors?.join(", ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={modalStyles.infoBox}>
                <Info
                  size={18}
                  style={{ marginRight: "8px", verticalAlign: "middle" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  {t("recordsCreated")}
                </span>
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div
        style={modalStyles.overlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div style={modalStyles.modal}>
          {/* Header */}
          <div style={modalStyles.header}>
            <h1 style={modalStyles.title}>
              <Download size={28} />
              {t("importPayroll")}
            </h1>
            {/* <button
              style={modalStyles.closeButton}
              onClick={onClose}
              disabled={importLoading}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${currentTheme.colors.danger}10`;
                e.currentTarget.style.borderColor = currentTheme.colors.danger;
                e.currentTarget.style.color = currentTheme.colors.danger;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = currentTheme.colors.border;
                e.currentTarget.style.color = currentTheme.colors.textSecondary;
              }}
            >
              <X size={20} />
            </button> */}
          </div>

          {/* Step Indicator */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "20px 32px",
              borderBottom: `1px solid ${currentTheme.colors.border}`,
              background: currentTheme.colors.inputBackground,
            }}
          >
            {[
              { number: 1, titleKey: "upload", icon: Download },
              { number: 2, titleKey: "preview", icon: Eye },
              { number: 3, titleKey: "results", icon: CheckCircle },
            ].map((step, index) => (
              <React.Fragment key={step.number}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    background:
                      importStep >= step.number
                        ? currentTheme.colors.primary
                        : "transparent",
                    color:
                      importStep >= step.number
                        ? "#fff"
                        : currentTheme.colors.textSecondary,
                    border: `1px solid ${
                      importStep >= step.number
                        ? currentTheme.colors.primary
                        : currentTheme.colors.border
                    }`,
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: "700",
                      background:
                        importStep >= step.number
                          ? "#fff"
                          : currentTheme.colors.border,
                      color:
                        importStep >= step.number
                          ? currentTheme.colors.primary
                          : currentTheme.colors.textSecondary,
                    }}
                  >
                    {step.number}
                  </div>
                  <step.icon size={16} />
                  {t(step.titleKey)}
                </div>
                {index < 2 && (
                  <div
                    style={{
                      width: "40px",
                      height: "2px",
                      background:
                        importStep > step.number
                          ? currentTheme.colors.primary
                          : currentTheme.colors.border,
                      transition: "all 0.3s ease",
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Progress Bar */}
          {importLoading && importProgress > 0 && (
            <div
              style={{
                padding: "16px 32px",
                background: currentTheme.colors.inputBackground,
                borderBottom: `1px solid ${currentTheme.colors.border}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    color: currentTheme.colors.textPrimary,
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  Importing payroll records...
                </span>
                <span
                  style={{
                    color: currentTheme.colors.primary,
                    fontWeight: "700",
                    fontSize: "14px",
                  }}
                >
                  {importProgress}%
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  background: currentTheme.colors.border,
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${importProgress}%`,
                    height: "100%",
                    background: ` (90deg, ${currentTheme.colors.primary}, ${currentTheme.colors.success})`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div style={modalStyles.content}>{renderStepContent()}</div>

          {/* Footer */}
          <div style={modalStyles.footer}>
            {importStep === 2 && (
              <>
                <Mo3tiButton
                  variant="secondary"
                  onClick={() => {
                    setImportStep(1);
                    setImportData(null);
                  }}
                  disabled={importLoading}
                >
                  Back
                </Mo3tiButton>
                <Mo3tiButton
                  variant="primary"
                  icon={Download}
                  onClick={handleImport}
                  disabled={
                    importLoading || !importData || importData.length === 0
                  }
                >
                  {importLoading
                    ? "Importing..."
                    : `Import ${
                        importData?.filter((r) => r.isValid).length || 0
                      } Records`}
                </Mo3tiButton>
              </>
            )}

            {importStep === 3 && (
              <Mo3tiButton
                variant="primary"
                icon={CheckCircle}
                onClick={() => {
                  onImportComplete();
                }}
              >
                Done
              </Mo3tiButton>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main component styles
  // const containerStyles = {
  //   minHeight: "100vh",
  //   background: currentTheme.background.gradient,
  //   fontFamily: currentTheme.fonts.primary,
  //   padding: "24px",
  //   maxWidth: "1600px",
  //   margin: "0 auto",
  //   position: "relative",
  //   direction: isRTL ? 'rtl' : 'ltr', // ده اللي يخلي الصفحة RTL
  // };

  // const headerStyles = {
  //   background: currentTheme.colors.cardBackground,
  //   padding: "1.55rem",

  //   borderRadius: "1rem",
  //   transition: "all 0.3s ease",
  //   boxShadow:
  //     currentTheme === "dark"
  //       ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
  //       : "0 0.5rem 2rem rgba(0,0,0,0.1)",
  //   backdropFilter: currentTheme === "dark" ? "blur(0.625rem)" : "blur(0px)",
  //   WebkitBackdropFilter:
  //     currentTheme === "dark" ? "blur(0.625rem)" : "blur(0px)",
  //   border:
  //     currentTheme === "dark"
  //       ? "1px solid rgba(255,255,255,0.15)"
  //       : "1px solid rgba(0,0,0,0.05)",
  //   marginBottom: 70,
  //   cursor: "pointer",
  //   marginBottom: 42,
  //   marginLeft: "10px",
  //   marginTop: -30,
  //   marginLeft: 20,
  // };

  // const titleStyles = {
  //   fontSize: window.innerWidth <= 768 ? "24px" : "30px",
  //   fontWeight: "00",
  //   color: currentTheme.colors.textPrimary,
  //   margin: "0",
  //   textAlign: isRTL ? "right" : "left", // ← كده هيبقى يمين لو عربي
  // };

  // Add this state at the top of your component (before return)
  // ✅ ضع هذا مكان containerStyles/headerStyles/titleStyles
  const getResponsiveStyles = () => ({
    header: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      //border: theme === "light" ? "1px solid rgba(0, 0, 0, 0.1)" : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        screenSize === "xs" ? "1rem" : screenSize === "sm" ? "1.25rem" : "1rem",
      padding: spacing.padding,
      marginTop: -40,
      marginBottom: spacing.margin,
      //backdropFilter: "blur(0px)",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
    },

    container: {
      marginTop: -20,
      // minHeight: "100%",
      // fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
      // padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1rem",
      // maxWidth: container.maxWidth,
      // margin: "0 auto",
      direction: language === "ar" ? "rtl" : "ltr",
    },

    statCard: {
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.6)" : "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
      padding: spacing.padding,
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
      boxShadow:
        theme === "dark"
          ? "0 8px 32px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(0,0,0,0.1)",
      cursor: "pointer",
    },

    welcomeSection: {
      //marginBottom: spacing.margin,
    },

    welcomeTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: "28px",
      fontWeight: "700",
      // margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
      textShadow:
        theme === "dark"
          ? "0 0.125rem 0.25rem rgba(0,0,0,0.3)"
          : "0 0.125rem 0.25rem rgba(0,0,0,0.1)",
      //lineHeight: "1.2",
      //marginBottom: 25,
    },

    primaryBtn: {
      background:
        theme === "dark"
          ? " (135deg, #2e7cba 0%, #2e7cba 100%)"
          : " (135deg, #2e7cba 0%, #1d4ed8 100%)",
      color: "white",
      border: "none",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.875rem 1.5rem",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      fontSize: fonts.body,
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.39)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },

    secondaryBtn: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: theme === "dark" ? "white" : "#1a1a1a",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.2)"
          : "1px solid rgba(0,0,0,0.1)",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.875rem 1.5rem",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      fontSize: fonts.body,
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },

    // ===== FILTER STYLES (Same as EmployeeTable) =====
    filterContainer: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : screenSize === "md"
              ? "repeat(3, 1fr)"
              : "repeat(4, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
      padding: spacing.padding,
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.5)",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
    },

    filterGroup: {
      display: "flex",
      flexDirection: "column",
      gap: spacing.small,
    },

    filterLabel: {
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.9)"
          : "rgba(30, 41, 59, 0.85)",
      fontSize: fonts.small,
      fontWeight: "500",
      marginBottom: spacing.xs,
    },

    filterInput: {
      width: "100%",
      padding: screenSize === "xs" ? "0.5rem 0.75rem" : "0.625rem 0.875rem",
      fontSize: fonts.body,
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(148, 163, 184, 0.2)",
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      outline: "none",
      transition: "all 0.3s ease",
      "&:focus": {
        borderColor: currentTheme.colors.primary,
        backgroundColor: theme === "dark" ? "rgba(30, 41, 59, 0.6)" : "white",
        boxShadow: `0 0 0 3px ${currentTheme.colors.primary}20`,
      },
    },

    // ===== TABLE STYLES (Same as EmployeeTable) =====
    tableContainer: {
      overflowX: "auto",
      marginTop: spacing.margin,
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      position: "relative",
      overflow: "hidden",
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
      ...(screenSize === "xs" && {
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
      }),
    },

    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth:
        screenSize === "xs" ? "600px" : screenSize === "sm" ? "700px" : "900px",
      position: "relative",
      zIndex: 1,
    },

    tableHeader: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      textAlign: "center",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(59, 130, 246, 0.3)"
          : "2px solid rgba(59, 130, 246, 0.1)",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 10,
      backdropFilter: "blur(0.625rem)",
      boxShadow:
        theme === "dark"
          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
    },

    tableCell: {
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.06)"
          : "1px solid rgba(148, 163, 184, 0.08)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      fontSize: fonts.body,
      fontWeight: "500",
      verticalAlign: "center",
      justifyContent: "center",
      textAlign: "center",
      position: "relative",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      background: "transparent",
    },
  });
  // قبل return بشوية - بعد السطر const styles = getResponsiveStyles();
  // Pagination calculations// Show More calculations
  const currentItems = filteredPayrollData.slice(0, visibleItems);
  const hasMore = visibleItems < filteredPayrollData.length;

  // Show More handler
  const handleShowMore = () => {
    setVisibleItems((prev) => prev + itemsPerPage);
  };
  const styles = getResponsiveStyles();
  const [deductionTooltip, setDeductionTooltip] = useState({
    visible: false,
    employeeId: null,
    content: "",
    position: { x: 0, y: 0 },
  });
  const [deductionModal, setDeductionModal] = useState({
    isOpen: false,
    employee: null,
  });
  const [deductionDetails, setDeductionDetails] = useState(null);
  const [loadingDeductions, setLoadingDeductions] = useState(false);
  // const [currentPage, setCurrentPage] = useState(1);
  // const [itemsPerPage] = useState(10);
  const getHoverStyles = () => ({
    background:
      currentTheme === "dark"
        ? "rgba(255,255,255,0.05)"
        : "rgba(255,255,255,0.95)",
    border:
      currentTheme === "dark"
        ? "1px solid rgba(255,255,255,0.2)"
        : "1px solid rgba(0,0,0,0.05)",
    backdropFilter:
      currentTheme === "dark"
        ? "blur(1.875rem) saturate(200%)"
        : "blur(0.9375rem)",
    WebkitBackdropFilter:
      currentTheme === "dark"
        ? "blur(1.875rem) saturate(200%)"
        : "blur(0.9375rem)",
  });

  // Prevent body scroll when any modal is open. Placed after modal state declarations.
  // ✅ حط ده مكان الكود القديم
  // ✅ Replace your scroll-locking useEffect with this

  useEffect(() => {
    // Force check with explicit boolean conversion
    const isAnyModalOpen = Boolean(
      showProcessModal ||
      showImportModal ||
      selectedEmployee ||
      (deductionModal && deductionModal.isOpen === true),
    );

    console.log("🔒 Scroll lock state:", isAnyModalOpen); // Debug log

    if (isAnyModalOpen) {
      // Lock scroll
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // Restore scroll - use 'visible' to force unlock
      document.body.style.overflow = "visible";
      document.body.style.paddingRight = "0px";

      // Extra aggressive unlock after a tiny delay
      setTimeout(() => {
        if (
          !showProcessModal &&
          !showImportModal &&
          !selectedEmployee &&
          !deductionModal?.isOpen
        ) {
          document.body.style.overflow = "visible";
          document.body.style.paddingRight = "0px";
        }
      }, 50);
    }

    // Cleanup
    return () => {
      document.body.style.overflow = "visible";
      document.body.style.paddingRight = "0px";
    };
  }, [showProcessModal, showImportModal, selectedEmployee, deductionModal]);
  // Default styles for cards when not hovered
  const getDefaultStyles = () => ({
    transform: "translateY(0)",
    boxShadow:
      currentTheme === "dark"
        ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
        : "0 0.5rem 2rem rgba(0,0,0,0.1)",
    background:
      currentTheme === "dark"
        ? "rgba(255,255,255,0.02)"
        : "rgba(255,255,255,0.9)",
    border:
      currentTheme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
    backdropFilter: currentTheme === "dark" ? "blur(0.625rem)" : "blur(0px)",
    WebkitBackdropFilter:
      currentTheme === "dark" ? "blur(0.625rem)" : "blur(0px)",
  });
  // Enhanced Mo3ti Design System Components
  const handleMouseEnter = (e) => {
    const hoverStyles = getHoverStyles();
    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleMouseLeave = (e) => {
    const defaultStyles = getDefaultStyles();
    Object.assign(e.currentTarget.style, defaultStyles);
  };
  // Function to export Excel template
  const handleExportTemplate = () => {
    const link = document.createElement("a");
    link.href = templateFile;
    link.download = "payroll_template.xlsx";
    link.click();
  };

  const handleExportAllowanceDeductionTemplate = () => {
    try {
      const templateRows = [
        {
          infinityCode: "1001",
          date: "01/03/2026", // DD/MM/YYYY — must fall within the employee's payroll cycle
          // ─── Allowances (use amount columns) ───────────────────────────────
          transportation_allowance: 500, // amount in EGP
          variable_allowance: 300,
          overtime_allowance: 250,
          // ─── Deductions — by amount ─────────────────────────────────────────
          penalty_deduction: 100, // amount in EGP
          late_deduction: 25,
          // ─── Deductions — by days (backend converts days → EGP via daily rate) ──
          // Leave the *_amount column blank and fill the *_days column instead,
          // OR fill both and the amount column takes precedence.
          absence_days: 1, // 1 absence day  → netSalary/30 × 1
          penalty_days: "", // leave blank if using amount column above
          sick_leave_25_days: 0.5, // half-day sick leave at 25 % rate
          late_arrival_days: "",
        },
        {
          infinityCode: "1002",
          date: "15/03/2026",
          absence_days: 2,
          penalty_days: 1,
          transportation_allowance: 500,
        },
      ];

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(templateRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, "AllowancesDeductions");
      XLSX.writeFile(workbook, "allowances_deductions_template.xlsx");
    } catch (error) {
      showAlert(t("error"), t("allowanceDeductionImportError"), "error");
    }
  };

  const handleOpenAllowanceDeductionImport = () => {
    const input = document.getElementById("allowance-deduction-import-input");
    if (input) {
      input.value = "";
      input.click();
    }
  };

  const handleAllowanceDeductionFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtension = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!validExtension) {
      showAlert(t("error"), t("allowanceDeductionImportError"), "error");
      return;
    }

    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(new Uint8Array(e.target.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        raw: false,
        defval: "",
      });

      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Empty file");
      }

      // ─────────────────────────────────────────────────────────────────────────
      // Reserved header aliases — these map to the two required scalar fields.
      // Everything else is treated as an allowance or deduction column.
      // ─────────────────────────────────────────────────────────────────────────
      const INFINITY_CODE_ALIASES = new Set([
        "infcode",
        "infinity code",
        "infinitycode",
        "employee code",
        "code",
      ]);
      const DATE_ALIASES = new Set(["date", "payroll date", "period date"]);

      const records = rows
        .map((row) => {
          const record = {};

          for (const [key, rawValue] of Object.entries(row)) {
            const value =
              rawValue === null || rawValue === undefined ? "" : rawValue;
            const normalizedHeader = String(key)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, " ");

            // ── reserved scalars ──────────────────────────────────────────────
            if (INFINITY_CODE_ALIASES.has(normalizedHeader)) {
              record.infinityCode = String(value).trim();
              continue;
            }
            if (DATE_ALIASES.has(normalizedHeader)) {
              record.date = String(value).trim();
              continue;
            }

            // ── skip empty values ─────────────────────────────────────────────
            const strValue = String(value).trim();
            if (strValue === "" || strValue === "0") continue;

            // ── pass every other column through as-is ─────────────────────────
            // The backend's extractFlatImportLines + new _days logic handles
            // classifying columns as allowances, deductions-by-amount, or
            // deductions-by-days.
            record[String(key).trim()] = strValue;
          }

          return record;
        })
        .filter((row) => row.infinityCode && row.date);

      if (records.length === 0) {
        throw new Error(
          "No valid records found — every row must have an infinity code and a date",
        );
      }

      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/bulk/import-allowances-deductions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ records }),
          timeout: 300000,
        },
      );

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.error || "Import failed");
      }

      const processedCount = result?.data?.processed || 0;
      const totalCount = result?.data?.total || records.length;
      const failedCount = result?.data?.failed || 0;

      if (processedCount === 0) {
        const firstError = result?.data?.errors?.[0]?.error;
        throw new Error(firstError || "No records were imported");
      }

      if (failedCount > 0) {
        const firstError = result?.data?.errors?.[0]?.error || "";
        showAlert(
          t("important"),
          `${t("allowanceDeductionImportSuccess")}: ${processedCount}/${totalCount}. Failed: ${failedCount}${firstError ? `. First error: ${firstError}` : ""}`,
          "info",
        );
      } else {
        showAlert(
          t("success"),
          `${t("allowanceDeductionImportSuccess")}: ${processedCount}/${totalCount}`,
          "success",
        );
      }

      refreshData();
    } catch (error) {
      showAlert(
        t("error"),
        `${t("allowanceDeductionImportError")}: ${error.message}`,
        "error",
      );
    }
  };

  const handleExportPayrollExcel = () => {
    try {
      // Prepare data for Excel export
      const exportData = filteredPayrollData.map((employee) => ({
        [t("employee")]: employee.employeeName,
        [t("infinitycode")]:
          employee.infinitycode || employee.infinityCode || employee.code || "",
        [t("department")]: employee.department,
        [t("basicSalary")]: Math.round(Number(employee.basicSalary) || 0),
        [t("bonus")]: Math.round(Number(employee.bonus) || 0),
        [t("overtimeTotal")]: Math.round(Number(employee.overtimeTotal) || 0),
        [t("transportation")]: Math.round(
          Number(employee.transportationAllowance) || 0,
        ),
        [t("meal")]: Math.round(Number(employee.mealAllowance) || 0),
        [t("mobile")]: Math.round(Number(employee.mobileAllowance) || 0),
        [t("bonusAdjustments")]: Math.round(
          Number(employee.bonusAdjustments) || 0,
        ),
        [t("allowances")]: Math.round(Number(employee.totalAllowances) || 0),
        [t("grossSalary")]: Math.round(Number(employee.grossSalary) || 0),
        [t("taxMonth")]: employee.taxMonth,
        [t("socialInsurance")]: Math.round(
          Number(employee.socialInsurance) || 0,
        ),
        [t("penalty")]: Math.round(Number(employee.penalty) || 0),
        [t("advancePayment")]: Math.round(Number(employee.advancePayment) || 0),
        [t("deductions")]: Math.round(Number(employee.totalDeductions) || 0),
        [t("netSalary")]: Math.round(Number(employee.netSalary) || 0),
        [t("status")]: employee.status || "Active",
        [t("paymentMethod")]: employee.paymentMethod || "Bank Transfer",
        [t("paymentDate")]:
          employee.paymentDate || new Date().toLocaleDateString(),
      }));

      // Calculate totals
      const totalGross = Math.round(
        filteredPayrollData.reduce(
          (sum, emp) => sum + (Number(emp.grossSalary) || 0),
          0,
        ),
      );
      const totalDeductions = Math.round(
        filteredPayrollData.reduce(
          (sum, emp) => sum + (Number(emp.totalDeductions) || 0),
          0,
        ),
      );
      const totalNet = Math.round(
        filteredPayrollData.reduce(
          (sum, emp) => sum + (Number(emp.netSalary) || 0),
          0,
        ),
      );

      // Add summary row
      const summaryRow = {};
      summaryRow[t("employee")] =
        `${t("netTotal")} (${filteredPayrollData.length} ${t("employees")})`;
      summaryRow[t("grossSalary")] = totalGross;
      summaryRow[t("deductions")] = totalDeductions;
      summaryRow[t("netSalary")] = totalNet;

      exportData.push({}); // Empty row
      exportData.push(summaryRow);

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const colWidths = [
        { wch: 25 }, // Employee name
        { wch: 18 }, // Infinity code
        { wch: 15 }, // Department
        { wch: 12 }, // Basic salary
        { wch: 10 }, // Bonus
        { wch: 12 }, // Overtime
        { wch: 12 }, // Transportation
        { wch: 10 }, // Meal
        { wch: 10 }, // Mobile
        { wch: 12 }, // Bonus adjustments
        { wch: 12 }, // Total allowances
        { wch: 12 }, // Gross salary
        { wch: 10 }, // Tax
        { wch: 12 }, // Insurance
        { wch: 10 }, // Penalty
        { wch: 12 }, // Advance
        { wch: 12 }, // Total deductions
        { wch: 12 }, // Net salary
        { wch: 10 }, // Status
        { wch: 15 }, // Payment method
        { wch: 12 }, // Payment date
      ];
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      const sheetName = t("payrollDetails");
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename
      const exportYear = Number(
        selectedMonth?.year || new Date().getFullYear(),
      );
      const exportMonth = String(
        Number(selectedMonth?.month || new Date().getMonth() + 1),
      ).padStart(2, "0");
      const fileName = `Payroll_${exportYear}-${exportMonth}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);

      showAlert(t("success"), `${t("payrollExportSuccess")}`, "success");
    } catch (error) {
      console.error("Error exporting payroll to Excel:", error);
      showAlert(
        t("error"),
        `${t("payrollExportError")}: ${error.message}`,
        "error",
      );
    }
  };

  const handleExportPayrollPDF = async () => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");

      const isAr = language === "ar";
      const exportYear = Number(
        selectedMonth?.year || new Date().getFullYear(),
      );
      const exportMonth = String(
        Number(selectedMonth?.month || new Date().getMonth() + 1),
      ).padStart(2, "0");
      const totalGross = Math.round(
        filteredPayrollData.reduce(
          (s, e) => s + (Number(e.grossSalary) || 0),
          0,
        ),
      );
      const totalDed = Math.round(
        filteredPayrollData.reduce(
          (s, e) => s + (Number(e.totalDeductions) || 0),
          0,
        ),
      );
      const totalNet = Math.round(
        filteredPayrollData.reduce((s, e) => s + (Number(e.netSalary) || 0), 0),
      );
      const currency = t("currency");
      const dir = isAr ? "rtl" : "ltr";

      const rowsHtml = filteredPayrollData
        .map(
          (emp, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f3f6fb"}">
        <td>${emp.employeeName || "-"}</td>
        <td>${emp.department || "-"}</td>
        <td>${emp.workingCompany || "-"}</td>
        <td>${Math.round(Number(emp.basicSalary) || 0).toLocaleString()}</td>
        <td>${Math.round(Number(emp.totalAllowances || 0) || 0).toLocaleString()}</td>
        <td>${Math.round(Number(emp.grossSalary) || 0).toLocaleString()}</td>
        <td>${Math.round(Number(emp.totalDeductions) || 0).toLocaleString()}</td>
        <td style="font-weight:700;color:#16a34a">${Math.round(Number(emp.netSalary) || 0).toLocaleString()}</td>
        <td>${emp.status || "Active"}</td>
      </tr>`,
        )
        .join("");

      // ── Build an off-screen container the browser renders natively ──────────
      // Using Tahoma / Arial — both ship with Windows and include Arabic glyphs.
      const wrapper = document.createElement("div");
      wrapper.style.cssText = [
        "position:fixed",
        "left:-99999px",
        "top:0",
        "width:1122px",
        "background:#fff",
        `font-family:Tahoma,Arial,'Segoe UI',sans-serif`,
        `direction:${dir}`,
        "color:#1e293b",
        "padding:24px",
        "z-index:-9999",
      ].join(";");

      wrapper.innerHTML = `
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        .ph{text-align:center;margin-bottom:20px}
        .ph h1{font-size:20px;font-weight:700;color:#2563eb;margin-bottom:4px}
        .ph p{font-size:11px;color:#64748b}
        table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:24px}
        th{background:#2563eb;color:#fff;font-weight:700;padding:8px 6px;text-align:center}
        td{padding:7px 6px;border-bottom:1px solid #e2e8f0;text-align:center}
        .sb{border:1px solid #e2e8f0;border-radius:8px;padding:14px 18px;max-width:380px;${isAr ? "margin-right:auto" : "margin-left:auto"}}
        .sb h3{font-size:13px;font-weight:700;color:#2563eb;margin-bottom:10px}
        .sr{display:flex;justify-content:space-between;padding:4px 0;font-size:11px;border-bottom:1px solid #f1f5f9}
        .sr.net{font-size:13px;font-weight:700;border-top:2px solid #1e293b;border-bottom:none;padding-top:8px;margin-top:4px}
      </style>
      <div class="ph">
        <h1>${t("payrollDetails")}</h1>
        <p>${t("exportDate")}: ${new Date().toLocaleDateString(isAr ? "ar-EG" : "en-GB")} &nbsp;|&nbsp; ${t("payPeriod")}: ${exportYear}-${exportMonth}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th>${t("employee")}</th><th>${t("department")}</th><th>${t("workingCompany")}</th>
            <th>${t("basicSalary")}</th><th>${t("allowances")}</th><th>${t("grossSalary")}</th>
            <th>${t("deductions")}</th><th>${t("netSalary")}</th><th>${t("status")}</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="sb">
        <h3>${t("payrollSummary")}</h3>
        <div class="sr"><span>${t("employees")}:</span><span>${filteredPayrollData.length}</span></div>
        <div class="sr"><span>${t("grossTotal")}:</span><span>${totalGross.toLocaleString()} ${currency}</span></div>
        <div class="sr"><span>${t("deductions")}:</span><span>${totalDed.toLocaleString()} ${currency}</span></div>
        <div class="sr net"><span>${t("netTotal")}:</span><span>${totalNet.toLocaleString()} ${currency}</span></div>
      </div>`;

      document.body.appendChild(wrapper);

      // Let the browser paint before capturing
      await new Promise((r) => setTimeout(r, 150));

      const canvas = await html2canvas(wrapper, {
        scale: 1.8,
        useCORS: false,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: 1122,
      });

      document.body.removeChild(wrapper);

      // ── Embed canvas image into a landscape A4 PDF (with auto-pagination) ──
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: "a4",
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * pageW) / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.93);

      let remaining = imgH;
      let offsetY = 0;

      pdf.addImage(imgData, "JPEG", 0, offsetY, imgW, imgH);
      remaining -= pageH;

      while (remaining > 0) {
        offsetY -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, offsetY, imgW, imgH);
        remaining -= pageH;
      }

      pdf.save(`Payroll_${exportYear}-${exportMonth}.pdf`);
      showAlert(t("success"), `${t("payrollExportSuccessPDF")}`, "success");
    } catch (error) {
      console.error("Error exporting payroll to PDF:", error);
      showAlert(
        t("error"),
        `${t("payrollExportErrorPDF")}: ${error.message}`,
        "error",
      );
    }
  };

  // ✅ حط ده قبل return مباشرة
  const headerStyles = {
    background:
      theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
    borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
    padding: spacing.padding,
    marginBottom: spacing.margin,
    backdropFilter: "blur(0px)",

    // boxShadow: theme === "light" ? "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1)" : "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.5)",
  };

  return (
    <div>
      {/* Background decorative elements */}

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",

          // background: theme === 'dark'
          //   ? 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 50%)'
          //   : 'radial-gradient(circle at 20% 50%, rgba(37, 99, 235, 0.05) 0%, transparent 50%)',
          //pointerEvents: "none",
          zIndex: 0,
        }}
      />
      {/* Header */}
      {/* <div style={styles.header}>
        <div style={styles.welcomeSection}>
          <h1 style={styles.welcomeTitle}>{t("pageTitle")}</h1>
        </div>
      </div> */}
      {/* Welcome Section (use WelcomeCard for consistency) */}
      <WelcomeCard
        styles={styles}
        text={translations}
        language={language}
        title={{ en: translations.en.pageTitle, ar: translations.ar.pageTitle }}
      />

      {/* Action Buttons */}
      <div style={{ ...headerStyles, marginTop: "30px" }}>
        <input
          id="allowance-deduction-import-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: "none" }}
          onChange={handleAllowanceDeductionFileSelect}
        />
        <div
          className="action-buttons-container"
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "12px",
            justifyContent: "flex-start",
            alignItems: "center",
            //  padding: "24px",
          }}
        >
          {modulePermissions &&
            (modulePermissions.payrollManagement ||
              modulePermissions.processPayroll) && (
              <Mo3tiButton
                variant="primary"
                icon={CreditCard}
                onClick={() => setShowProcessModal(true)}
                style={styles.primaryBtn}
              >
                {t("processPayroll")}
              </Mo3tiButton>
            )}

          {modulePermissions && modulePermissions.dataImport && (
            <Mo3tiButton
              variant="secondary"
              icon={Upload}
              onClick={() => setShowImportModal(true)}
              style={styles.secondaryBtn}
            >
              {t("importExcel")}
            </Mo3tiButton>
          )}

          {modulePermissions && modulePermissions.dataImport && (
            <Mo3tiButton
              variant="secondary"
              icon={Users}
              onClick={handleOpenAllowanceDeductionImport}
              style={styles.secondaryBtn}
            >
              {t("importAllowancesDeductions")}
            </Mo3tiButton>
          )}

          {modulePermissions && modulePermissions.dataExport && (
            <Mo3tiButton
              variant="secondary"
              icon={Download}
              onClick={handleExportTemplate}
              style={styles.secondaryBtn}
            >
              {t("exportTemplate")}
            </Mo3tiButton>
          )}

          {modulePermissions && modulePermissions.dataExport && (
            <Mo3tiButton
              variant="secondary"
              icon={Download}
              onClick={handleExportAllowanceDeductionTemplate}
              style={styles.secondaryBtn}
            >
              {t("exportAllowanceDeductionTemplate")}
            </Mo3tiButton>
          )}

          {/* Export Buttons - PDF & Excel */}
          {filteredPayrollData.length > 0 && (
            <>
              <Mo3tiButton
                variant="secondary"
                icon={FileText}
                onClick={handleExportPayrollPDF}
                style={{
                  ...styles.secondaryBtn,
                  background: `${currentTheme.colors.danger}15`,
                  color: currentTheme.colors.danger,
                  border: `1px solid ${currentTheme.colors.danger}30`,
                }}
                title={t("exportPayrollPDF")}
              >
                {t("exportPayrollPDF")}
              </Mo3tiButton>
              <Mo3tiButton
                variant="secondary"
                icon={Download}
                onClick={handleExportPayrollExcel}
                style={{
                  ...styles.secondaryBtn,
                  background: `${currentTheme.colors.success}15`,
                  color: currentTheme.colors.success,
                  border: `1px solid ${currentTheme.colors.success}30`,
                }}
                title={t("exportPayrollExcel")}
              >
                {t("exportPayrollExcel")}
              </Mo3tiButton>
            </>
          )}

          {/* Manage Visibility button removed per user request */}
          <Mo3tiButton
            variant="secondary"
            icon={Heart}
            onClick={() => setShowMedicalInsuranceModal(true)}
            style={styles.secondaryBtn}
          >
            {t("medicalInsurance")}
          </Mo3tiButton>

          <Mo3tiButton
            variant="secondary"
            icon={Info}
            onClick={() => setShowBenefitsModal(true)}
            style={styles.secondaryBtn}
            aria-label={t("benefitsPackage")}
          >
            {/* Info button near Export Template */}
          </Mo3tiButton>
        </div>
      </div>

      {/* Statistics Grid */}

      <div
        className="stats-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)", // ✅ 4 أعمدة متساوية
          gap: spacing.gap,
          marginBottom: spacing.margin,
          //padding: "1rem",
          transition: "all 0.30s ease",
          backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
          WebkitBackdropFilter:
            theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
          cursor: "pointer",
        }}
      >
        {salaryStats.map((stat, index) => (
          <div
            key={index}
            style={styles.statCard}
            // onMouseEnter={(e) => {
            //   e.currentTarget.style.transform = "translateY(-4px) scale(1.02)";
            //   e.currentTarget.style.boxShadow = theme === "dark"
            //     ? "0 12px 40px rgba(0,0,0,0.4)"
            //     : "0 12px 40px rgba(0,0,0,0.15)";
            // }}
            // onMouseLeave={(e) => {
            //   e.currentTarget.style.transform = "translateY(0) scale(1)";
            //   e.currentTarget.style.boxShadow = styles.statCard.boxShadow;
            // }}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
              }}
            >
              <stat.icon size={screenSize === "xs" ? 14 : 16} />
              {stat.label}
            </h4>
            <p
              style={{
                color: stat.color,
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {stat.value}
            </p>
            <p
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.tiny,
                margin: "0.25rem 0 0 0",
              }}
            >
              {stat.subtitle}
            </p>
          </div>
        ))}
      </div>

      {/* Benefits Info Modal */}
      {showBenefitsModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          {/* Backdrop */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.2s ease-out",
            }}
            onClick={() => setShowBenefitsModal(false)}
          />

          {/* Modal Content */}
          <div
            style={{
              position: "relative",
              zIndex: 10000,
              width: "min(720px, 100%)",
              maxHeight: "85vh",
              background: currentTheme.colors.inputBackground,
              borderRadius: "16px",
              boxShadow:
                theme === "dark"
                  ? "0 20px 60px rgba(0, 0, 0, 1.6), 0 0 0 1px rgba(255, 255, 255, 0.1)"
                  : "0 20px 60px rgba(0, 0, 0, 1.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              animation: "slideUp 0.3s ease-out",
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
              WebkitOverflowScrolling: "touch", // للـ iOS
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "24px 24px 20px",
                borderBottom: `1px solid ${
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.08)"
                }`,
                background:
                  theme === "dark"
                    ? "linear-gradient(to bottom, rgba(255, 255, 255, 0.03), transparent)"
                    : "linear-gradient(to bottom, rgba(0, 0, 0, 0.02), transparent)",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "700",
                  color: currentTheme.colors.textPrimary,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: `${currentTheme.colors.primary}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Gift size={20} color={currentTheme.colors.primary} />
                </div>
                {t("benefitsPackage")}
              </h3>

              <button
                onClick={() => setShowBenefitsModal(false)}
                style={{
                  background:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.04)",
                  border: "none",
                  cursor: "pointer",
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: currentTheme.colors.textSecondary,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.08)";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.04)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div
              style={{
                padding: "24px",
                overflowY: "auto",
                flex: 1,
                scrollbarWidth: "none", // للـ Firefox
                msOverflowStyle: "none", // للـ IE و Edge
                WebkitOverflowScrolling: "touch", // للـ iOS
                scrollbarWidth: "none", // للـ Firefox
                msOverflowStyle: "none", // للـ IE و Edge
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "16px",
                }}
              >
                {benefits.map((b, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "flex-start",
                      padding: "16px",
                      borderRadius: "12px",
                      background:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(0, 0, 0, 0.02)",
                      border: `1px solid ${
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.06)"
                          : "rgba(0, 0, 0, 0.06)"
                      }`,
                      transition: "all 0.2s ease",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.03)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        theme === "dark"
                          ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                          : "0 8px 24px rgba(0, 0, 0, 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(0, 0, 0, 0.02)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {/* Icon */}
                    <div
                      style={{
                        minWidth: "48px",
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: `linear-gradient(135deg, ${currentTheme.colors.primary}25, ${currentTheme.colors.primary}15)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: currentTheme.colors.primary,
                        boxShadow: `0 4px 12px ${currentTheme.colors.primary}20`,
                      }}
                    >
                      <b.icon size={22} strokeWidth={2.5} />
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title & Status */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <strong
                          style={{
                            fontSize: "15px",
                            fontWeight: "700",
                            color: currentTheme.colors.textPrimary,
                          }}
                        >
                          {b.name}
                        </strong>

                        <span
                          style={{
                            padding: "4px 12px",
                            borderRadius: "8px",
                            background: `${currentTheme.colors.success}18`,
                            color: currentTheme.colors.success,
                            fontWeight: "600",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            border: `1px solid ${currentTheme.colors.success}30`,
                          }}
                        >
                          {b.status}
                        </span>
                      </div>

                      {/* Coverage */}
                      <div
                        style={{
                          fontSize: "14px",
                          color: currentTheme.colors.textSecondary,
                          marginBottom: "6px",
                          fontWeight: "600",
                        }}
                      >
                        {b.coverage}
                      </div>

                      {/* Description */}
                      <div
                        style={{
                          fontSize: "13px",
                          color: currentTheme.colors.textMuted,
                          lineHeight: "1.5",
                        }}
                      >
                        {b.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Animation Styles */}
          <style>{`
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(20px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      
      /* Custom Scrollbar */
      div[style*="overflowY: auto"]::-webkit-scrollbar {
        width: 8px;
      }
      
      div[style*="overflowY: auto"]::-webkit-scrollbar-track {
        background: transparent;
      }
      
      div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
        background: ${
          theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"
        };
        borderRadius: 10px;
      }
      
      div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
        background: ${
          theme === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"
        };
      }
    `}</style>
        </div>
      )}
      {/* Filters Section - Same as EmployeeTable */}

      <div style={styles.statCard}>
        {/* Old Mo3tiCard removed - filters now in grid above */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            width: "100%",
            padding: "8px 0 0 0",
            alignItems: "center",
            justifyItems: "stretch",
          }}
        >
          {/* Search Employees */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label
              style={{
                color: currentTheme.colors.textPrimary,
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: 2,
                textAlign: "start",
                letterSpacing: 0.1,
              }}
            >
              {t("searchEmployees")}
            </label>
            <input
              type="text"
              placeholder={t("searchPlaceholder")}
              style={{
                ...require("../components/ReusableDropdown.jsx").unifiedControlStyle(
                  theme,
                  language === "ar",
                ),
                marginBottom: 0,
                minHeight: 38,
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label
              style={{
                color: currentTheme.colors.textPrimary,
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: 2,
                textAlign: "start",
                letterSpacing: 0.1,
              }}
            >
              {t("paymentStatus")}
            </label>
            <ReusableDropdown
              items={[
                { key: "All", label: t("allStatuses") },
                { key: "paid", label: t("paid") },
                { key: "pending", label: t("pending") },
                { key: "approved", label: t("approved") },
                { key: "rejected", label: t("rejected") },
              ]}
              value={statusSearch}
              onChange={(val) => setStatusSearch(val)}
              onSelect={(item) => {
                setStatusFilter(item.key);
                setStatusSearch(item.label);
              }}
              onClear={() => {
                setStatusSearch("");
                setStatusFilter("All");
              }}
              placeholder={t("allStatuses")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
              inputStyle={{
                ...require("../components/ReusableDropdown.jsx").unifiedControlStyle(
                  theme,
                  language === "ar",
                ),
                minHeight: 38,
              }}
            />
          </div>

          {/* Department Filter */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label
              style={{
                color: currentTheme.colors.textPrimary,
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: 2,
                textAlign: "start",
                letterSpacing: 0.1,
              }}
            >
              {t("department")}
            </label>
            <ReusableDropdown
              items={[
                { key: "All", label: t("allDepartments") },
                ...Array.from(new Set(payrollData.map((p) => p.department)))
                  .filter((dept) => dept && dept !== "N/A")
                  .sort()
                  .map((dept) => ({ key: dept, label: dept })),
              ]}
              value={departmentSearch}
              onChange={(val) => setDepartmentSearch(val)}
              onSelect={(item) => {
                setSelectedDepartment(item.key);
                setDepartmentSearch(item.label);
              }}
              onClear={() => {
                setDepartmentSearch("");
                setSelectedDepartment("All");
              }}
              placeholder={t("allDepartments")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
              inputStyle={{
                ...require("../components/ReusableDropdown.jsx").unifiedControlStyle(
                  theme,
                  language === "ar",
                ),
                minHeight: 38,
              }}
            />
          </div>

          {/* Working Company Filter */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label
              style={{
                color: currentTheme.colors.textPrimary,
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: 2,
                textAlign: "start",
                letterSpacing: 0.1,
              }}
            >
              {t("workingCompany")}
            </label>
            <ReusableDropdown
              items={[
                { key: "All", label: t("allCompanies") },
                ...Array.from(
                  new Set(
                    payrollData
                      .map((p) => p.workingCompany)
                      .filter((c) => c && c !== "N/A"),
                  ),
                )
                  .sort()
                  .map((c) => ({ key: c, label: c })),
              ]}
              value={companySearch}
              onChange={(val) => setCompanySearch(val)}
              onSelect={(item) => {
                setSelectedCompany(item.key);
                setCompanySearch(item.label);
              }}
              onClear={() => {
                setCompanySearch("");
                setSelectedCompany("All");
              }}
              placeholder={t("allCompanies")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
              inputStyle={{
                ...require("../components/ReusableDropdown.jsx").unifiedControlStyle(
                  theme,
                  language === "ar",
                ),
                minHeight: 38,
              }}
            />
          </div>

          {/* Year Filter */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label
              style={{
                color: currentTheme.colors.textPrimary,
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: 2,
                textAlign: "start",
                letterSpacing: 0.1,
              }}
            >
              {t("payPeriodYear")}
            </label>
            <ReusableDropdown
              items={[
                { key: 2026, label: "2026" },
                { key: 2025, label: "2025" },
                { key: 2024, label: "2024" },
                { key: 2023, label: "2023" },
                { key: 2022, label: "2022" },
              ]}
              value={yearSearch}
              onChange={(val) => setYearSearch(val)}
              onSelect={(item) => {
                setSelectedMonth({ ...selectedMonth, year: item.key });
                setYearSearch(String(item.key));
              }}
              onClear={() => {
                setYearSearch("");
                setSelectedMonth({
                  ...selectedMonth,
                  year: new Date().getFullYear(),
                });
              }}
              placeholder={t("selectYear")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
              inputStyle={{
                ...require("../components/ReusableDropdown.jsx").unifiedControlStyle(
                  theme,
                  language === "ar",
                ),
                minHeight: 38,
              }}
            />
          </div>

          {/* Month Filter */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label
              style={{
                color: currentTheme.colors.textPrimary,
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: 2,
                textAlign: "start",
                letterSpacing: 0.1,
              }}
            >
              {t("payPeriodMonth")}
            </label>
            <ReusableDropdown
              items={[
                { key: 1, label: t("january") },
                { key: 2, label: t("february") },
                { key: 3, label: t("march") },
                { key: 4, label: t("april") },
                { key: 5, label: t("may") },
                { key: 6, label: t("june") },
                { key: 7, label: t("july") },
                { key: 8, label: t("august") },
                { key: 9, label: t("september") },
                { key: 10, label: t("october") },
                { key: 11, label: t("november") },
                { key: 12, label: t("december") },
              ]}
              value={monthSearch}
              onChange={(val) => setMonthSearch(val)}
              onSelect={(item) => {
                setSelectedMonth({ ...selectedMonth, month: item.key });
                setMonthSearch(item.label);
              }}
              onClear={() => {
                setMonthSearch("");
                setSelectedMonth({
                  ...selectedMonth,
                  month: new Date().getMonth() + 1,
                });
              }}
              placeholder={t("selectMonth")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
              inputStyle={{
                ...require("../components/ReusableDropdown.jsx").unifiedControlStyle(
                  theme,
                  language === "ar",
                ),
                minHeight: 38,
              }}
            />
          </div>
        </div>

        {/* Active Filters Display */}
        {(searchTerm ||
          statusFilter !== "All" ||
          selectedDepartment !== "All" ||
          selectedCompany !== "All") && (
          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              background: `${currentTheme.colors.primary}10`,
              borderRadius: "12px",
              border: `1px solid ${currentTheme.colors.primary}30`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  color: currentTheme.colors.textPrimary,
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("activeFilters")}:
              </span>
              {searchTerm && (
                <span
                  style={{
                    padding: "6px 12px",
                    background: currentTheme.colors.primary,
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Search: {searchTerm}
                  <X
                    size={14}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSearchTerm("")}
                  />
                </span>
              )}
              {statusFilter !== "All" && (
                <span
                  style={{
                    padding: "6px 12px",
                    background: currentTheme.colors.success,
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Status: {statusFilter}
                  <X
                    size={14}
                    style={{ cursor: "pointer" }}
                    onClick={() => setStatusFilter("All")}
                  />
                </span>
              )}
              {selectedDepartment !== "All" && (
                <span
                  style={{
                    padding: "6px 12px",
                    background: currentTheme.colors.info,
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Dept: {selectedDepartment}
                  <X
                    size={14}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedDepartment("All")}
                  />
                </span>
              )}
              {selectedCompany !== "All" && (
                <span
                  style={{
                    padding: "6px 12px",
                    background: "#8b5cf6",
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Company: {selectedCompany}
                  <X
                    size={14}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedCompany("All");
                      setCompanySearch("");
                    }}
                  />
                </span>
              )}
              <Mo3tiButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("All");
                  setSelectedDepartment("All");
                  setSelectedCompany("All");
                  setCompanySearch("");
                }}
              >
                {t("clearAll")}
              </Mo3tiButton>
            </div>
          </div>
        )}

        {/* Employee Benefits removed — info moved to header button */}

        {/* Comprehensive Payroll Table - Same styles as EmployeeTable */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.tableHeader}>{t("employee")}</th>
                <th style={styles.tableHeader}>{t("basicSalary")}</th>
                <th style={styles.tableHeader}>{t("allowances")}</th>
                <th style={styles.tableHeader}>{t("grossSalary")}</th>
                <th style={styles.tableHeader}>{t("deductions")}</th>
                <th style={styles.tableHeader}>{t("netSalary")}</th>
                <th style={styles.tableHeader}>{t("status")}</th>
                <th style={styles.tableHeader}>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((employee) => (
                <tr
                  key={employee.id}
                  style={{
                    background: currentTheme.colors.cardBackground,
                    borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${currentTheme.colors.primary}10`;
                    e.currentTarget.style.transform = "scale(1.01)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      currentTheme.colors.cardBackground;
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {/* Employee Info */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: currentTheme.colors.textPrimary,
                            fontSize: "15px",
                            fontWeight: "700",
                            fontFamily: "monospace",
                            marginBottom: "4px",
                            whiteSpace: "nowrap", // منع النزول لسطر جديد
                            overflow: "hidden", // إخفاء الزيادة
                            textOverflow: "ellipsis", // إضافة ... في النهاية
                          }}
                        >
                          {employee.employeeName}
                        </div>
                        <div
                          style={{
                            color: currentTheme.colors.textSecondary,
                            fontSize: "13px",
                          }}
                        >
                          {employee.department}
                        </div>
                        <div
                          style={{
                            color: currentTheme.colors.textMuted,
                            fontSize: "12px",
                          }}
                        >
                          {/* {employee.position} */}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Basic Salary */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        color: currentTheme.colors.textPrimary,
                        fontSize: "15px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                      }}
                    >
                      {employee.basicSalary.toLocaleString()} {t("currency")}
                    </div>
                  </td>

                  {/* Total Allowances */}
                  <td style={{ ...styles.tableCell, whiteSpace: "nowrap" }}>
                    <div
                      style={{
                        color: currentTheme.colors.textPrimary,
                        fontSize: "15px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                      }}
                    >
                      {`+${(Number(employee.totalAllowances) || 0).toLocaleString()} ${t("currency")}`}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: currentTheme.colors.textMuted,
                        marginTop: "2px",
                      }}
                    >
                      {t("includingOvertime")}
                    </div>
                  </td>

                  {/* Gross Salary */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        color: currentTheme.colors.textPrimary,
                        fontSize: "15px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                      }}
                    >
                      {employee.grossSalary.toLocaleString()} {t("currency")}
                    </div>
                  </td>

                  {/* Total Deductions */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        color: currentTheme.colors.danger,
                        fontSize: "15px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                      }}
                    >
                      {`-${employee.totalDeductions.toLocaleString()} ${t("currency")}`}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: currentTheme.colors.textMuted,
                        marginTop: "2px",
                      }}
                    >
                      {t("taxInsuranceOther")}
                    </div>
                  </td>

                  {/* Net Salary */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        color: currentTheme.colors.textPrimary,
                        fontSize: "15px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                        // textShadow: `0 2px 4px ${currentTheme.colors.success}30`,
                      }}
                    >
                      {employee.netSalary.toLocaleString()} {t("currency")}
                    </div>
                  </td>

                  {/* KPI Score */}
                  {/* <td style={{ padding: "20px 16px" }}>
                  {getKPIBadge(employee.kpiScore)}
                </td> */}

                  {/* Status */}
                  {/* Status */}
                  <td style={styles.tableCell}>
                    <StatusBadge
                      status={employee.status}
                      language={language}
                      translations={translations}
                    />
                    {employee.paidDate && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: currentTheme.colors.textMuted,
                          marginTop: "4px",
                        }}
                      >
                        {new Date(employee.paidDate).toLocaleDateString(
                          "en-US",
                        )}
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td style={{ ...styles.tableCell, position: "relative" }}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Mo3tiButton
                        variant="ghost"
                        icon={Eye}
                        size="sm"
                        onClick={() => setSelectedEmployee(employee)}
                      ></Mo3tiButton>
                      {/* <Mo3tiButton
                        variant="ghost"
                        icon={Download}
                        size="sm"
                      ></Mo3tiButton>
                      moaty hu3mlha form */}
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <Mo3tiButton
                          variant="ghost"
                          icon={Minus}
                          size="sm"
                          onClick={() =>
                            setDeductionModal({ isOpen: true, employee })
                          }
                          style={{
                            background: currentTheme.colors.danger + "10",
                            color: currentTheme.colors.danger,
                          }}
                        ></Mo3tiButton>
                      </div>
                      {/* Toggle isIgnored */}
                      {(() => {
                        const isToggling = togglingIgnoredIds.has(employee.id);
                        const isDark = theme === "dark";
                        const btnBg = isToggling
                          ? isDark
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.04)"
                          : employee.isIgnored
                            ? currentTheme.colors.warning + "25"
                            : isDark
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.06)";
                        const btnColor = isToggling
                          ? isDark
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(0,0,0,0.3)"
                          : employee.isIgnored
                            ? currentTheme.colors.warning
                            : isDark
                              ? "rgba(255,255,255,0.55)"
                              : "rgba(0,0,0,0.45)";
                        const btnBorder =
                          "1px solid " +
                          (isToggling
                            ? isDark
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)"
                            : employee.isIgnored
                              ? currentTheme.colors.warning + "60"
                              : isDark
                                ? "rgba(255,255,255,0.15)"
                                : "rgba(0,0,0,0.15)");
                        return (
                          <button
                            onClick={() => handleToggleIsIgnored(employee.id)}
                            disabled={isToggling}
                            title={
                              employee.isIgnored
                                ? "Unignore attendance"
                                : "Ignore attendance"
                            }
                            style={{
                              background: btnBg,
                              color: btnColor,
                              fontSize: "11px",
                              fontWeight: "600",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              border: btnBorder,
                              cursor: isToggling ? "not-allowed" : "pointer",
                              whiteSpace: "nowrap",
                              opacity: isToggling ? 0.6 : 1,
                            }}
                          >
                            {isToggling
                              ? "Processing..."
                              : employee.isIgnored
                                ? "✓ Attendance Ignored"
                                : "Ignore attendance"}
                          </button>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* بعد </table> وقبل </div> اللي قافلة الـ table container */}

          {/* Pagination Controls */}
          {/* Show More Button */}
          {hasMore && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "24px",
                padding: "20px",
              }}
            >
              <button
                onClick={handleShowMore}
                style={{
                  background:
                    theme === "dark"
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)",
                  border:
                    theme === "dark"
                      ? "1px solid rgba(59, 130, 246, 0.4)"
                      : "1px solid rgba(59, 130, 246, 0.3)",
                  borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
                  padding:
                    screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                  cursor: "pointer",
                  color: theme === "dark" ? "#d1d5db" : "#374151",
                  fontWeight: "500",
                  //fontSize: fonts.body,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.375rem",
                  transition: "all 0.2s ease",
                }}
              >
                {t("showMore")} ({filteredPayrollData.length - visibleItems}{" "}
                {t("remaining")})
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedEmployee && (
        <EmployeeDetailsModal
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        />
      )}
      {showProcessModal && (
        <ProcessPayrollModal
          onClose={() => {
            setShowProcessModal(false);
            setProcessStep(1);
            setSelectedEmployees([]);
          }}
          onProcessComplete={() => {
            refreshPayrollData();
            setShowProcessModal(false);
            setProcessStep(1);
            setSelectedEmployees([]);
          }}
          payrollData={payrollData}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          theme={theme}
          language={language}
          API_BASE_URL={API_BASE_URL}
          clientName={clientName}
          showAlert={showAlert}
          processStep={processStep}
          setProcessStep={setProcessStep}
        />
      )}
      {deductionModal.isOpen && (
        <DeductionsModal
          isOpen={deductionModal.isOpen}
          employee={deductionModal.employee}
          onClose={() => setDeductionModal({ isOpen: false, employee: null })}
        />
      )}
      {showImportModal && (
        <ExcelImportModal
          onClose={() => {
            setShowImportModal(false);
            setImportStep(1);
            setImportFile(null);
            setImportData(null);
          }}
          onImportComplete={() => {
            refreshPayrollData();
            setShowImportModal(false);
          }}
        />
      )}

      {/* Visibility Rules Modal removed per user request */}

      {showMedicalInsuranceModal && (
        <MedicalInsuranceModal
          onClose={() => setShowMedicalInsuranceModal(false)}
          API_BASE_URL={API_BASE_URL}
          clientName={clientName}
          workingCompany={workingCompany}
          theme={theme}
          language={language}
        />
      )}

      {/* AlertModal Component */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={closeAlert}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />

      {/* Enhanced Custom Styles */}
      <style>
        {`
  * {
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    overflow-x: clip; /* ✅ Use clip instead of hidden */
    overflow-y: auto; /* ✅ Explicitly allow vertical scroll */
  }

  body {
    overflow-x: clip; /* ✅ Use clip instead of hidden */
    overflow-y: auto !important; /* ✅ Force allow vertical scroll */
    min-height: 100vh;
    margin: 0;
    padding: 0;
  }

  /* للـ modals */
  .modal-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`}
      </style>
    </div>
  );
};
export default ProfessionalPayrollSystem;